//! moment.js
//! version : 2.30.1
//! authors : Tim Wood, Iskren Chernev, Moment.js contributors
//! license : MIT
//! momentjs.com

;(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
    typeof define === 'function' && define.amd ? define(factory) :
    global.moment = factory()
}(this, (function () { 'use strict';

    var hookCallback;

    function hooks() {
        return hookCallback.apply(null, arguments);
    }

    // This is done to register the method called with moment()
    // without creating circular dependencies.
    function setHookCallback(callback) {
        hookCallback = callback;
    }

    function isArray(input) {
        return (
            input instanceof Array ||
            Object.prototype.toString.call(input) === '[object Array]'
        );
    }

    function isObject(input) {
        // IE8 will treat undefined and null as object if it wasn't for
        // input != null
        return (
            input != null &&
            Object.prototype.toString.call(input) === '[object Object]'
        );
    }

    function hasOwnProp(a, b) {
        return Object.prototype.hasOwnProperty.call(a, b);
    }

    function isObjectEmpty(obj) {
        if (Object.getOwnPropertyNames) {
            return Object.getOwnPropertyNames(obj).length === 0;
        } else {
            var k;
            for (k in obj) {
                if (hasOwnProp(obj, k)) {
                    return false;
                }
            }
            return true;
        }
    }

    function isUndefined(input) {
        return input === void 0;
    }

    function isNumber(input) {
        return (
            typeof input === 'number' ||
            Object.prototype.toString.call(input) === '[object Number]'
        );
    }

    function isDate(input) {
        return (
            input instanceof Date ||
            Object.prototype.toString.call(input) === '[object Date]'
        );
    }

    function map(arr, fn) {
        var res = [],
            i,
            arrLen = arr.length;
        for (i = 0; i < arrLen; ++i) {
            res.push(fn(arr[i], i));
        }
        return res;
    }

    function extend(a, b) {
        for (var i in b) {
            if (hasOwnProp(b, i)) {
                a[i] = b[i];
            }
        }

        if (hasOwnProp(b, 'toString')) {
            a.toString = b.toString;
        }

        if (hasOwnProp(b, 'valueOf')) {
            a.valueOf = b.valueOf;
        }

        return a;
    }

    function createUTC(input, format, locale, strict) {
        return createLocalOrUTC(input, format, locale, strict, true).utc();
    }

    function defaultParsingFlags() {
        // We need to deep clone this object.
        return {
            empty: false,
            unusedTokens: [],
            unusedInput: [],
            overflow: -2,
            charsLeftOver: 0,
            nullInput: false,
            invalidEra: null,
            invalidMonth: null,
            invalidFormat: false,
            userInvalidated: false,
            iso: false,
            parsedDateParts: [],
            era: null,
            meridiem: null,
            rfc2822: false,
            weekdayMismatch: false,
        };
    }

    function getParsingFlags(m) {
        if (m._pf == null) {
            m._pf = defaultParsingFlags();
        }
        return m._pf;
    }

    var some;
    if (Array.prototype.some) {
        some = Array.prototype.some;
    } else {
        some = function (fun) {
            var t = Object(this),
                len = t.length >>> 0,
                i;

            for (i = 0; i < len; i++) {
                if (i in t && fun.call(this, t[i], i, t)) {
                    return true;
                }
            }

            return false;
        };
    }

    function isValid(m) {
        var flags = null,
            parsedParts = false,
            isNowValid = m._d && !isNaN(m._d.getTime());
        if (isNowValid) {
            flags = getParsingFlags(m);
            parsedParts = some.call(flags.parsedDateParts, function (i) {
                return i != null;
            });
            isNowValid =
                flags.overflow < 0 &&
                !flags.empty &&
                !flags.invalidEra &&
                !flags.invalidMonth &&
                !flags.invalidWeekday &&
                !flags.weekdayMismatch &&
                !flags.nullInput &&
                !flags.invalidFormat &&
                !flags.userInvalidated &&
                (!flags.meridiem || (flags.meridiem && parsedParts));
            if (m._strict) {
                isNowValid =
                    isNowValid &&
                    flags.charsLeftOver === 0 &&
                    flags.unusedTokens.length === 0 &&
                    flags.bigHour === undefined;
            }
        }
        if (Object.isFrozen == null || !Object.isFrozen(m)) {
            m._isValid = isNowValid;
        } else {
            return isNowValid;
        }
        return m._isValid;
    }

    function createInvalid(flags) {
        var m = createUTC(NaN);
        if (flags != null) {
            extend(getParsingFlags(m), flags);
        } else {
            getParsingFlags(m).userInvalidated = true;
        }

        return m;
    }

    // Plugins that add properties should also add the key here (null value),
    // so we can properly clone ourselves.
    var momentProperties = (hooks.momentProperties = []),
        updateInProgress = false;

    function copyConfig(to, from) {
        var i,
            prop,
            val,
            momentPropertiesLen = momentProperties.length;

        if (!isUndefined(from._isAMomentObject)) {
            to._isAMomentObject = from._isAMomentObject;
        }
        if (!isUndefined(from._i)) {
            to._i = from._i;
        }
        if (!isUndefined(from._f)) {
            to._f = from._f;
        }
        if (!isUndefined(from._l)) {
            to._l = from._l;
        }
        if (!isUndefined(from._strict)) {
            to._strict = from._strict;
        }
        if (!isUndefined(from._tzm)) {
            to._tzm = from._tzm;
        }
        if (!isUndefined(from._isUTC)) {
            to._isUTC = from._isUTC;
        }
        if (!isUndefined(from._offset)) {
            to._offset = from._offset;
        }
        if (!isUndefined(from._pf)) {
            to._pf = getParsingFlags(from);
        }
        if (!isUndefined(from._locale)) {
            to._locale = from._locale;
        }

        if (momentPropertiesLen > 0) {
            for (i = 0; i < momentPropertiesLen; i++) {
                prop = momentProperties[i];
                val = from[prop];
                if (!isUndefined(val)) {
                    to[prop] = val;
                }
            }
        }

        return to;
    }

    // Moment prototype object
    function Moment(config) {
        copyConfig(this, config);
        this._d = new Date(config._d != null ? config._d.getTime() : NaN);
        if (!this.isValid()) {
            this._d = new Date(NaN);
        }
        // Prevent infinite loop in case updateOffset creates new moment
        // objects.
        if (updateInProgress === false) {
            updateInProgress = true;
            hooks.updateOffset(this);
            updateInProgress = false;
        }
    }

    function isMoment(obj) {
        return (
            obj instanceof Moment || (obj != null && obj._isAMomentObject != null)
        );
    }

    function warn(msg) {
        if (
            hooks.suppressDeprecationWarnings === false &&
            typeof console !== 'undefined' &&
            console.warn
        ) {
            console.warn('Deprecation warning: ' + msg);
        }
    }

    function deprecate(msg, fn) {
        var firstTime = true;

        return extend(function () {
            if (hooks.deprecationHandler != null) {
                hooks.deprecationHandler(null, msg);
            }
            if (firstTime) {
                var args = [],
                    arg,
                    i,
                    key,
                    argLen = arguments.length;
                for (i = 0; i < argLen; i++) {
                    arg = '';
                    if (typeof arguments[i] === 'object') {
                        arg += '\n[' + i + '] ';
                        for (key in arguments[0]) {
                            if (hasOwnProp(arguments[0], key)) {
                                arg += key + ': ' + arguments[0][key] + ', ';
                            }
                        }
                        arg = arg.slice(0, -2); // Remove trailing comma and space
                    } else {
                        arg = arguments[i];
                    }
                    args.push(arg);
                }
                warn(
                    msg +
                        '\nArguments: ' +
                        Array.prototype.slice.call(args).join('') +
                        '\n' +
                        new Error().stack
                );
                firstTime = false;
            }
            return fn.apply(this, arguments);
        }, fn);
    }

    var deprecations = {};

    function deprecateSimple(name, msg) {
        if (hooks.deprecationHandler != null) {
            hooks.deprecationHandler(name, msg);
        }
        if (!deprecations[name]) {
            warn(msg);
            deprecations[name] = true;
        }
    }

    hooks.suppressDeprecationWarnings = false;
    hooks.deprecationHandler = null;

    function isFunction(input) {
        return (
            (typeof Function !== 'undefined' && input instanceof Function) ||
            Object.prototype.toString.call(input) === '[object Function]'
        );
    }

    function set(config) {
        var prop, i;
        for (i in config) {
            if (hasOwnProp(config, i)) {
                prop = config[i];
                if (isFunction(prop)) {
                    this[i] = prop;
                } else {
                    this['_' + i] = prop;
                }
            }
        }
        this._config = config;
        // Lenient ordinal parsing accepts just a number in addition to
        // number + (possibly) stuff coming from _dayOfMonthOrdinalParse.
        // TODO: Remove "ordinalParse" fallback in next major release.
        this._dayOfMonthOrdinalParseLenient = new RegExp(
            (this._dayOfMonthOrdinalParse.source || this._ordinalParse.source) +
                '|' +
                /\d{1,2}/.source
        );
    }

    function mergeConfigs(parentConfig, childConfig) {
        var res = extend({}, parentConfig),
            prop;
        for (prop in childConfig) {
            if (hasOwnProp(childConfig, prop)) {
                if (isObject(parentConfig[prop]) && isObject(childConfig[prop])) {
                    res[prop] = {};
                    extend(res[prop], parentConfig[prop]);
                    extend(res[prop], childConfig[prop]);
                } else if (childConfig[prop] != null) {
                    res[prop] = childConfig[prop];
                } else {
                    delete res[prop];
                }
            }
        }
        for (prop in parentConfig) {
            if (
                hasOwnProp(parentConfig, prop) &&
                !hasOwnProp(childConfig, prop) &&
                isObject(parentConfig[prop])
            ) {
                // make sure changes to properties don't modify parent config
                res[prop] = extend({}, res[prop]);
            }
        }
        return res;
    }

    function Locale(config) {
        if (config != null) {
            this.set(config);
        }
    }

    var keys;

    if (Object.keys) {
        keys = Object.keys;
    } else {
        keys = function (obj) {
            var i,
                res = [];
            for (i in obj) {
                if (hasOwnProp(obj, i)) {
                    res.push(i);
                }
            }
            return res;
        };
    }

    var defaultCalendar = {
        sameDay: '[Today at] LT',
        nextDay: '[Tomorrow at] LT',
        nextWeek: 'dddd [at] LT',
        lastDay: '[Yesterday at] LT',
        lastWeek: '[Last] dddd [at] LT',
        sameElse: 'L',
    };

    function calendar(key, mom, now) {
        var output = this._calendar[key] || this._calendar['sameElse'];
        return isFunction(output) ? output.call(mom, now) : output;
    }

    function zeroFill(number, targetLength, forceSign) {
        var absNumber = '' + Math.abs(number),
            zerosToFill = targetLength - absNumber.length,
            sign = number >= 0;
        return (
            (sign ? (forceSign ? '+' : '') : '-') +
            Math.pow(10, Math.max(0, zerosToFill)).toString().substr(1) +
            absNumber
        );
    }

    var formattingTokens =
            /(\[[^\[]*\])|(\\)?([Hh]mm(ss)?|Mo|MM?M?M?|Do|DDDo|DD?D?D?|ddd?d?|do?|w[o|w]?|W[o|W]?|Qo?|N{1,5}|YYYYYY|YYYYY|YYYY|YY|y{2,4}|yo?|gg(ggg?)?|GG(GGG?)?|e|E|a|A|hh?|HH?|kk?|mm?|ss?|S{1,9}|x|X|zz?|ZZ?|.)/g,
        localFormattingTokens = /(\[[^\[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})/g,
        formatFunctions = {},
        formatTokenFunctions = {};

    // token:    'M'
    // padded:   ['MM', 2]
    // ordinal:  'Mo'
    // callback: function () { this.month() + 1 }
    function addFormatToken(token, padded, ordinal, callback) {
        var func = callback;
        if (typeof callback === 'string') {
            func = function () {
                return this[callback]();
            };
        }
        if (token) {
            formatTokenFunctions[token] = func;
        }
        if (padded) {
            formatTokenFunctions[padded[0]] = function () {
                return zeroFill(func.apply(this, arguments), padded[1], padded[2]);
            };
        }
        if (ordinal) {
            formatTokenFunctions[ordinal] = function () {
                return this.localeData().ordinal(
                    func.apply(this, arguments),
                    token
                );
            };
        }
    }

    function removeFormattingTokens(input) {
        if (input.match(/\[[\s\S]/)) {
            return input.replace(/^\[|\]$/g, '');
        }
        return input.replace(/\\/g, '');
    }

    function makeFormatFunction(format) {
        var array = format.match(formattingTokens),
            i,
            length;

        for (i = 0, length = array.length; i < length; i++) {
            if (formatTokenFunctions[array[i]]) {
                array[i] = formatTokenFunctions[array[i]];
            } else {
                array[i] = removeFormattingTokens(array[i]);
            }
        }

        return function (mom) {
            var output = '',
                i;
            for (i = 0; i < length; i++) {
                output += isFunction(array[i])
                    ? array[i].call(mom, format)
                    : array[i];
            }
            return output;
        };
    }

    // format date using native date object
    function formatMoment(m, format) {
        if (!m.isValid()) {
            return m.localeData().invalidDate();
        }

        format = expandFormat(format, m.localeData());
        formatFunctions[format] =
            formatFunctions[format] || makeFormatFunction(format);

        return formatFunctions[format](m);
    }

    function expandFormat(format, locale) {
        var i = 5;

        function replaceLongDateFormatTokens(input) {
            return locale.longDateFormat(input) || input;
        }

        localFormattingTokens.lastIndex = 0;
        while (i >= 0 && localFormattingTokens.test(format)) {
            format = format.replace(
                localFormattingTokens,
                replaceLongDateFormatTokens
            );
            localFormattingTokens.lastIndex = 0;
            i -= 1;
        }

        return format;
    }

    var defaultLongDateFormat = {
        LTS: 'h:mm:ss A',
        LT: 'h:mm A',
        L: 'MM/DD/YYYY',
        LL: 'MMMM D, YYYY',
        LLL: 'MMMM D, YYYY h:mm A',
        LLLL: 'dddd, MMMM D, YYYY h:mm A',
    };

    function longDateFormat(key) {
        var format = this._longDateFormat[key],
            formatUpper = this._longDateFormat[key.toUpperCase()];

        if (format || !formatUpper) {
            return format;
        }

        this._longDateFormat[key] = formatUpper
            .match(formattingTokens)
            .map(function (tok) {
                if (
                    tok === 'MMMM' ||
                    tok === 'MM' ||
                    tok === 'DD' ||
                    tok === 'dddd'
                ) {
                    return tok.slice(1);
                }
                return tok;
            })
            .join('');

        return this._longDateFormat[key];
    }

    var defaultInvalidDate = 'Invalid date';

    function invalidDate() {
        return this._invalidDate;
    }

    var defaultOrdinal = '%d',
        defaultDayOfMonthOrdinalParse = /\d{1,2}/;

    function ordinal(number) {
        return this._ordinal.replace('%d', number);
    }

    var defaultRelativeTime = {
        future: 'in %s',
        past: '%s ago',
        s: 'a few seconds',
        ss: '%d seconds',
        m: 'a minute',
        mm: '%d minutes',
        h: 'an hour',
        hh: '%d hours',
        d: 'a day',
        dd: '%d days',
        w: 'a week',
        ww: '%d weeks',
        M: 'a month',
        MM: '%d months',
        y: 'a year',
        yy: '%d years',
    };

    function relativeTime(number, withoutSuffix, string, isFuture) {
        var output = this._relativeTime[string];
        return isFunction(output)
            ? output(number, withoutSuffix, string, isFuture)
            : output.replace(/%d/i, number);
    }

    function pastFuture(diff, output) {
        var format = this._relativeTime[diff > 0 ? 'future' : 'past'];
        return isFunction(format) ? format(output) : format.replace(/%s/i, output);
    }

    var aliases = {
        D: 'date',
        dates: 'date',
        date: 'date',
        d: 'day',
        days: 'day',
        day: 'day',
        e: 'weekday',
        weekdays: 'weekday',
        weekday: 'weekday',
        E: 'isoWeekday',
        isoweekdays: 'isoWeekday',
        isoweekday: 'isoWeekday',
        DDD: 'dayOfYear',
        dayofyears: 'dayOfYear',
        dayofyear: 'dayOfYear',
        h: 'hour',
        hours: 'hour',
        hour: 'hour',
        ms: 'millisecond',
        milliseconds: 'millisecond',
        millisecond: 'millisecond',
        m: 'minute',
        minutes: 'minute',
        minute: 'minute',
        M: 'month',
        months: 'month',
        month: 'month',
        Q: 'quarter',
        quarters: 'quarter',
        quarter: 'quarter',
        s: 'second',
        seconds: 'second',
        second: 'second',
        gg: 'weekYear',
        weekyears: 'weekYear',
        weekyear: 'weekYear',
        GG: 'isoWeekYear',
        isoweekyears: 'isoWeekYear',
        isoweekyear: 'isoWeekYear',
        w: 'week',
        weeks: 'week',
        week: 'week',
        W: 'isoWeek',
        isoweeks: 'isoWeek',
        isoweek: 'isoWeek',
        y: 'year',
        years: 'year',
        year: 'year',
    };

    function normalizeUnits(units) {
        return typeof units === 'string'
            ? aliases[units] || aliases[units.toLowerCase()]
            : undefined;
    }

    function normalizeObjectUnits(inputObject) {
        var normalizedInput = {},
            normalizedProp,
            prop;

        for (prop in inputObject) {
            if (hasOwnProp(inputObject, prop)) {
                normalizedProp = normalizeUnits(prop);
                if (normalizedProp) {
                    normalizedInput[normalizedProp] = inputObject[prop];
                }
            }
        }

        return normalizedInput;
    }

    var priorities = {
        date: 9,
        day: 11,
        weekday: 11,
        isoWeekday: 11,
        dayOfYear: 4,
        hour: 13,
        millisecond: 16,
        minute: 14,
        month: 8,
        quarter: 7,
        second: 15,
        weekYear: 1,
        isoWeekYear: 1,
        week: 5,
        isoWeek: 5,
        year: 1,
    };

    function getPrioritizedUnits(unitsObj) {
        var units = [],
            u;
        for (u in unitsObj) {
            if (hasOwnProp(unitsObj, u)) {
                units.push({ unit: u, priority: priorities[u] });
            }
        }
        units.sort(function (a, b) {
            return a.priority - b.priority;
        });
        return units;
    }

    var match1 = /\d/, //       0 - 9
        match2 = /\d\d/, //      00 - 99
        match3 = /\d{3}/, //     000 - 999
        match4 = /\d{4}/, //    0000 - 9999
        match6 = /[+-]?\d{6}/, // -999999 - 999999
        match1to2 = /\d\d?/, //       0 - 99
        match3to4 = /\d\d\d\d?/, //     999 - 9999
        match5to6 = /\d\d\d\d\d\d?/, //   99999 - 999999
        match1to3 = /\d{1,3}/, //       0 - 999
        match1to4 = /\d{1,4}/, //       0 - 9999
        match1to6 = /[+-]?\d{1,6}/, // -999999 - 999999
        matchUnsigned = /\d+/, //       0 - inf
        matchSigned = /[+-]?\d+/, //    -inf - inf
        matchOffset = /Z|[+-]\d\d:?\d\d/gi, // +00:00 -00:00 +0000 -0000 or Z
        matchShortOffset = /Z|[+-]\d\d(?::?\d\d)?/gi, // +00 -00 +00:00 -00:00 +0000 -0000 or Z
        matchTimestamp = /[+-]?\d+(\.\d{1,3})?/, // 123456789 123456789.123
        // any word (or two) characters or numbers including two/three word month in arabic.
        // includes scottish gaelic two word and hyphenated months
        matchWord =
            /[0-9]{0,256}['a-z\u00A0-\u05FF\u0700-\uD7FF\uF900-\uFDCF\uFDF0-\uFF07\uFF10-\uFFEF]{1,256}|[\u0600-\u06FF\/]{1,256}(\s*?[\u0600-\u06FF]{1,256}){1,2}/i,
        match1to2NoLeadingZero = /^[1-9]\d?/, //         1-99
        match1to2HasZero = /^([1-9]\d|\d)/, //           0-99
        regexes;

    regexes = {};

    function addRegexToken(token, regex, strictRegex) {
        regexes[token] = isFunction(regex)
            ? regex
            : function (isStrict, localeData) {
                  return isStrict && strictRegex ? strictRegex : regex;
              };
    }

    function getParseRegexForToken(token, config) {
        if (!hasOwnProp(regexes, token)) {
            return new RegExp(unescapeFormat(token));
        }

        return regexes[token](config._strict, config._locale);
    }

    // Code from http://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript
    function unescapeFormat(s) {
        return regexEscape(
            s
                .replace('\\', '')
                .replace(
                    /\\(\[)|\\(\])|\[([^\]\[]*)\]|\\(.)/g,
                    function (matched, p1, p2, p3, p4) {
                        return p1 || p2 || p3 || p4;
                    }
                )
        );
    }

    function regexEscape(s) {
        return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    }

    function absFloor(number) {
        if (number < 0) {
            // -0 -> 0
            return Math.ceil(number) || 0;
        } else {
            return Math.floor(number);
        }
    }

    function toInt(argumentForCoercion) {
        var coercedNumber = +argumentForCoercion,
            value = 0;

        if (coercedNumber !== 0 && isFinite(coercedNumber)) {
            value = absFloor(coercedNumber);
        }

        return value;
    }

    var tokens = {};

    function addParseToken(token, callback) {
        var i,
            func = callback,
            tokenLen;
        if (typeof token === 'string') {
            token = [token];
        }
        if (isNumber(callback)) {
            func = function (input, array) {
                array[callback] = toInt(input);
            };
        }
        tokenLen = token.length;
        for (i = 0; i < tokenLen; i++) {
            tokens[token[i]] = func;
        }
    }

    function addWeekParseToken(token, callback) {
        addParseToken(token, function (input, array, config, token) {
            config._w = config._w || {};
            callback(input, config._w, config, token);
        });
    }

    function addTimeToArrayFromToken(token, input, config) {
        if (input != null && hasOwnProp(tokens, token)) {
            tokens[token](input, config._a, config, token);
        }
    }

    function isLeapYear(year) {
        return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    }

    var YEAR = 0,
        MONTH = 1,
        DATE = 2,
        HOUR = 3,
        MINUTE = 4,
        SECOND = 5,
        MILLISECOND = 6,
        WEEK = 7,
        WEEKDAY = 8;

    // FORMATTING

    addFormatToken('Y', 0, 0, function () {
        var y = this.year();
        return y <= 9999 ? zeroFill(y, 4) : '+' + y;
    });

    addFormatToken(0, ['YY', 2], 0, function () {
        return this.year() % 100;
    });

    addFormatToken(0, ['YYYY', 4], 0, 'year');
    addFormatToken(0, ['YYYYY', 5], 0, 'year');
    addFormatToken(0, ['YYYYYY', 6, true], 0, 'year');

    // PARSING

    addRegexToken('Y', matchSigned);
    addRegexToken('YY', match1to2, match2);
    addRegexToken('YYYY', match1to4, match4);
    addRegexToken('YYYYY', match1to6, match6);
    addRegexToken('YYYYYY', match1to6, match6);

    addParseToken(['YYYYY', 'YYYYYY'], YEAR);
    addParseToken('YYYY', function (input, array) {
        array[YEAR] =
            input.length === 2 ? hooks.parseTwoDigitYear(input) : toInt(input);
    });
    addParseToken('YY', function (input, array) {
        array[YEAR] = hooks.parseTwoDigitYear(input);
    });
    addParseToken('Y', function (input, array) {
        array[YEAR] = parseInt(input, 10);
    });

    // HELPERS

    function daysInYear(year) {
        return isLeapYear(year) ? 366 : 365;
    }

    // HOOKS

    hooks.parseTwoDigitYear = function (input) {
        return toInt(input) + (toInt(input) > 68 ? 1900 : 2000);
    };

    // MOMENTS

    var getSetYear = makeGetSet('FullYear', true);

    function getIsLeapYear() {
        return isLeapYear(this.year());
    }

    function makeGetSet(unit, keepTime) {
        return function (value) {
            if (value != null) {
                set$1(this, unit, value);
                hooks.updateOffset(this, keepTime);
                return this;
            } else {
                return get(this, unit);
            }
        };
    }

    function get(mom, unit) {
        if (!mom.isValid()) {
            return NaN;
        }

        var d = mom._d,
            isUTC = mom._isUTC;

        switch (unit) {
            case 'Milliseconds':
                return isUTC ? d.getUTCMilliseconds() : d.getMilliseconds();
            case 'Seconds':
                return isUTC ? d.getUTCSeconds() : d.getSeconds();
            case 'Minutes':
                return isUTC ? d.getUTCMinutes() : d.getMinutes();
            case 'Hours':
                return isUTC ? d.getUTCHours() : d.getHours();
            case 'Date':
                return isUTC ? d.getUTCDate() : d.getDate();
            case 'Day':
                return isUTC ? d.getUTCDay() : d.getDay();
            case 'Month':
                return isUTC ? d.getUTCMonth() : d.getMonth();
            case 'FullYear':
                return isUTC ? d.getUTCFullYear() : d.getFullYear();
            default:
                return NaN; // Just in case
        }
    }

    function set$1(mom, unit, value) {
        var d, isUTC, year, month, date;

        if (!mom.isValid() || isNaN(value)) {
            return;
        }

        d = mom._d;
        isUTC = mom._isUTC;

        switch (unit) {
            case 'Milliseconds':
                return void (isUTC
                    ? d.setUTCMilliseconds(value)
                    : d.setMilliseconds(value));
            case 'Seconds':
                return void (isUTC ? d.setUTCSeconds(value) : d.setSeconds(value));
            case 'Minutes':
                return void (isUTC ? d.setUTCMinutes(value) : d.setMinutes(value));
            case 'Hours':
                return void (isUTC ? d.setUTCHours(value) : d.setHours(value));
            case 'Date':
                return void (isUTC ? d.setUTCDate(value) : d.setDate(value));
            // case 'Day': // Not real
            //    return void (isUTC ? d.setUTCDay(value) : d.setDay(value));
            // case 'Month': // Not used because we need to pass two variables
            //     return void (isUTC ? d.setUTCMonth(value) : d.setMonth(value));
            case 'FullYear':
                break; // See below ...
            default:
                return; // Just in case
        }

        year = value;
        month = mom.month();
        date = mom.date();
        date = date === 29 && month === 1 && !isLeapYear(year) ? 28 : date;
        void (isUTC
            ? d.setUTCFullYear(year, month, date)
            : d.setFullYear(year, month, date));
    }

    // MOMENTS

    function stringGet(units) {
        units = normalizeUnits(units);
        if (isFunction(this[units])) {
            return this[units]();
        }
        return this;
    }

    function stringSet(units, value) {
        if (typeof units === 'object') {
            units = normalizeObjectUnits(units);
            var prioritized = getPrioritizedUnits(units),
                i,
                prioritizedLen = prioritized.length;
            for (i = 0; i < prioritizedLen; i++) {
                this[prioritized[i].unit](units[prioritized[i].unit]);
            }
        } else {
            units = normalizeUnits(units);
            if (isFunction(this[units])) {
                return this[units](value);
            }
        }
        return this;
    }

    function mod(n, x) {
        return ((n % x) + x) % x;
    }

    var indexOf;

    if (Array.prototype.indexOf) {
        indexOf = Array.prototype.indexOf;
    } else {
        indexOf = function (o) {
            // I know
            var i;
            for (i = 0; i < this.length; ++i) {
                if (this[i] === o) {
                    return i;
                }
            }
            return -1;
        };
    }

    function daysInMonth(year, month) {
        if (isNaN(year) || isNaN(month)) {
            return NaN;
        }
        var modMonth = mod(month, 12);
        year += (month - modMonth) / 12;
        return modMonth === 1
            ? isLeapYear(year)
                ? 29
                : 28
            : 31 - ((modMonth % 7) % 2);
    }

    // FORMATTING

    addFormatToken('M', ['MM', 2], 'Mo', function () {
        return this.month() + 1;
    });

    addFormatToken('MMM', 0, 0, function (format) {
        return this.localeData().monthsShort(this, format);
    });

    addFormatToken('MMMM', 0, 0, function (format) {
        return this.localeData().months(this, format);
    });

    // PARSING

    addRegexToken('M', match1to2, match1to2NoLeadingZero);
    addRegexToken('MM', match1to2, match2);
    addRegexToken('MMM', function (isStrict, locale) {
        return locale.monthsShortRegex(isStrict);
    });
    addRegexToken('MMMM', function (isStrict, locale) {
        return locale.monthsRegex(isStrict);
    });

    addParseToken(['M', 'MM'], function (input, array) {
        array[MONTH] = toInt(input) - 1;
    });

    addParseToken(['MMM', 'MMMM'], function (input, array, config, token) {
        var month = config._locale.monthsParse(input, token, config._strict);
        // if we didn't find a month name, mark the date as invalid.
        if (month != null) {
            array[MONTH] = month;
        } else {
            getParsingFlags(config).invalidMonth = input;
        }
    });

    // LOCALES

    var defaultLocaleMonths =
            'January_February_March_April_May_June_July_August_September_October_November_December'.split(
                '_'
            ),
        defaultLocaleMonthsShort =
            'Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec'.split('_'),
        MONTHS_IN_FORMAT = /D[oD]?(\[[^\[\]]*\]|\s)+MMMM?/,
        defaultMonthsShortRegex = matchWord,
        defaultMonthsRegex = matchWord;

    function localeMonths(m, format) {
        if (!m) {
            return isArray(this._months)
                ? this._months
                : this._months['standalone'];
        }
        return isArray(this._months)
            ? this._months[m.month()]
            : this._months[
                  (this._months.isFormat || MONTHS_IN_FORMAT).test(format)
                      ? 'format'
                      : 'standalone'
              ][m.month()];
    }

    function localeMonthsShort(m, format) {
        if (!m) {
            return isArray(this._monthsShort)
                ? this._monthsShort
                : this._monthsShort['standalone'];
        }
        return isArray(this._monthsShort)
            ? this._monthsShort[m.month()]
            : this._monthsShort[
                  MONTHS_IN_FORMAT.test(format) ? 'format' : 'standalone'
              ][m.month()];
    }

    function handleStrictParse(monthName, format, strict) {
        var i,
            ii,
            mom,
            llc = monthName.toLocaleLowerCase();
        if (!this._monthsParse) {
            // this is not used
            this._monthsParse = [];
            this._longMonthsParse = [];
            this._shortMonthsParse = [];
            for (i = 0; i < 12; ++i) {
                mom = createUTC([2000, i]);
                this._shortMonthsParse[i] = this.monthsShort(
                    mom,
                    ''
                ).toLocaleLowerCase();
                this._longMonthsParse[i] = this.months(mom, '').toLocaleLowerCase();
            }
        }

        if (strict) {
            if (format === 'MMM') {
                ii = indexOf.call(this._shortMonthsParse, llc);
                return ii !== -1 ? ii : null;
            } else {
                ii = indexOf.call(this._longMonthsParse, llc);
                return ii !== -1 ? ii : null;
            }
        } else {
            if (format === 'MMM') {
                ii = indexOf.call(this._shortMonthsParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._longMonthsParse, llc);
                return ii !== -1 ? ii : null;
            } else {
                ii = indexOf.call(this._longMonthsParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._shortMonthsParse, llc);
                return ii !== -1 ? ii : null;
            }
        }
    }

    function localeMonthsParse(monthName, format, strict) {
        var i, mom, regex;

        if (this._monthsParseExact) {
            return handleStrictParse.call(this, monthName, format, strict);
        }

        if (!this._monthsParse) {
            this._monthsParse = [];
            this._longMonthsParse = [];
            this._shortMonthsParse = [];
        }

        // TODO: add sorting
        // Sorting makes sure if one month (or abbr) is a prefix of another
        // see sorting in computeMonthsParse
        for (i = 0; i < 12; i++) {
            // make the regex if we don't have it already
            mom = createUTC([2000, i]);
            if (strict && !this._longMonthsParse[i]) {
                this._longMonthsParse[i] = new RegExp(
                    '^' + this.months(mom, '').replace('.', '') + '$',
                    'i'
                );
                this._shortMonthsParse[i] = new RegExp(
                    '^' + this.monthsShort(mom, '').replace('.', '') + '$',
                    'i'
                );
            }
            if (!strict && !this._monthsParse[i]) {
                regex =
                    '^' + this.months(mom, '') + '|^' + this.monthsShort(mom, '');
                this._monthsParse[i] = new RegExp(regex.replace('.', ''), 'i');
            }
            // test the regex
            if (
                strict &&
                format === 'MMMM' &&
                this._longMonthsParse[i].test(monthName)
            ) {
                return i;
            } else if (
                strict &&
                format === 'MMM' &&
                this._shortMonthsParse[i].test(monthName)
            ) {
                return i;
            } else if (!strict && this._monthsParse[i].test(monthName)) {
                return i;
            }
        }
    }

    // MOMENTS

    function setMonth(mom, value) {
        if (!mom.isValid()) {
            // No op
            return mom;
        }

        if (typeof value === 'string') {
            if (/^\d+$/.test(value)) {
                value = toInt(value);
            } else {
                value = mom.localeData().monthsParse(value);
                // TODO: Another silent failure?
                if (!isNumber(value)) {
                    return mom;
                }
            }
        }

        var month = value,
            date = mom.date();

        date = date < 29 ? date : Math.min(date, daysInMonth(mom.year(), month));
        void (mom._isUTC
            ? mom._d.setUTCMonth(month, date)
            : mom._d.setMonth(month, date));
        return mom;
    }

    function getSetMonth(value) {
        if (value != null) {
            setMonth(this, value);
            hooks.updateOffset(this, true);
            return this;
        } else {
            return get(this, 'Month');
        }
    }

    function getDaysInMonth() {
        return daysInMonth(this.year(), this.month());
    }

    function monthsShortRegex(isStrict) {
        if (this._monthsParseExact) {
            if (!hasOwnProp(this, '_monthsRegex')) {
                computeMonthsParse.call(this);
            }
            if (isStrict) {
                return this._monthsShortStrictRegex;
            } else {
                return this._monthsShortRegex;
            }
        } else {
            if (!hasOwnProp(this, '_monthsShortRegex')) {
                this._monthsShortRegex = defaultMonthsShortRegex;
            }
            return this._monthsShortStrictRegex && isStrict
                ? this._monthsShortStrictRegex
                : this._monthsShortRegex;
        }
    }

    function monthsRegex(isStrict) {
        if (this._monthsParseExact) {
            if (!hasOwnProp(this, '_monthsRegex')) {
                computeMonthsParse.call(this);
            }
            if (isStrict) {
                return this._monthsStrictRegex;
            } else {
                return this._monthsRegex;
            }
        } else {
            if (!hasOwnProp(this, '_monthsRegex')) {
                this._monthsRegex = defaultMonthsRegex;
            }
            return this._monthsStrictRegex && isStrict
                ? this._monthsStrictRegex
                : this._monthsRegex;
        }
    }

    function computeMonthsParse() {
        function cmpLenRev(a, b) {
            return b.length - a.length;
        }

        var shortPieces = [],
            longPieces = [],
            mixedPieces = [],
            i,
            mom,
            shortP,
            longP;
        for (i = 0; i < 12; i++) {
            // make the regex if we don't have it already
            mom = createUTC([2000, i]);
            shortP = regexEscape(this.monthsShort(mom, ''));
            longP = regexEscape(this.months(mom, ''));
            shortPieces.push(shortP);
            longPieces.push(longP);
            mixedPieces.push(longP);
            mixedPieces.push(shortP);
        }
        // Sorting makes sure if one month (or abbr) is a prefix of another it
        // will match the longer piece.
        shortPieces.sort(cmpLenRev);
        longPieces.sort(cmpLenRev);
        mixedPieces.sort(cmpLenRev);

        this._monthsRegex = new RegExp('^(' + mixedPieces.join('|') + ')', 'i');
        this._monthsShortRegex = this._monthsRegex;
        this._monthsStrictRegex = new RegExp(
            '^(' + longPieces.join('|') + ')',
            'i'
        );
        this._monthsShortStrictRegex = new RegExp(
            '^(' + shortPieces.join('|') + ')',
            'i'
        );
    }

    function createDate(y, m, d, h, M, s, ms) {
        // can't just apply() to create a date:
        // https://stackoverflow.com/q/181348
        var date;
        // the date constructor remaps years 0-99 to 1900-1999
        if (y < 100 && y >= 0) {
            // preserve leap years using a full 400 year cycle, then reset
            date = new Date(y + 400, m, d, h, M, s, ms);
            if (isFinite(date.getFullYear())) {
                date.setFullYear(y);
            }
        } else {
            date = new Date(y, m, d, h, M, s, ms);
        }

        return date;
    }

    function createUTCDate(y) {
        var date, args;
        // the Date.UTC function remaps years 0-99 to 1900-1999
        if (y < 100 && y >= 0) {
            args = Array.prototype.slice.call(arguments);
            // preserve leap years using a full 400 year cycle, then reset
            args[0] = y + 400;
            date = new Date(Date.UTC.apply(null, args));
            if (isFinite(date.getUTCFullYear())) {
                date.setUTCFullYear(y);
            }
        } else {
            date = new Date(Date.UTC.apply(null, arguments));
        }

        return date;
    }

    // start-of-first-week - start-of-year
    function firstWeekOffset(year, dow, doy) {
        var // first-week day -- which january is always in the first week (4 for iso, 1 for other)
            fwd = 7 + dow - doy,
            // first-week day local weekday -- which local weekday is fwd
            fwdlw = (7 + createUTCDate(year, 0, fwd).getUTCDay() - dow) % 7;

        return -fwdlw + fwd - 1;
    }

    // https://en.wikipedia.org/wiki/ISO_week_date#Calculating_a_date_given_the_year.2C_week_number_and_weekday
    function dayOfYearFromWeeks(year, week, weekday, dow, doy) {
        var localWeekday = (7 + weekday - dow) % 7,
            weekOffset = firstWeekOffset(year, dow, doy),
            dayOfYear = 1 + 7 * (week - 1) + localWeekday + weekOffset,
            resYear,
            resDayOfYear;

        if (dayOfYear <= 0) {
            resYear = year - 1;
            resDayOfYear = daysInYear(resYear) + dayOfYear;
        } else if (dayOfYear > daysInYear(year)) {
            resYear = year + 1;
            resDayOfYear = dayOfYear - daysInYear(year);
        } else {
            resYear = year;
            resDayOfYear = dayOfYear;
        }

        return {
            year: resYear,
            dayOfYear: resDayOfYear,
        };
    }

    function weekOfYear(mom, dow, doy) {
        var weekOffset = firstWeekOffset(mom.year(), dow, doy),
            week = Math.floor((mom.dayOfYear() - weekOffset - 1) / 7) + 1,
            resWeek,
            resYear;

        if (week < 1) {
            resYear = mom.year() - 1;
            resWeek = week + weeksInYear(resYear, dow, doy);
        } else if (week > weeksInYear(mom.year(), dow, doy)) {
            resWeek = week - weeksInYear(mom.year(), dow, doy);
            resYear = mom.year() + 1;
        } else {
            resYear = mom.year();
            resWeek = week;
        }

        return {
            week: resWeek,
            year: resYear,
        };
    }

    function weeksInYear(year, dow, doy) {
        var weekOffset = firstWeekOffset(year, dow, doy),
            weekOffsetNext = firstWeekOffset(year + 1, dow, doy);
        return (daysInYear(year) - weekOffset + weekOffsetNext) / 7;
    }

    // FORMATTING

    addFormatToken('w', ['ww', 2], 'wo', 'week');
    addFormatToken('W', ['WW', 2], 'Wo', 'isoWeek');

    // PARSING

    addRegexToken('w', match1to2, match1to2NoLeadingZero);
    addRegexToken('ww', match1to2, match2);
    addRegexToken('W', match1to2, match1to2NoLeadingZero);
    addRegexToken('WW', match1to2, match2);

    addWeekParseToken(
        ['w', 'ww', 'W', 'WW'],
        function (input, week, config, token) {
            week[token.substr(0, 1)] = toInt(input);
        }
    );

    // HELPERS

    // LOCALES

    function localeWeek(mom) {
        return weekOfYear(mom, this._week.dow, this._week.doy).week;
    }

    var defaultLocaleWeek = {
        dow: 0, // Sunday is the first day of the week.
        doy: 6, // The week that contains Jan 6th is the first week of the year.
    };

    function localeFirstDayOfWeek() {
        return this._week.dow;
    }

    function localeFirstDayOfYear() {
        return this._week.doy;
    }

    // MOMENTS

    function getSetWeek(input) {
        var week = this.localeData().week(this);
        return input == null ? week : this.add((input - week) * 7, 'd');
    }

    function getSetISOWeek(input) {
        var week = weekOfYear(this, 1, 4).week;
        return input == null ? week : this.add((input - week) * 7, 'd');
    }

    // FORMATTING

    addFormatToken('d', 0, 'do', 'day');

    addFormatToken('dd', 0, 0, function (format) {
        return this.localeData().weekdaysMin(this, format);
    });

    addFormatToken('ddd', 0, 0, function (format) {
        return this.localeData().weekdaysShort(this, format);
    });

    addFormatToken('dddd', 0, 0, function (format) {
        return this.localeData().weekdays(this, format);
    });

    addFormatToken('e', 0, 0, 'weekday');
    addFormatToken('E', 0, 0, 'isoWeekday');

    // PARSING

    addRegexToken('d', match1to2);
    addRegexToken('e', match1to2);
    addRegexToken('E', match1to2);
    addRegexToken('dd', function (isStrict, locale) {
        return locale.weekdaysMinRegex(isStrict);
    });
    addRegexToken('ddd', function (isStrict, locale) {
        return locale.weekdaysShortRegex(isStrict);
    });
    addRegexToken('dddd', function (isStrict, locale) {
        return locale.weekdaysRegex(isStrict);
    });

    addWeekParseToken(['dd', 'ddd', 'dddd'], function (input, week, config, token) {
        var weekday = config._locale.weekdaysParse(input, token, config._strict);
        // if we didn't get a weekday name, mark the date as invalid
        if (weekday != null) {
            week.d = weekday;
        } else {
            getParsingFlags(config).invalidWeekday = input;
        }
    });

    addWeekParseToken(['d', 'e', 'E'], function (input, week, config, token) {
        week[token] = toInt(input);
    });

    // HELPERS

    function parseWeekday(input, locale) {
        if (typeof input !== 'string') {
            return input;
        }

        if (!isNaN(input)) {
            return parseInt(input, 10);
        }

        input = locale.weekdaysParse(input);
        if (typeof input === 'number') {
            return input;
        }

        return null;
    }

    function parseIsoWeekday(input, locale) {
        if (typeof input === 'string') {
            return locale.weekdaysParse(input) % 7 || 7;
        }
        return isNaN(input) ? null : input;
    }

    // LOCALES
    function shiftWeekdays(ws, n) {
        return ws.slice(n, 7).concat(ws.slice(0, n));
    }

    var defaultLocaleWeekdays =
            'Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday'.split('_'),
        defaultLocaleWeekdaysShort = 'Sun_Mon_Tue_Wed_Thu_Fri_Sat'.split('_'),
        defaultLocaleWeekdaysMin = 'Su_Mo_Tu_We_Th_Fr_Sa'.split('_'),
        defaultWeekdaysRegex = matchWord,
        defaultWeekdaysShortRegex = matchWord,
        defaultWeekdaysMinRegex = matchWord;

    function localeWeekdays(m, format) {
        var weekdays = isArray(this._weekdays)
            ? this._weekdays
            : this._weekdays[
                  m && m !== true && this._weekdays.isFormat.test(format)
                      ? 'format'
                      : 'standalone'
              ];
        return m === true
            ? shiftWeekdays(weekdays, this._week.dow)
            : m
              ? weekdays[m.day()]
              : weekdays;
    }

    function localeWeekdaysShort(m) {
        return m === true
            ? shiftWeekdays(this._weekdaysShort, this._week.dow)
            : m
              ? this._weekdaysShort[m.day()]
              : this._weekdaysShort;
    }

    function localeWeekdaysMin(m) {
        return m === true
            ? shiftWeekdays(this._weekdaysMin, this._week.dow)
            : m
              ? this._weekdaysMin[m.day()]
              : this._weekdaysMin;
    }

    function handleStrictParse$1(weekdayName, format, strict) {
        var i,
            ii,
            mom,
            llc = weekdayName.toLocaleLowerCase();
        if (!this._weekdaysParse) {
            this._weekdaysParse = [];
            this._shortWeekdaysParse = [];
            this._minWeekdaysParse = [];

            for (i = 0; i < 7; ++i) {
                mom = createUTC([2000, 1]).day(i);
                this._minWeekdaysParse[i] = this.weekdaysMin(
                    mom,
                    ''
                ).toLocaleLowerCase();
                this._shortWeekdaysParse[i] = this.weekdaysShort(
                    mom,
                    ''
                ).toLocaleLowerCase();
                this._weekdaysParse[i] = this.weekdays(mom, '').toLocaleLowerCase();
            }
        }

        if (strict) {
            if (format === 'dddd') {
                ii = indexOf.call(this._weekdaysParse, llc);
                return ii !== -1 ? ii : null;
            } else if (format === 'ddd') {
                ii = indexOf.call(this._shortWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            } else {
                ii = indexOf.call(this._minWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            }
        } else {
            if (format === 'dddd') {
                ii = indexOf.call(this._weekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._shortWeekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._minWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            } else if (format === 'ddd') {
                ii = indexOf.call(this._shortWeekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._weekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._minWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            } else {
                ii = indexOf.call(this._minWeekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._weekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._shortWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            }
        }
    }

    function localeWeekdaysParse(weekdayName, format, strict) {
        var i, mom, regex;

        if (this._weekdaysParseExact) {
            return handleStrictParse$1.call(this, weekdayName, format, strict);
        }

        if (!this._weekdaysParse) {
            this._weekdaysParse = [];
            this._minWeekdaysParse = [];
            this._shortWeekdaysParse = [];
            this._fullWeekdaysParse = [];
        }

        for (i = 0; i < 7; i++) {
            // make the regex if we don't have it already

            mom = createUTC([2000, 1]).day(i);
            if (strict && !this._fullWeekdaysParse[i]) {
                this._fullWeekdaysParse[i] = new RegExp(
                    '^' + this.weekdays(mom, '').replace('.', '\\.?') + '$',
                    'i'
                );
                this._shortWeekdaysParse[i] = new RegExp(
                    '^' + this.weekdaysShort(mom, '').replace('.', '\\.?') + '$',
                    'i'
                );
                this._minWeekdaysParse[i] = new RegExp(
                    '^' + this.weekdaysMin(mom, '').replace('.', '\\.?') + '$',
                    'i'
                );
            }
            if (!this._weekdaysParse[i]) {
                regex =
                    '^' +
                    this.weekdays(mom, '') +
                    '|^' +
                    this.weekdaysShort(mom, '') +
                    '|^' +
                    this.weekdaysMin(mom, '');
                this._weekdaysParse[i] = new RegExp(regex.replace('.', ''), 'i');
            }
            // test the regex
            if (
                strict &&
                format === 'dddd' &&
                this._fullWeekdaysParse[i].test(weekdayName)
            ) {
                return i;
            } else if (
                strict &&
                format === 'ddd' &&
                this._shortWeekdaysParse[i].test(weekdayName)
            ) {
                return i;
            } else if (
                strict &&
                format === 'dd' &&
                this._minWeekdaysParse[i].test(weekdayName)
            ) {
                return i;
            } else if (!strict && this._weekdaysParse[i].test(weekdayName)) {
                return i;
            }
        }
    }

    // MOMENTS

    function getSetDayOfWeek(input) {
        if (!this.isValid()) {
            return input != null ? this : NaN;
        }

        var day = get(this, 'Day');
        if (input != null) {
            input = parseWeekday(input, this.localeData());
            return this.add(input - day, 'd');
        } else {
            return day;
        }
    }

    function getSetLocaleDayOfWeek(input) {
        if (!this.isValid()) {
            return input != null ? this : NaN;
        }
        var weekday = (this.day() + 7 - this.localeData()._week.dow) % 7;
        return input == null ? weekday : this.add(input - weekday, 'd');
    }

    function getSetISODayOfWeek(input) {
        if (!this.isValid()) {
            return input != null ? this : NaN;
        }

        // behaves the same as moment#day except
        // as a getter, returns 7 instead of 0 (1-7 range instead of 0-6)
        // as a setter, sunday should belong to the previous week.

        if (input != null) {
            var weekday = parseIsoWeekday(input, this.localeData());
            return this.day(this.day() % 7 ? weekday : weekday - 7);
        } else {
            return this.day() || 7;
        }
    }

    function weekdaysRegex(isStrict) {
        if (this._weekdaysParseExact) {
            if (!hasOwnProp(this, '_weekdaysRegex')) {
                computeWeekdaysParse.call(this);
            }
            if (isStrict) {
                return this._weekdaysStrictRegex;
            } else {
                return this._weekdaysRegex;
            }
        } else {
            if (!hasOwnProp(this, '_weekdaysRegex')) {
                this._weekdaysRegex = defaultWeekdaysRegex;
            }
            return this._weekdaysStrictRegex && isStrict
                ? this._weekdaysStrictRegex
                : this._weekdaysRegex;
        }
    }

    function weekdaysShortRegex(isStrict) {
        if (this._weekdaysParseExact) {
            if (!hasOwnProp(this, '_weekdaysRegex')) {
                computeWeekdaysParse.call(this);
            }
            if (isStrict) {
                return this._weekdaysShortStrictRegex;
            } else {
                return this._weekdaysShortRegex;
            }
        } else {
            if (!hasOwnProp(this, '_weekdaysShortRegex')) {
                this._weekdaysShortRegex = defaultWeekdaysShortRegex;
            }
            return this._weekdaysShortStrictRegex && isStrict
                ? this._weekdaysShortStrictRegex
                : this._weekdaysShortRegex;
        }
    }

    function weekdaysMinRegex(isStrict) {
        if (this._weekdaysParseExact) {
            if (!hasOwnProp(this, '_weekdaysRegex')) {
                computeWeekdaysParse.call(this);
            }
            if (isStrict) {
                return this._weekdaysMinStrictRegex;
            } else {
                return this._weekdaysMinRegex;
            }
        } else {
            if (!hasOwnProp(this, '_weekdaysMinRegex')) {
                this._weekdaysMinRegex = defaultWeekdaysMinRegex;
            }
            return this._weekdaysMinStrictRegex && isStrict
                ? this._weekdaysMinStrictRegex
                : this._weekdaysMinRegex;
        }
    }

    function computeWeekdaysParse() {
        function cmpLenRev(a, b) {
            return b.length - a.length;
        }

        var minPieces = [],
            shortPieces = [],
            longPieces = [],
            mixedPieces = [],
            i,
            mom,
            minp,
            shortp,
            longp;
        for (i = 0; i < 7; i++) {
            // make the regex if we don't have it already
            mom = createUTC([2000, 1]).day(i);
            minp = regexEscape(this.weekdaysMin(mom, ''));
            shortp = regexEscape(this.weekdaysShort(mom, ''));
            longp = regexEscape(this.weekdays(mom, ''));
            minPieces.push(minp);
            shortPieces.push(shortp);
            longPieces.push(longp);
            mixedPieces.push(minp);
            mixedPieces.push(shortp);
            mixedPieces.push(longp);
        }
        // Sorting makes sure if one weekday (or abbr) is a prefix of another it
        // will match the longer piece.
        minPieces.sort(cmpLenRev);
        shortPieces.sort(cmpLenRev);
        longPieces.sort(cmpLenRev);
        mixedPieces.sort(cmpLenRev);

        this._weekdaysRegex = new RegExp('^(' + mixedPieces.join('|') + ')', 'i');
        this._weekdaysShortRegex = this._weekdaysRegex;
        this._weekdaysMinRegex = this._weekdaysRegex;

        this._weekdaysStrictRegex = new RegExp(
            '^(' + longPieces.join('|') + ')',
            'i'
        );
        this._weekdaysShortStrictRegex = new RegExp(
            '^(' + shortPieces.join('|') + ')',
            'i'
        );
        this._weekdaysMinStrictRegex = new RegExp(
            '^(' + minPieces.join('|') + ')',
            'i'
        );
    }

    // FORMATTING

    function hFormat() {
        return this.hours() % 12 || 12;
    }

    function kFormat() {
        return this.hours() || 24;
    }

    addFormatToken('H', ['HH', 2], 0, 'hour');
    addFormatToken('h', ['hh', 2], 0, hFormat);
    addFormatToken('k', ['kk', 2], 0, kFormat);

    addFormatToken('hmm', 0, 0, function () {
        return '' + hFormat.apply(this) + zeroFill(this.minutes(), 2);
    });

    addFormatToken('hmmss', 0, 0, function () {
        return (
            '' +
            hFormat.apply(this) +
            zeroFill(this.minutes(), 2) +
            zeroFill(this.seconds(), 2)
        );
    });

    addFormatToken('Hmm', 0, 0, function () {
        return '' + this.hours() + zeroFill(this.minutes(), 2);
    });

    addFormatToken('Hmmss', 0, 0, function () {
        return (
            '' +
            this.hours() +
            zeroFill(this.minutes(), 2) +
            zeroFill(this.seconds(), 2)
        );
    });

    function meridiem(token, lowercase) {
        addFormatToken(token, 0, 0, function () {
            return this.localeData().meridiem(
                this.hours(),
                this.minutes(),
                lowercase
            );
        });
    }

    meridiem('a', true);
    meridiem('A', false);

    // PARSING

    function matchMeridiem(isStrict, locale) {
        return locale._meridiemParse;
    }

    addRegexToken('a', matchMeridiem);
    addRegexToken('A', matchMeridiem);
    addRegexToken('H', match1to2, match1to2HasZero);
    addRegexToken('h', match1to2, match1to2NoLeadingZero);
    addRegexToken('k', match1to2, match1to2NoLeadingZero);
    addRegexToken('HH', match1to2, match2);
    addRegexToken('hh', match1to2, match2);
    addRegexToken('kk', match1to2, match2);

    addRegexToken('hmm', match3to4);
    addRegexToken('hmmss', match5to6);
    addRegexToken('Hmm', match3to4);
    addRegexToken('Hmmss', match5to6);

    addParseToken(['H', 'HH'], HOUR);
    addParseToken(['k', 'kk'], function (input, array, config) {
        var kInput = toInt(input);
        array[HOUR] = kInput === 24 ? 0 : kInput;
    });
    addParseToken(['a', 'A'], function (input, array, config) {
        config._isPm = config._locale.isPM(input);
        config._meridiem = input;
    });
    addParseToken(['h', 'hh'], function (input, array, config) {
        array[HOUR] = toInt(input);
        getParsingFlags(config).bigHour = true;
    });
    addParseToken('hmm', function (input, array, config) {
        var pos = input.length - 2;
        array[HOUR] = toInt(input.substr(0, pos));
        array[MINUTE] = toInt(input.substr(pos));
        getParsingFlags(config).bigHour = true;
    });
    addParseToken('hmmss', function (input, array, config) {
        var pos1 = input.length - 4,
            pos2 = input.length - 2;
        array[HOUR] = toInt(input.substr(0, pos1));
        array[MINUTE] = toInt(input.substr(pos1, 2));
        array[SECOND] = toInt(input.substr(pos2));
        getParsingFlags(config).bigHour = true;
    });
    addParseToken('Hmm', function (input, array, config) {
        var pos = input.length - 2;
        array[HOUR] = toInt(input.substr(0, pos));
        array[MINUTE] = toInt(input.substr(pos));
    });
    addParseToken('Hmmss', function (input, array, config) {
        var pos1 = input.length - 4,
            pos2 = input.length - 2;
        array[HOUR] = toInt(input.substr(0, pos1));
        array[MINUTE] = toInt(input.substr(pos1, 2));
        array[SECOND] = toInt(input.substr(pos2));
    });

    // LOCALES

    function localeIsPM(input) {
        // IE8 Quirks Mode & IE7 Standards Mode do not allow accessing strings like arrays
        // Using charAt should be more compatible.
        return (input + '').toLowerCase().charAt(0) === 'p';
    }

    var defaultLocaleMeridiemParse = /[ap]\.?m?\.?/i,
        // Setting the hour should keep the time, because the user explicitly
        // specified which hour they want. So trying to maintain the same hour (in
        // a new timezone) makes sense. Adding/subtracting hours does not follow
        // this rule.
        getSetHour = makeGetSet('Hours', true);

    function localeMeridiem(hours, minutes, isLower) {
        if (hours > 11) {
            return isLower ? 'pm' : 'PM';
        } else {
            return isLower ? 'am' : 'AM';
        }
    }

    var baseConfig = {
        calendar: defaultCalendar,
        longDateFormat: defaultLongDateFormat,
        invalidDate: defaultInvalidDate,
        ordinal: defaultOrdinal,
        dayOfMonthOrdinalParse: defaultDayOfMonthOrdinalParse,
        relativeTime: defaultRelativeTime,

        months: defaultLocaleMonths,
        monthsShort: defaultLocaleMonthsShort,

        week: defaultLocaleWeek,

        weekdays: defaultLocaleWeekdays,
        weekdaysMin: defaultLocaleWeekdaysMin,
        weekdaysShort: defaultLocaleWeekdaysShort,

        meridiemParse: defaultLocaleMeridiemParse,
    };

    // internal storage for locale config files
    var locales = {},
        localeFamilies = {},
        globalLocale;

    function commonPrefix(arr1, arr2) {
        var i,
            minl = Math.min(arr1.length, arr2.length);
        for (i = 0; i < minl; i += 1) {
            if (arr1[i] !== arr2[i]) {
                return i;
            }
        }
        return minl;
    }

    function normalizeLocale(key) {
        return key ? key.toLowerCase().replace('_', '-') : key;
    }

    // pick the locale from the array
    // try ['en-au', 'en-gb'] as 'en-au', 'en-gb', 'en', as in move through the list trying each
    // substring from most specific to least, but move to the next array item if it's a more specific variant than the current root
    function chooseLocale(names) {
        var i = 0,
            j,
            next,
            locale,
            split;

        while (i < names.length) {
            split = normalizeLocale(names[i]).split('-');
            j = split.length;
            next = normalizeLocale(names[i + 1]);
            next = next ? next.split('-') : null;
            while (j > 0) {
                locale = loadLocale(split.slice(0, j).join('-'));
                if (locale) {
                    return locale;
                }
                if (
                    next &&
                    next.length >= j &&
                    commonPrefix(split, next) >= j - 1
                ) {
                    //the next array item is better than a shallower substring of this one
                    break;
                }
                j--;
            }
            i++;
        }
        return globalLocale;
    }

    function isLocaleNameSane(name) {
        // Prevent names that look like filesystem paths, i.e contain '/' or '\'
        // Ensure name is available and function returns boolean
        return !!(name && name.match('^[^/\\\\]*$'));
    }

    function loadLocale(name) {
        var oldLocale = null,
            aliasedRequire;
        // TODO: Find a better way to register and load all the locales in Node
        if (
            locales[name] === undefined &&
            typeof module !== 'undefined' &&
            module &&
            module.exports &&
            isLocaleNameSane(name)
        ) {
            try {
                oldLocale = globalLocale._abbr;
                aliasedRequire = require;
                aliasedRequire('./locale/' + name);
                getSetGlobalLocale(oldLocale);
            } catch (e) {
                // mark as not found to avoid repeating expensive file require call causing high CPU
                // when trying to find en-US, en_US, en-us for every format call
                locales[name] = null; // null means not found
            }
        }
        return locales[name];
    }

    // This function will load locale and then set the global locale.  If
    // no arguments are passed in, it will simply return the current global
    // locale key.
    function getSetGlobalLocale(key, values) {
        var data;
        if (key) {
            if (isUndefined(values)) {
                data = getLocale(key);
            } else {
                data = defineLocale(key, values);
            }

            if (data) {
                // moment.duration._locale = moment._locale = data;
                globalLocale = data;
            } else {
                if (typeof console !== 'undefined' && console.warn) {
                    //warn user if arguments are passed but the locale could not be set
                    console.warn(
                        'Locale ' + key + ' not found. Did you forget to load it?'
                    );
                }
            }
        }

        return globalLocale._abbr;
    }

    function defineLocale(name, config) {
        if (config !== null) {
            var locale,
                parentConfig = baseConfig;
            config.abbr = name;
            if (locales[name] != null) {
                deprecateSimple(
                    'defineLocaleOverride',
                    'use moment.updateLocale(localeName, config) to change ' +
                        'an existing locale. moment.defineLocale(localeName, ' +
                        'config) should only be used for creating a new locale ' +
                        'See http://momentjs.com/guides/#/warnings/define-locale/ for more info.'
                );
                parentConfig = locales[name]._config;
            } else if (config.parentLocale != null) {
                if (locales[config.parentLocale] != null) {
                    parentConfig = locales[config.parentLocale]._config;
                } else {
                    locale = loadLocale(config.parentLocale);
                    if (locale != null) {
                        parentConfig = locale._config;
                    } else {
                        if (!localeFamilies[config.parentLocale]) {
                            localeFamilies[config.parentLocale] = [];
                        }
                        localeFamilies[config.parentLocale].push({
                            name: name,
                            config: config,
                        });
                        return null;
                    }
                }
            }
            locales[name] = new Locale(mergeConfigs(parentConfig, config));

            if (localeFamilies[name]) {
                localeFamilies[name].forEach(function (x) {
                    defineLocale(x.name, x.config);
                });
            }

            // backwards compat for now: also set the locale
            // make sure we set the locale AFTER all child locales have been
            // created, so we won't end up with the child locale set.
            getSetGlobalLocale(name);

            return locales[name];
        } else {
            // useful for testing
            delete locales[name];
            return null;
        }
    }

    function updateLocale(name, config) {
        if (config != null) {
            var locale,
                tmpLocale,
                parentConfig = baseConfig;

            if (locales[name] != null && locales[name].parentLocale != null) {
                // Update existing child locale in-place to avoid memory-leaks
                locales[name].set(mergeConfigs(locales[name]._config, config));
            } else {
                // MERGE
                tmpLocale = loadLocale(name);
                if (tmpLocale != null) {
                    parentConfig = tmpLocale._config;
                }
                config = mergeConfigs(parentConfig, config);
                if (tmpLocale == null) {
                    // updateLocale is called for creating a new locale
                    // Set abbr so it will have a name (getters return
                    // undefined otherwise).
                    config.abbr = name;
                }
                locale = new Locale(config);
                locale.parentLocale = locales[name];
                locales[name] = locale;
            }

            // backwards compat for now: also set the locale
            getSetGlobalLocale(name);
        } else {
            // pass null for config to unupdate, useful for tests
            if (locales[name] != null) {
                if (locales[name].parentLocale != null) {
                    locales[name] = locales[name].parentLocale;
                    if (name === getSetGlobalLocale()) {
                        getSetGlobalLocale(name);
                    }
                } else if (locales[name] != null) {
                    delete locales[name];
                }
            }
        }
        return locales[name];
    }

    // returns locale data
    function getLocale(key) {
        var locale;

        if (key && key._locale && key._locale._abbr) {
            key = key._locale._abbr;
        }

        if (!key) {
            return globalLocale;
        }

        if (!isArray(key)) {
            //short-circuit everything else
            locale = loadLocale(key);
            if (locale) {
                return locale;
            }
            key = [key];
        }

        return chooseLocale(key);
    }

    function listLocales() {
        return keys(locales);
    }

    function checkOverflow(m) {
        var overflow,
            a = m._a;

        if (a && getParsingFlags(m).overflow === -2) {
            overflow =
                a[MONTH] < 0 || a[MONTH] > 11
                    ? MONTH
                    : a[DATE] < 1 || a[DATE] > daysInMonth(a[YEAR], a[MONTH])
                      ? DATE
                      : a[HOUR] < 0 ||
                          a[HOUR] > 24 ||
                          (a[HOUR] === 24 &&
                              (a[MINUTE] !== 0 ||
                                  a[SECOND] !== 0 ||
                                  a[MILLISECOND] !== 0))
                        ? HOUR
                        : a[MINUTE] < 0 || a[MINUTE] > 59
                          ? MINUTE
                          : a[SECOND] < 0 || a[SECOND] > 59
                            ? SECOND
                            : a[MILLISECOND] < 0 || a[MILLISECOND] > 999
                              ? MILLISECOND
                              : -1;

            if (
                getParsingFlags(m)._overflowDayOfYear &&
                (overflow < YEAR || overflow > DATE)
            ) {
                overflow = DATE;
            }
            if (getParsingFlags(m)._overflowWeeks && overflow === -1) {
                overflow = WEEK;
            }
            if (getParsingFlags(m)._overflowWeekday && overflow === -1) {
                overflow = WEEKDAY;
            }

            getParsingFlags(m).overflow = overflow;
        }

        return m;
    }

    // iso 8601 regex
    // 0000-00-00 0000-W00 or 0000-W00-0 + T + 00 or 00:00 or 00:00:00 or 00:00:00.000 + +00:00 or +0000 or +00)
    var extendedIsoRegex =
            /^\s*((?:[+-]\d{6}|\d{4})-(?:\d\d-\d\d|W\d\d-\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?::\d\d(?::\d\d(?:[.,]\d+)?)?)?)([+-]\d\d(?::?\d\d)?|\s*Z)?)?$/,
        basicIsoRegex =
            /^\s*((?:[+-]\d{6}|\d{4})(?:\d\d\d\d|W\d\d\d|W\d\d|\d\d\d|\d\d|))(?:(T| )(\d\d(?:\d\d(?:\d\d(?:[.,]\d+)?)?)?)([+-]\d\d(?::?\d\d)?|\s*Z)?)?$/,
        tzRegex = /Z|[+-]\d\d(?::?\d\d)?/,
        isoDates = [
            ['YYYYYY-MM-DD', /[+-]\d{6}-\d\d-\d\d/],
            ['YYYY-MM-DD', /\d{4}-\d\d-\d\d/],
            ['GGGG-[W]WW-E', /\d{4}-W\d\d-\d/],
            ['GGGG-[W]WW', /\d{4}-W\d\d/, false],
            ['YYYY-DDD', /\d{4}-\d{3}/],
            ['YYYY-MM', /\d{4}-\d\d/, false],
            ['YYYYYYMMDD', /[+-]\d{10}/],
            ['YYYYMMDD', /\d{8}/],
            ['GGGG[W]WWE', /\d{4}W\d{3}/],
            ['GGGG[W]WW', /\d{4}W\d{2}/, false],
            ['YYYYDDD', /\d{7}/],
            ['YYYYMM', /\d{6}/, false],
            ['YYYY', /\d{4}/, false],
        ],
        // iso time formats and regexes
        isoTimes = [
            ['HH:mm:ss.SSSS', /\d\d:\d\d:\d\d\.\d+/],
            ['HH:mm:ss,SSSS', /\d\d:\d\d:\d\d,\d+/],
            ['HH:mm:ss', /\d\d:\d\d:\d\d/],
            ['HH:mm', /\d\d:\d\d/],
            ['HHmmss.SSSS', /\d\d\d\d\d\d\.\d+/],
            ['HHmmss,SSSS', /\d\d\d\d\d\d,\d+/],
            ['HHmmss', /\d\d\d\d\d\d/],
            ['HHmm', /\d\d\d\d/],
            ['HH', /\d\d/],
        ],
        aspNetJsonRegex = /^\/?Date\((-?\d+)/i,
        // RFC 2822 regex: For details see https://tools.ietf.org/html/rfc2822#section-3.3
        rfc2822 =
            /^(?:(Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s)?(\d{1,2})\s(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s(\d{2,4})\s(\d\d):(\d\d)(?::(\d\d))?\s(?:(UT|GMT|[ECMP][SD]T)|([Zz])|([+-]\d{4}))$/,
        obsOffsets = {
            UT: 0,
            GMT: 0,
            EDT: -4 * 60,
            EST: -5 * 60,
            CDT: -5 * 60,
            CST: -6 * 60,
            MDT: -6 * 60,
            MST: -7 * 60,
            PDT: -7 * 60,
            PST: -8 * 60,
        };

    // date from iso format
    function configFromISO(config) {
        var i,
            l,
            string = config._i,
            match = extendedIsoRegex.exec(string) || basicIsoRegex.exec(string),
            allowTime,
            dateFormat,
            timeFormat,
            tzFormat,
            isoDatesLen = isoDates.length,
            isoTimesLen = isoTimes.length;

        if (match) {
            getParsingFlags(config).iso = true;
            for (i = 0, l = isoDatesLen; i < l; i++) {
                if (isoDates[i][1].exec(match[1])) {
                    dateFormat = isoDates[i][0];
                    allowTime = isoDates[i][2] !== false;
                    break;
                }
            }
            if (dateFormat == null) {
                config._isValid = false;
                return;
            }
            if (match[3]) {
                for (i = 0, l = isoTimesLen; i < l; i++) {
                    if (isoTimes[i][1].exec(match[3])) {
                        // match[2] should be 'T' or space
                        timeFormat = (match[2] || ' ') + isoTimes[i][0];
                        break;
                    }
                }
                if (timeFormat == null) {
                    config._isValid = false;
                    return;
                }
            }
            if (!allowTime && timeFormat != null) {
                config._isValid = false;
                return;
            }
            if (match[4]) {
                if (tzRegex.exec(match[4])) {
                    tzFormat = 'Z';
                } else {
                    config._isValid = false;
                    return;
                }
            }
            config._f = dateFormat + (timeFormat || '') + (tzFormat || '');
            configFromStringAndFormat(config);
        } else {
            config._isValid = false;
        }
    }

    function extractFromRFC2822Strings(
        yearStr,
        monthStr,
        dayStr,
        hourStr,
        minuteStr,
        secondStr
    ) {
        var result = [
            untruncateYear(yearStr),
            defaultLocaleMonthsShort.indexOf(monthStr),
            parseInt(dayStr, 10),
            parseInt(hourStr, 10),
            parseInt(minuteStr, 10),
        ];

        if (secondStr) {
            result.push(parseInt(secondStr, 10));
        }

        return result;
    }

    function untruncateYear(yearStr) {
        var year = parseInt(yearStr, 10);
        if (year <= 49) {
            return 2000 + year;
        } else if (year <= 999) {
            return 1900 + year;
        }
        return year;
    }

    function preprocessRFC2822(s) {
        // Remove comments and folding whitespace and replace multiple-spaces with a single space
        return s
            .replace(/\([^()]*\)|[\n\t]/g, ' ')
            .replace(/(\s\s+)/g, ' ')
            .replace(/^\s\s*/, '')
            .replace(/\s\s*$/, '');
    }

    function checkWeekday(weekdayStr, parsedInput, config) {
        if (weekdayStr) {
            // TODO: Replace the vanilla JS Date object with an independent day-of-week check.
            var weekdayProvided = defaultLocaleWeekdaysShort.indexOf(weekdayStr),
                weekdayActual = new Date(
                    parsedInput[0],
                    parsedInput[1],
                    parsedInput[2]
                ).getDay();
            if (weekdayProvided !== weekdayActual) {
                getParsingFlags(config).weekdayMismatch = true;
                config._isValid = false;
                return false;
            }
        }
        return true;
    }

    function calculateOffset(obsOffset, militaryOffset, numOffset) {
        if (obsOffset) {
            return obsOffsets[obsOffset];
        } else if (militaryOffset) {
            // the only allowed military tz is Z
            return 0;
        } else {
            var hm = parseInt(numOffset, 10),
                m = hm % 100,
                h = (hm - m) / 100;
            return h * 60 + m;
        }
    }

    // date and time from ref 2822 format
    function configFromRFC2822(config) {
        var match = rfc2822.exec(preprocessRFC2822(config._i)),
            parsedArray;
        if (match) {
            parsedArray = extractFromRFC2822Strings(
                match[4],
                match[3],
                match[2],
                match[5],
                match[6],
                match[7]
            );
            if (!checkWeekday(match[1], parsedArray, config)) {
                return;
            }

            config._a = parsedArray;
            config._tzm = calculateOffset(match[8], match[9], match[10]);

            config._d = createUTCDate.apply(null, config._a);
            config._d.setUTCMinutes(config._d.getUTCMinutes() - config._tzm);

            getParsingFlags(config).rfc2822 = true;
        } else {
            config._isValid = false;
        }
    }

    // date from 1) ASP.NET, 2) ISO, 3) RFC 2822 formats, or 4) optional fallback if parsing isn't strict
    function configFromString(config) {
        var matched = aspNetJsonRegex.exec(config._i);
        if (matched !== null) {
            config._d = new Date(+matched[1]);
            return;
        }

        configFromISO(config);
        if (config._isValid === false) {
            delete config._isValid;
        } else {
            return;
        }

        configFromRFC2822(config);
        if (config._isValid === false) {
            delete config._isValid;
        } else {
            return;
        }

        if (config._strict) {
            config._isValid = false;
        } else {
            // Final attempt, use Input Fallback
            hooks.createFromInputFallback(config);
        }
    }

    hooks.createFromInputFallback = deprecate(
        'value provided is not in a recognized RFC2822 or ISO format. moment construction falls back to js Date(), ' +
            'which is not reliable across all browsers and versions. Non RFC2822/ISO date formats are ' +
            'discouraged. Please refer to http://momentjs.com/guides/#/warnings/js-date/ for more info.',
        function (config) {
            config._d = new Date(config._i + (config._useUTC ? ' UTC' : ''));
        }
    );

    // Pick the first defined of two or three arguments.
    function defaults(a, b, c) {
        if (a != null) {
            return a;
        }
        if (b != null) {
            return b;
        }
        return c;
    }

    function currentDateArray(config) {
        // hooks is actually the exported moment object
        var nowValue = new Date(hooks.now());
        if (config._useUTC) {
            return [
                nowValue.getUTCFullYear(),
                nowValue.getUTCMonth(),
                nowValue.getUTCDate(),
            ];
        }
        return [nowValue.getFullYear(), nowValue.getMonth(), nowValue.getDate()];
    }

    // convert an array to a date.
    // the array should mirror the parameters below
    // note: all values past the year are optional and will default to the lowest possible value.
    // [year, month, day , hour, minute, second, millisecond]
    function configFromArray(config) {
        var i,
            date,
            input = [],
            currentDate,
            expectedWeekday,
            yearToUse;

        if (config._d) {
            return;
        }

        currentDate = currentDateArray(config);

        //compute day of the year from weeks and weekdays
        if (config._w && config._a[DATE] == null && config._a[MONTH] == null) {
            dayOfYearFromWeekInfo(config);
        }

        //if the day of the year is set, figure out what it is
        if (config._dayOfYear != null) {
            yearToUse = defaults(config._a[YEAR], currentDate[YEAR]);

            if (
                config._dayOfYear > daysInYear(yearToUse) ||
                config._dayOfYear === 0
            ) {
                getParsingFlags(config)._overflowDayOfYear = true;
            }

            date = createUTCDate(yearToUse, 0, config._dayOfYear);
            config._a[MONTH] = date.getUTCMonth();
            config._a[DATE] = date.getUTCDate();
        }

        // Default to current date.
        // * if no year, month, day of month are given, default to today
        // * if day of month is given, default month and year
        // * if month is given, default only year
        // * if year is given, don't default anything
        for (i = 0; i < 3 && config._a[i] == null; ++i) {
            config._a[i] = input[i] = currentDate[i];
        }

        // Zero out whatever was not defaulted, including time
        for (; i < 7; i++) {
            config._a[i] = input[i] =
                config._a[i] == null ? (i === 2 ? 1 : 0) : config._a[i];
        }

        // Check for 24:00:00.000
        if (
            config._a[HOUR] === 24 &&
            config._a[MINUTE] === 0 &&
            config._a[SECOND] === 0 &&
            config._a[MILLISECOND] === 0
        ) {
            config._nextDay = true;
            config._a[HOUR] = 0;
        }

        config._d = (config._useUTC ? createUTCDate : createDate).apply(
            null,
            input
        );
        expectedWeekday = config._useUTC
            ? config._d.getUTCDay()
            : config._d.getDay();

        // Apply timezone offset from input. The actual utcOffset can be changed
        // with parseZone.
        if (config._tzm != null) {
            config._d.setUTCMinutes(config._d.getUTCMinutes() - config._tzm);
        }

        if (config._nextDay) {
            config._a[HOUR] = 24;
        }

        // check for mismatching day of week
        if (
            config._w &&
            typeof config._w.d !== 'undefined' &&
            config._w.d !== expectedWeekday
        ) {
            getParsingFlags(config).weekdayMismatch = true;
        }
    }

    function dayOfYearFromWeekInfo(config) {
        var w, weekYear, week, weekday, dow, doy, temp, weekdayOverflow, curWeek;

        w = config._w;
        if (w.GG != null || w.W != null || w.E != null) {
            dow = 1;
            doy = 4;

            // TODO: We need to take the current isoWeekYear, but that depends on
            // how we interpret now (local, utc, fixed offset). So create
            // a now version of current config (take local/utc/offset flags, and
            // create now).
            weekYear = defaults(
                w.GG,
                config._a[YEAR],
                weekOfYear(createLocal(), 1, 4).year
            );
            week = defaults(w.W, 1);
            weekday = defaults(w.E, 1);
            if (weekday < 1 || weekday > 7) {
                weekdayOverflow = true;
            }
        } else {
            dow = config._locale._week.dow;
            doy = config._locale._week.doy;

            curWeek = weekOfYear(createLocal(), dow, doy);

            weekYear = defaults(w.gg, config._a[YEAR], curWeek.year);

            // Default to current week.
            week = defaults(w.w, curWeek.week);

            if (w.d != null) {
                // weekday -- low day numbers are considered next week
                weekday = w.d;
                if (weekday < 0 || weekday > 6) {
                    weekdayOverflow = true;
                }
            } else if (w.e != null) {
                // local weekday -- counting starts from beginning of week
                weekday = w.e + dow;
                if (w.e < 0 || w.e > 6) {
                    weekdayOverflow = true;
                }
            } else {
                // default to beginning of week
                weekday = dow;
            }
        }
        if (week < 1 || week > weeksInYear(weekYear, dow, doy)) {
            getParsingFlags(config)._overflowWeeks = true;
        } else if (weekdayOverflow != null) {
            getParsingFlags(config)._overflowWeekday = true;
        } else {
            temp = dayOfYearFromWeeks(weekYear, week, weekday, dow, doy);
            config._a[YEAR] = temp.year;
            config._dayOfYear = temp.dayOfYear;
        }
    }

    // constant that refers to the ISO standard
    hooks.ISO_8601 = function () {};

    // constant that refers to the RFC 2822 form
    hooks.RFC_2822 = function () {};

    // date from string and format string
    function configFromStringAndFormat(config) {
        // TODO: Move this to another part of the creation flow to prevent circular deps
        if (config._f === hooks.ISO_8601) {
            configFromISO(config);
            return;
        }
        if (config._f === hooks.RFC_2822) {
            configFromRFC2822(config);
            return;
        }
        config._a = [];
        getParsingFlags(config).empty = true;

        // This array is used to make a Date, either with `new Date` or `Date.UTC`
        var string = '' + config._i,
            i,
            parsedInput,
            tokens,
            token,
            skipped,
            stringLength = string.length,
            totalParsedInputLength = 0,
            era,
            tokenLen;

        tokens =
            expandFormat(config._f, config._locale).match(formattingTokens) || [];
        tokenLen = tokens.length;
        for (i = 0; i < tokenLen; i++) {
            token = tokens[i];
            parsedInput = (string.match(getParseRegexForToken(token, config)) ||
                [])[0];
            if (parsedInput) {
                skipped = string.substr(0, string.indexOf(parsedInput));
                if (skipped.length > 0) {
                    getParsingFlags(config).unusedInput.push(skipped);
                }
                string = string.slice(
                    string.indexOf(parsedInput) + parsedInput.length
                );
                totalParsedInputLength += parsedInput.length;
            }
            // don't parse if it's not a known token
            if (formatTokenFunctions[token]) {
                if (parsedInput) {
                    getParsingFlags(config).empty = false;
                } else {
                    getParsingFlags(config).unusedTokens.push(token);
                }
                addTimeToArrayFromToken(token, parsedInput, config);
            } else if (config._strict && !parsedInput) {
                getParsingFlags(config).unusedTokens.push(token);
            }
        }

        // add remaining unparsed input length to the string
        getParsingFlags(config).charsLeftOver =
            stringLength - totalParsedInputLength;
        if (string.length > 0) {
            getParsingFlags(config).unusedInput.push(string);
        }

        // clear _12h flag if hour is <= 12
        if (
            config._a[HOUR] <= 12 &&
            getParsingFlags(config).bigHour === true &&
            config._a[HOUR] > 0
        ) {
            getParsingFlags(config).bigHour = undefined;
        }

        getParsingFlags(config).parsedDateParts = config._a.slice(0);
        getParsingFlags(config).meridiem = config._meridiem;
        // handle meridiem
        config._a[HOUR] = meridiemFixWrap(
            config._locale,
            config._a[HOUR],
            config._meridiem
        );

        // handle era
        era = getParsingFlags(config).era;
        if (era !== null) {
            config._a[YEAR] = config._locale.erasConvertYear(era, config._a[YEAR]);
        }

        configFromArray(config);
        checkOverflow(config);
    }

    function meridiemFixWrap(locale, hour, meridiem) {
        var isPm;

        if (meridiem == null) {
            // nothing to do
            return hour;
        }
        if (locale.meridiemHour != null) {
            return locale.meridiemHour(hour, meridiem);
        } else if (locale.isPM != null) {
            // Fallback
            isPm = locale.isPM(meridiem);
            if (isPm && hour < 12) {
                hour += 12;
            }
            if (!isPm && hour === 12) {
                hour = 0;
            }
            return hour;
        } else {
            // this is not supposed to happen
            return hour;
        }
    }

    // date from string and array of format strings
    function configFromStringAndArray(config) {
        var tempConfig,
            bestMoment,
            scoreToBeat,
            i,
            currentScore,
            validFormatFound,
            bestFormatIsValid = false,
            configfLen = config._f.length;

        if (configfLen === 0) {
            getParsingFlags(config).invalidFormat = true;
            config._d = new Date(NaN);
            return;
        }

        for (i = 0; i < configfLen; i++) {
            currentScore = 0;
            validFormatFound = false;
            tempConfig = copyConfig({}, config);
            if (config._useUTC != null) {
                tempConfig._useUTC = config._useUTC;
            }
            tempConfig._f = config._f[i];
            configFromStringAndFormat(tempConfig);

            if (isValid(tempConfig)) {
                validFormatFound = true;
            }

            // if there is any input that was not parsed add a penalty for that format
            currentScore += getParsingFlags(tempConfig).charsLeftOver;

            //or tokens
            currentScore += getParsingFlags(tempConfig).unusedTokens.length * 10;

            getParsingFlags(tempConfig).score = currentScore;

            if (!bestFormatIsValid) {
                if (
                    scoreToBeat == null ||
                    currentScore < scoreToBeat ||
                    validFormatFound
                ) {
                    scoreToBeat = currentScore;
                    bestMoment = tempConfig;
                    if (validFormatFound) {
                        bestFormatIsValid = true;
                    }
                }
            } else {
                if (currentScore < scoreToBeat) {
                    scoreToBeat = currentScore;
                    bestMoment = tempConfig;
                }
            }
        }

        extend(config, bestMoment || tempConfig);
    }

    function configFromObject(config) {
        if (config._d) {
            return;
        }

        var i = normalizeObjectUnits(config._i),
            dayOrDate = i.day === undefined ? i.date : i.day;
        config._a = map(
            [i.year, i.month, dayOrDate, i.hour, i.minute, i.second, i.millisecond],
            function (obj) {
                return obj && parseInt(obj, 10);
            }
        );

        configFromArray(config);
    }

    function createFromConfig(config) {
        var res = new Moment(checkOverflow(prepareConfig(config)));
        if (res._nextDay) {
            // Adding is smart enough around DST
            res.add(1, 'd');
            res._nextDay = undefined;
        }

        return res;
    }

    function prepareConfig(config) {
        var input = config._i,
            format = config._f;

        config._locale = config._locale || getLocale(config._l);

        if (input === null || (format === undefined && input === '')) {
            return createInvalid({ nullInput: true });
        }

        if (typeof input === 'string') {
            config._i = input = config._locale.preparse(input);
        }

        if (isMoment(input)) {
            return new Moment(checkOverflow(input));
        } else if (isDate(input)) {
            config._d = input;
        } else if (isArray(format)) {
            configFromStringAndArray(config);
        } else if (format) {
            configFromStringAndFormat(config);
        } else {
            configFromInput(config);
        }

        if (!isValid(config)) {
            config._d = null;
        }

        return config;
    }

    function configFromInput(config) {
        var input = config._i;
        if (isUndefined(input)) {
            config._d = new Date(hooks.now());
        } else if (isDate(input)) {
            config._d = new Date(input.valueOf());
        } else if (typeof input === 'string') {
            configFromString(config);
        } else if (isArray(input)) {
            config._a = map(input.slice(0), function (obj) {
                return parseInt(obj, 10);
            });
            configFromArray(config);
        } else if (isObject(input)) {
            configFromObject(config);
        } else if (isNumber(input)) {
            // from milliseconds
            config._d = new Date(input);
        } else {
            hooks.createFromInputFallback(config);
        }
    }

    function createLocalOrUTC(input, format, locale, strict, isUTC) {
        var c = {};

        if (format === true || format === false) {
            strict = format;
            format = undefined;
        }

        if (locale === true || locale === false) {
            strict = locale;
            locale = undefined;
        }

        if (
            (isObject(input) && isObjectEmpty(input)) ||
            (isArray(input) && input.length === 0)
        ) {
            input = undefined;
        }
        // object construction must be done this way.
        // https://github.com/moment/moment/issues/1423
        c._isAMomentObject = true;
        c._useUTC = c._isUTC = isUTC;
        c._l = locale;
        c._i = input;
        c._f = format;
        c._strict = strict;

        return createFromConfig(c);
    }

    function createLocal(input, format, locale, strict) {
        return createLocalOrUTC(input, format, locale, strict, false);
    }

    var prototypeMin = deprecate(
            'moment().min is deprecated, use moment.max instead. http://momentjs.com/guides/#/warnings/min-max/',
            function () {
                var other = createLocal.apply(null, arguments);
                if (this.isValid() && other.isValid()) {
                    return other < this ? this : other;
                } else {
                    return createInvalid();
                }
            }
        ),
        prototypeMax = deprecate(
            'moment().max is deprecated, use moment.min instead. http://momentjs.com/guides/#/warnings/min-max/',
            function () {
                var other = createLocal.apply(null, arguments);
                if (this.isValid() && other.isValid()) {
                    return other > this ? this : other;
                } else {
                    return createInvalid();
                }
            }
        );

    // Pick a moment m from moments so that m[fn](other) is true for all
    // other. This relies on the function fn to be transitive.
    //
    // moments should either be an array of moment objects or an array, whose
    // first element is an array of moment objects.
    function pickBy(fn, moments) {
        var res, i;
        if (moments.length === 1 && isArray(moments[0])) {
            moments = moments[0];
        }
        if (!moments.length) {
            return createLocal();
        }
        res = moments[0];
        for (i = 1; i < moments.length; ++i) {
            if (!moments[i].isValid() || moments[i][fn](res)) {
                res = moments[i];
            }
        }
        return res;
    }

    // TODO: Use [].sort instead?
    function min() {
        var args = [].slice.call(arguments, 0);

        return pickBy('isBefore', args);
    }

    function max() {
        var args = [].slice.call(arguments, 0);

        return pickBy('isAfter', args);
    }

    var now = function () {
        return Date.now ? Date.now() : +new Date();
    };

    var ordering = [
        'year',
        'quarter',
        'month',
        'week',
        'day',
        'hour',
        'minute',
        'second',
        'millisecond',
    ];

    function isDurationValid(m) {
        var key,
            unitHasDecimal = false,
            i,
            orderLen = ordering.length;
        for (key in m) {
            if (
                hasOwnProp(m, key) &&
                !(
                    indexOf.call(ordering, key) !== -1 &&
                    (m[key] == null || !isNaN(m[key]))
                )
            ) {
                return false;
            }
        }

        for (i = 0; i < orderLen; ++i) {
            if (m[ordering[i]]) {
                if (unitHasDecimal) {
                    return false; // only allow non-integers for smallest unit
                }
                if (parseFloat(m[ordering[i]]) !== toInt(m[ordering[i]])) {
                    unitHasDecimal = true;
                }
            }
        }

        return true;
    }

    function isValid$1() {
        return this._isValid;
    }

    function createInvalid$1() {
        return createDuration(NaN);
    }

    function Duration(duration) {
        var normalizedInput = normalizeObjectUnits(duration),
            years = normalizedInput.year || 0,
            quarters = normalizedInput.quarter || 0,
            months = normalizedInput.month || 0,
            weeks = normalizedInput.week || normalizedInput.isoWeek || 0,
            days = normalizedInput.day || 0,
            hours = normalizedInput.hour || 0,
            minutes = normalizedInput.minute || 0,
            seconds = normalizedInput.second || 0,
            milliseconds = normalizedInput.millisecond || 0;

        this._isValid = isDurationValid(normalizedInput);

        // representation for dateAddRemove
        this._milliseconds =
            +milliseconds +
            seconds * 1e3 + // 1000
            minutes * 6e4 + // 1000 * 60
            hours * 1000 * 60 * 60; //using 1000 * 60 * 60 instead of 36e5 to avoid floating point rounding errors https://github.com/moment/moment/issues/2978
        // Because of dateAddRemove treats 24 hours as different from a
        // day when working around DST, we need to store them separately
        this._days = +days + weeks * 7;
        // It is impossible to translate months into days without knowing
        // which months you are are talking about, so we have to store
        // it separately.
        this._months = +months + quarters * 3 + years * 12;

        this._data = {};

        this._locale = getLocale();

        this._bubble();
    }

    function isDuration(obj) {
        return obj instanceof Duration;
    }

    function absRound(number) {
        if (number < 0) {
            return Math.round(-1 * number) * -1;
        } else {
            return Math.round(number);
        }
    }

    // compare two arrays, return the number of differences
    function compareArrays(array1, array2, dontConvert) {
        var len = Math.min(array1.length, array2.length),
            lengthDiff = Math.abs(array1.length - array2.length),
            diffs = 0,
            i;
        for (i = 0; i < len; i++) {
            if (
                (dontConvert && array1[i] !== array2[i]) ||
                (!dontConvert && toInt(array1[i]) !== toInt(array2[i]))
            ) {
                diffs++;
            }
        }
        return diffs + lengthDiff;
    }

    // FORMATTING

    function offset(token, separator) {
        addFormatToken(token, 0, 0, function () {
            var offset = this.utcOffset(),
                sign = '+';
            if (offset < 0) {
                offset = -offset;
                sign = '-';
            }
            return (
                sign +
                zeroFill(~~(offset / 60), 2) +
                separator +
                zeroFill(~~offset % 60, 2)
            );
        });
    }

    offset('Z', ':');
    offset('ZZ', '');

    // PARSING

    addRegexToken('Z', matchShortOffset);
    addRegexToken('ZZ', matchShortOffset);
    addParseToken(['Z', 'ZZ'], function (input, array, config) {
        config._useUTC = true;
        config._tzm = offsetFromString(matchShortOffset, input);
    });

    // HELPERS

    // timezone chunker
    // '+10:00' > ['10',  '00']
    // '-1530'  > ['-15', '30']
    var chunkOffset = /([\+\-]|\d\d)/gi;

    function offsetFromString(matcher, string) {
        var matches = (string || '').match(matcher),
            chunk,
            parts,
            minutes;

        if (matches === null) {
            return null;
        }

        chunk = matches[matches.length - 1] || [];
        parts = (chunk + '').match(chunkOffset) || ['-', 0, 0];
        minutes = +(parts[1] * 60) + toInt(parts[2]);

        return minutes === 0 ? 0 : parts[0] === '+' ? minutes : -minutes;
    }

    // Return a moment from input, that is local/utc/zone equivalent to model.
    function cloneWithOffset(input, model) {
        var res, diff;
        if (model._isUTC) {
            res = model.clone();
            diff =
                (isMoment(input) || isDate(input)
                    ? input.valueOf()
                    : createLocal(input).valueOf()) - res.valueOf();
            // Use low-level api, because this fn is low-level api.
            res._d.setTime(res._d.valueOf() + diff);
            hooks.updateOffset(res, false);
            return res;
        } else {
            return createLocal(input).local();
        }
    }

    function getDateOffset(m) {
        // On Firefox.24 Date#getTimezoneOffset returns a floating point.
        // https://github.com/moment/moment/pull/1871
        return -Math.round(m._d.getTimezoneOffset());
    }

    // HOOKS

    // This function will be called whenever a moment is mutated.
    // It is intended to keep the offset in sync with the timezone.
    hooks.updateOffset = function () {};

    // MOMENTS

    // keepLocalTime = true means only change the timezone, without
    // affecting the local hour. So 5:31:26 +0300 --[utcOffset(2, true)]-->
    // 5:31:26 +0200 It is possible that 5:31:26 doesn't exist with offset
    // +0200, so we adjust the time as needed, to be valid.
    //
    // Keeping the time actually adds/subtracts (one hour)
    // from the actual represented time. That is why we call updateOffset
    // a second time. In case it wants us to change the offset again
    // _changeInProgress == true case, then we have to adjust, because
    // there is no such time in the given timezone.
    function getSetOffset(input, keepLocalTime, keepMinutes) {
        var offset = this._offset || 0,
            localAdjust;
        if (!this.isValid()) {
            return input != null ? this : NaN;
        }
        if (input != null) {
            if (typeof input === 'string') {
                input = offsetFromString(matchShortOffset, input);
                if (input === null) {
                    return this;
                }
            } else if (Math.abs(input) < 16 && !keepMinutes) {
                input = input * 60;
            }
            if (!this._isUTC && keepLocalTime) {
                localAdjust = getDateOffset(this);
            }
            this._offset = input;
            this._isUTC = true;
            if (localAdjust != null) {
                this.add(localAdjust, 'm');
            }
            if (offset !== input) {
                if (!keepLocalTime || this._changeInProgress) {
                    addSubtract(
                        this,
                        createDuration(input - offset, 'm'),
                        1,
                        false
                    );
                } else if (!this._changeInProgress) {
                    this._changeInProgress = true;
                    hooks.updateOffset(this, true);
                    this._changeInProgress = null;
                }
            }
            return this;
        } else {
            return this._isUTC ? offset : getDateOffset(this);
        }
    }

    function getSetZone(input, keepLocalTime) {
        if (input != null) {
            if (typeof input !== 'string') {
                input = -input;
            }

            this.utcOffset(input, keepLocalTime);

            return this;
        } else {
            return -this.utcOffset();
        }
    }

    function setOffsetToUTC(keepLocalTime) {
        return this.utcOffset(0, keepLocalTime);
    }

    function setOffsetToLocal(keepLocalTime) {
        if (this._isUTC) {
            this.utcOffset(0, keepLocalTime);
            this._isUTC = false;

            if (keepLocalTime) {
                this.subtract(getDateOffset(this), 'm');
            }
        }
        return this;
    }

    function setOffsetToParsedOffset() {
        if (this._tzm != null) {
            this.utcOffset(this._tzm, false, true);
        } else if (typeof this._i === 'string') {
            var tZone = offsetFromString(matchOffset, this._i);
            if (tZone != null) {
                this.utcOffset(tZone);
            } else {
                this.utcOffset(0, true);
            }
        }
        return this;
    }

    function hasAlignedHourOffset(input) {
        if (!this.isValid()) {
            return false;
        }
        input = input ? createLocal(input).utcOffset() : 0;

        return (this.utcOffset() - input) % 60 === 0;
    }

    function isDaylightSavingTime() {
        return (
            this.utcOffset() > this.clone().month(0).utcOffset() ||
            this.utcOffset() > this.clone().month(5).utcOffset()
        );
    }

    function isDaylightSavingTimeShifted() {
        if (!isUndefined(this._isDSTShifted)) {
            return this._isDSTShifted;
        }

        var c = {},
            other;

        copyConfig(c, this);
        c = prepareConfig(c);

        if (c._a) {
            other = c._isUTC ? createUTC(c._a) : createLocal(c._a);
            this._isDSTShifted =
                this.isValid() && compareArrays(c._a, other.toArray()) > 0;
        } else {
            this._isDSTShifted = false;
        }

        return this._isDSTShifted;
    }

    function isLocal() {
        return this.isValid() ? !this._isUTC : false;
    }

    function isUtcOffset() {
        return this.isValid() ? this._isUTC : false;
    }

    function isUtc() {
        return this.isValid() ? this._isUTC && this._offset === 0 : false;
    }

    // ASP.NET json date format regex
    var aspNetRegex = /^(-|\+)?(?:(\d*)[. ])?(\d+):(\d+)(?::(\d+)(\.\d*)?)?$/,
        // from http://docs.closure-library.googlecode.com/git/closure_goog_date_date.js.source.html
        // somewhat more in line with 4.4.3.2 2004 spec, but allows decimal anywhere
        // and further modified to allow for strings containing both week and day
        isoRegex =
            /^(-|\+)?P(?:([-+]?[0-9,.]*)Y)?(?:([-+]?[0-9,.]*)M)?(?:([-+]?[0-9,.]*)W)?(?:([-+]?[0-9,.]*)D)?(?:T(?:([-+]?[0-9,.]*)H)?(?:([-+]?[0-9,.]*)M)?(?:([-+]?[0-9,.]*)S)?)?$/;

    function createDuration(input, key) {
        var duration = input,
            // matching against regexp is expensive, do it on demand
            match = null,
            sign,
            ret,
            diffRes;

        if (isDuration(input)) {
            duration = {
                ms: input._milliseconds,
                d: input._days,
                M: input._months,
            };
        } else if (isNumber(input) || !isNaN(+input)) {
            duration = {};
            if (key) {
                duration[key] = +input;
            } else {
                duration.milliseconds = +input;
            }
        } else if ((match = aspNetRegex.exec(input))) {
            sign = match[1] === '-' ? -1 : 1;
            duration = {
                y: 0,
                d: toInt(match[DATE]) * sign,
                h: toInt(match[HOUR]) * sign,
                m: toInt(match[MINUTE]) * sign,
                s: toInt(match[SECOND]) * sign,
                ms: toInt(absRound(match[MILLISECOND] * 1000)) * sign, // the millisecond decimal point is included in the match
            };
        } else if ((match = isoRegex.exec(input))) {
            sign = match[1] === '-' ? -1 : 1;
            duration = {
                y: parseIso(match[2], sign),
                M: parseIso(match[3], sign),
                w: parseIso(match[4], sign),
                d: parseIso(match[5], sign),
                h: parseIso(match[6], sign),
                m: parseIso(match[7], sign),
                s: parseIso(match[8], sign),
            };
        } else if (duration == null) {
            // checks for null or undefined
            duration = {};
        } else if (
            typeof duration === 'object' &&
            ('from' in duration || 'to' in duration)
        ) {
            diffRes = momentsDifference(
                createLocal(duration.from),
                createLocal(duration.to)
            );

            duration = {};
            duration.ms = diffRes.milliseconds;
            duration.M = diffRes.months;
        }

        ret = new Duration(duration);

        if (isDuration(input) && hasOwnProp(input, '_locale')) {
            ret._locale = input._locale;
        }

        if (isDuration(input) && hasOwnProp(input, '_isValid')) {
            ret._isValid = input._isValid;
        }

        return ret;
    }

    createDuration.fn = Duration.prototype;
    createDuration.invalid = createInvalid$1;

    function parseIso(inp, sign) {
        // We'd normally use ~~inp for this, but unfortunately it also
        // converts floats to ints.
        // inp may be undefined, so careful calling replace on it.
        var res = inp && parseFloat(inp.replace(',', '.'));
        // apply sign while we're at it
        return (isNaN(res) ? 0 : res) * sign;
    }

    function positiveMomentsDifference(base, other) {
        var res = {};

        res.months =
            other.month() - base.month() + (other.year() - base.year()) * 12;
        if (base.clone().add(res.months, 'M').isAfter(other)) {
            --res.months;
        }

        res.milliseconds = +other - +base.clone().add(res.months, 'M');

        return res;
    }

    function momentsDifference(base, other) {
        var res;
        if (!(base.isValid() && other.isValid())) {
            return { milliseconds: 0, months: 0 };
        }

        other = cloneWithOffset(other, base);
        if (base.isBefore(other)) {
            res = positiveMomentsDifference(base, other);
        } else {
            res = positiveMomentsDifference(other, base);
            res.milliseconds = -res.milliseconds;
            res.months = -res.months;
        }

        return res;
    }

    // TODO: remove 'name' arg after deprecation is removed
    function createAdder(direction, name) {
        return function (val, period) {
            var dur, tmp;
            //invert the arguments, but complain about it
            if (period !== null && !isNaN(+period)) {
                deprecateSimple(
                    name,
                    'moment().' +
                        name +
                        '(period, number) is deprecated. Please use moment().' +
                        name +
                        '(number, period). ' +
                        'See http://momentjs.com/guides/#/warnings/add-inverted-param/ for more info.'
                );
                tmp = val;
                val = period;
                period = tmp;
            }

            dur = createDuration(val, period);
            addSubtract(this, dur, direction);
            return this;
        };
    }

    function addSubtract(mom, duration, isAdding, updateOffset) {
        var milliseconds = duration._milliseconds,
            days = absRound(duration._days),
            months = absRound(duration._months);

        if (!mom.isValid()) {
            // No op
            return;
        }

        updateOffset = updateOffset == null ? true : updateOffset;

        if (months) {
            setMonth(mom, get(mom, 'Month') + months * isAdding);
        }
        if (days) {
            set$1(mom, 'Date', get(mom, 'Date') + days * isAdding);
        }
        if (milliseconds) {
            mom._d.setTime(mom._d.valueOf() + milliseconds * isAdding);
        }
        if (updateOffset) {
            hooks.updateOffset(mom, days || months);
        }
    }

    var add = createAdder(1, 'add'),
        subtract = createAdder(-1, 'subtract');

    function isString(input) {
        return typeof input === 'string' || input instanceof String;
    }

    // type MomentInput = Moment | Date | string | number | (number | string)[] | MomentInputObject | void; // null | undefined
    function isMomentInput(input) {
        return (
            isMoment(input) ||
            isDate(input) ||
            isString(input) ||
            isNumber(input) ||
            isNumberOrStringArray(input) ||
            isMomentInputObject(input) ||
            input === null ||
            input === undefined
        );
    }

    function isMomentInputObject(input) {
        var objectTest = isObject(input) && !isObjectEmpty(input),
            propertyTest = false,
            properties = [
                'years',
                'year',
                'y',
                'months',
                'month',
                'M',
                'days',
                'day',
                'd',
                'dates',
                'date',
                'D',
                'hours',
                'hour',
                'h',
                'minutes',
                'minute',
                'm',
                'seconds',
                'second',
                's',
                'milliseconds',
                'millisecond',
                'ms',
            ],
            i,
            property,
            propertyLen = properties.length;

        for (i = 0; i < propertyLen; i += 1) {
            property = properties[i];
            propertyTest = propertyTest || hasOwnProp(input, property);
        }

        return objectTest && propertyTest;
    }

    function isNumberOrStringArray(input) {
        var arrayTest = isArray(input),
            dataTypeTest = false;
        if (arrayTest) {
            dataTypeTest =
                input.filter(function (item) {
                    return !isNumber(item) && isString(input);
                }).length === 0;
        }
        return arrayTest && dataTypeTest;
    }

    function isCalendarSpec(input) {
        var objectTest = isObject(input) && !isObjectEmpty(input),
            propertyTest = false,
            properties = [
                'sameDay',
                'nextDay',
                'lastDay',
                'nextWeek',
                'lastWeek',
                'sameElse',
            ],
            i,
            property;

        for (i = 0; i < properties.length; i += 1) {
            property = properties[i];
            propertyTest = propertyTest || hasOwnProp(input, property);
        }

        return objectTest && propertyTest;
    }

    function getCalendarFormat(myMoment, now) {
        var diff = myMoment.diff(now, 'days', true);
        return diff < -6
            ? 'sameElse'
            : diff < -1
              ? 'lastWeek'
              : diff < 0
                ? 'lastDay'
                : diff < 1
                  ? 'sameDay'
                  : diff < 2
                    ? 'nextDay'
                    : diff < 7
                      ? 'nextWeek'
                      : 'sameElse';
    }

    function calendar$1(time, formats) {
        // Support for single parameter, formats only overload to the calendar function
        if (arguments.length === 1) {
            if (!arguments[0]) {
                time = undefined;
                formats = undefined;
            } else if (isMomentInput(arguments[0])) {
                time = arguments[0];
                formats = undefined;
            } else if (isCalendarSpec(arguments[0])) {
                formats = arguments[0];
                time = undefined;
            }
        }
        // We want to compare the start of today, vs this.
        // Getting start-of-today depends on whether we're local/utc/offset or not.
        var now = time || createLocal(),
            sod = cloneWithOffset(now, this).startOf('day'),
            format = hooks.calendarFormat(this, sod) || 'sameElse',
            output =
                formats &&
                (isFunction(formats[format])
                    ? formats[format].call(this, now)
                    : formats[format]);

        return this.format(
            output || this.localeData().calendar(format, this, createLocal(now))
        );
    }

    function clone() {
        return new Moment(this);
    }

    function isAfter(input, units) {
        var localInput = isMoment(input) ? input : createLocal(input);
        if (!(this.isValid() && localInput.isValid())) {
            return false;
        }
        units = normalizeUnits(units) || 'millisecond';
        if (units === 'millisecond') {
            return this.valueOf() > localInput.valueOf();
        } else {
            return localInput.valueOf() < this.clone().startOf(units).valueOf();
        }
    }

    function isBefore(input, units) {
        var localInput = isMoment(input) ? input : createLocal(input);
        if (!(this.isValid() && localInput.isValid())) {
            return false;
        }
        units = normalizeUnits(units) || 'millisecond';
        if (units === 'millisecond') {
            return this.valueOf() < localInput.valueOf();
        } else {
            return this.clone().endOf(units).valueOf() < localInput.valueOf();
        }
    }

    function isBetween(from, to, units, inclusivity) {
        var localFrom = isMoment(from) ? from : createLocal(from),
            localTo = isMoment(to) ? to : createLocal(to);
        if (!(this.isValid() && localFrom.isValid() && localTo.isValid())) {
            return false;
        }
        inclusivity = inclusivity || '()';
        return (
            (inclusivity[0] === '('
                ? this.isAfter(localFrom, units)
                : !this.isBefore(localFrom, units)) &&
            (inclusivity[1] === ')'
                ? this.isBefore(localTo, units)
                : !this.isAfter(localTo, units))
        );
    }

    function isSame(input, units) {
        var localInput = isMoment(input) ? input : createLocal(input),
            inputMs;
        if (!(this.isValid() && localInput.isValid())) {
            return false;
        }
        units = normalizeUnits(units) || 'millisecond';
        if (units === 'millisecond') {
            return this.valueOf() === localInput.valueOf();
        } else {
            inputMs = localInput.valueOf();
            return (
                this.clone().startOf(units).valueOf() <= inputMs &&
                inputMs <= this.clone().endOf(units).valueOf()
            );
        }
    }

    function isSameOrAfter(input, units) {
        return this.isSame(input, units) || this.isAfter(input, units);
    }

    function isSameOrBefore(input, units) {
        return this.isSame(input, units) || this.isBefore(input, units);
    }

    function diff(input, units, asFloat) {
        var that, zoneDelta, output;

        if (!this.isValid()) {
            return NaN;
        }

        that = cloneWithOffset(input, this);

        if (!that.isValid()) {
            return NaN;
        }

        zoneDelta = (that.utcOffset() - this.utcOffset()) * 6e4;

        units = normalizeUnits(units);

        switch (units) {
            case 'year':
                output = monthDiff(this, that) / 12;
                break;
            case 'month':
                output = monthDiff(this, that);
                break;
            case 'quarter':
                output = monthDiff(this, that) / 3;
                break;
            case 'second':
                output = (this - that) / 1e3;
                break; // 1000
            case 'minute':
                output = (this - that) / 6e4;
                break; // 1000 * 60
            case 'hour':
                output = (this - that) / 36e5;
                break; // 1000 * 60 * 60
            case 'day':
                output = (this - that - zoneDelta) / 864e5;
                break; // 1000 * 60 * 60 * 24, negate dst
            case 'week':
                output = (this - that - zoneDelta) / 6048e5;
                break; // 1000 * 60 * 60 * 24 * 7, negate dst
            default:
                output = this - that;
        }

        return asFloat ? output : absFloor(output);
    }

    function monthDiff(a, b) {
        if (a.date() < b.date()) {
            // end-of-month calculations work correct when the start month has more
            // days than the end month.
            return -monthDiff(b, a);
        }
        // difference in months
        var wholeMonthDiff = (b.year() - a.year()) * 12 + (b.month() - a.month()),
            // b is in (anchor - 1 month, anchor + 1 month)
            anchor = a.clone().add(wholeMonthDiff, 'months'),
            anchor2,
            adjust;

        if (b - anchor < 0) {
            anchor2 = a.clone().add(wholeMonthDiff - 1, 'months');
            // linear across the month
            adjust = (b - anchor) / (anchor - anchor2);
        } else {
            anchor2 = a.clone().add(wholeMonthDiff + 1, 'months');
            // linear across the month
            adjust = (b - anchor) / (anchor2 - anchor);
        }

        //check for negative zero, return zero if negative zero
        return -(wholeMonthDiff + adjust) || 0;
    }

    hooks.defaultFormat = 'YYYY-MM-DDTHH:mm:ssZ';
    hooks.defaultFormatUtc = 'YYYY-MM-DDTHH:mm:ss[Z]';

    function toString() {
        return this.clone().locale('en').format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ');
    }

    function toISOString(keepOffset) {
        if (!this.isValid()) {
            return null;
        }
        var utc = keepOffset !== true,
            m = utc ? this.clone().utc() : this;
        if (m.year() < 0 || m.year() > 9999) {
            return formatMoment(
                m,
                utc
                    ? 'YYYYYY-MM-DD[T]HH:mm:ss.SSS[Z]'
                    : 'YYYYYY-MM-DD[T]HH:mm:ss.SSSZ'
            );
        }
        if (isFunction(Date.prototype.toISOString)) {
            // native implementation is ~50x faster, use it when we can
            if (utc) {
                return this.toDate().toISOString();
            } else {
                return new Date(this.valueOf() + this.utcOffset() * 60 * 1000)
                    .toISOString()
                    .replace('Z', formatMoment(m, 'Z'));
            }
        }
        return formatMoment(
            m,
            utc ? 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]' : 'YYYY-MM-DD[T]HH:mm:ss.SSSZ'
        );
    }

    /**
     * Return a human readable representation of a moment that can
     * also be evaluated to get a new moment which is the same
     *
     * @link https://nodejs.org/dist/latest/docs/api/util.html#util_custom_inspect_function_on_objects
     */
    function inspect() {
        if (!this.isValid()) {
            return 'moment.invalid(/* ' + this._i + ' */)';
        }
        var func = 'moment',
            zone = '',
            prefix,
            year,
            datetime,
            suffix;
        if (!this.isLocal()) {
            func = this.utcOffset() === 0 ? 'moment.utc' : 'moment.parseZone';
            zone = 'Z';
        }
        prefix = '[' + func + '("]';
        year = 0 <= this.year() && this.year() <= 9999 ? 'YYYY' : 'YYYYYY';
        datetime = '-MM-DD[T]HH:mm:ss.SSS';
        suffix = zone + '[")]';

        return this.format(prefix + year + datetime + suffix);
    }

    function format(inputString) {
        if (!inputString) {
            inputString = this.isUtc()
                ? hooks.defaultFormatUtc
                : hooks.defaultFormat;
        }
        var output = formatMoment(this, inputString);
        return this.localeData().postformat(output);
    }

    function from(time, withoutSuffix) {
        if (
            this.isValid() &&
            ((isMoment(time) && time.isValid()) || createLocal(time).isValid())
        ) {
            return createDuration({ to: this, from: time })
                .locale(this.locale())
                .humanize(!withoutSuffix);
        } else {
            return this.localeData().invalidDate();
        }
    }

    function fromNow(withoutSuffix) {
        return this.from(createLocal(), withoutSuffix);
    }

    function to(time, withoutSuffix) {
        if (
            this.isValid() &&
            ((isMoment(time) && time.isValid()) || createLocal(time).isValid())
        ) {
            return createDuration({ from: this, to: time })
                .locale(this.locale())
                .humanize(!withoutSuffix);
        } else {
            return this.localeData().invalidDate();
        }
    }

    function toNow(withoutSuffix) {
        return this.to(createLocal(), withoutSuffix);
    }

    // If passed a locale key, it will set the locale for this
    // instance.  Otherwise, it will return the locale configuration
    // variables for this instance.
    function locale(key) {
        var newLocaleData;

        if (key === undefined) {
            return this._locale._abbr;
        } else {
            newLocaleData = getLocale(key);
            if (newLocaleData != null) {
                this._locale = newLocaleData;
            }
            return this;
        }
    }

    var lang = deprecate(
        'moment().lang() is deprecated. Instead, use moment().localeData() to get the language configuration. Use moment().locale() to change languages.',
        function (key) {
            if (key === undefined) {
                return this.localeData();
            } else {
                return this.locale(key);
            }
        }
    );

    function localeData() {
        return this._locale;
    }

    var MS_PER_SECOND = 1000,
        MS_PER_MINUTE = 60 * MS_PER_SECOND,
        MS_PER_HOUR = 60 * MS_PER_MINUTE,
        MS_PER_400_YEARS = (365 * 400 + 97) * 24 * MS_PER_HOUR;

    // actual modulo - handles negative numbers (for dates before 1970):
    function mod$1(dividend, divisor) {
        return ((dividend % divisor) + divisor) % divisor;
    }

    function localStartOfDate(y, m, d) {
        // the date constructor remaps years 0-99 to 1900-1999
        if (y < 100 && y >= 0) {
            // preserve leap years using a full 400 year cycle, then reset
            return new Date(y + 400, m, d) - MS_PER_400_YEARS;
        } else {
            return new Date(y, m, d).valueOf();
        }
    }

    function utcStartOfDate(y, m, d) {
        // Date.UTC remaps years 0-99 to 1900-1999
        if (y < 100 && y >= 0) {
            // preserve leap years using a full 400 year cycle, then reset
            return Date.UTC(y + 400, m, d) - MS_PER_400_YEARS;
        } else {
            return Date.UTC(y, m, d);
        }
    }

    function startOf(units) {
        var time, startOfDate;
        units = normalizeUnits(units);
        if (units === undefined || units === 'millisecond' || !this.isValid()) {
            return this;
        }

        startOfDate = this._isUTC ? utcStartOfDate : localStartOfDate;

        switch (units) {
            case 'year':
                time = startOfDate(this.year(), 0, 1);
                break;
            case 'quarter':
                time = startOfDate(
                    this.year(),
                    this.month() - (this.month() % 3),
                    1
                );
                break;
            case 'month':
                time = startOfDate(this.year(), this.month(), 1);
                break;
            case 'week':
                time = startOfDate(
                    this.year(),
                    this.month(),
                    this.date() - this.weekday()
                );
                break;
            case 'isoWeek':
                time = startOfDate(
                    this.year(),
                    this.month(),
                    this.date() - (this.isoWeekday() - 1)
                );
                break;
            case 'day':
            case 'date':
                time = startOfDate(this.year(), this.month(), this.date());
                break;
            case 'hour':
                time = this._d.valueOf();
                time -= mod$1(
                    time + (this._isUTC ? 0 : this.utcOffset() * MS_PER_MINUTE),
                    MS_PER_HOUR
                );
                break;
            case 'minute':
                time = this._d.valueOf();
                time -= mod$1(time, MS_PER_MINUTE);
                break;
            case 'second':
                time = this._d.valueOf();
                time -= mod$1(time, MS_PER_SECOND);
                break;
        }

        this._d.setTime(time);
        hooks.updateOffset(this, true);
        return this;
    }

    function endOf(units) {
        var time, startOfDate;
        units = normalizeUnits(units);
        if (units === undefined || units === 'millisecond' || !this.isValid()) {
            return this;
        }

        startOfDate = this._isUTC ? utcStartOfDate : localStartOfDate;

        switch (units) {
            case 'year':
                time = startOfDate(this.year() + 1, 0, 1) - 1;
                break;
            case 'quarter':
                time =
                    startOfDate(
                        this.year(),
                        this.month() - (this.month() % 3) + 3,
                        1
                    ) - 1;
                break;
            case 'month':
                time = startOfDate(this.year(), this.month() + 1, 1) - 1;
                break;
            case 'week':
                time =
                    startOfDate(
                        this.year(),
                        this.month(),
                        this.date() - this.weekday() + 7
                    ) - 1;
                break;
            case 'isoWeek':
                time =
                    startOfDate(
                        this.year(),
                        this.month(),
                        this.date() - (this.isoWeekday() - 1) + 7
                    ) - 1;
                break;
            case 'day':
            case 'date':
                time = startOfDate(this.year(), this.month(), this.date() + 1) - 1;
                break;
            case 'hour':
                time = this._d.valueOf();
                time +=
                    MS_PER_HOUR -
                    mod$1(
                        time + (this._isUTC ? 0 : this.utcOffset() * MS_PER_MINUTE),
                        MS_PER_HOUR
                    ) -
                    1;
                break;
            case 'minute':
                time = this._d.valueOf();
                time += MS_PER_MINUTE - mod$1(time, MS_PER_MINUTE) - 1;
                break;
            case 'second':
                time = this._d.valueOf();
                time += MS_PER_SECOND - mod$1(time, MS_PER_SECOND) - 1;
                break;
        }

        this._d.setTime(time);
        hooks.updateOffset(this, true);
        return this;
    }

    function valueOf() {
        return this._d.valueOf() - (this._offset || 0) * 60000;
    }

    function unix() {
        return Math.floor(this.valueOf() / 1000);
    }

    function toDate() {
        return new Date(this.valueOf());
    }

    function toArray() {
        var m = this;
        return [
            m.year(),
            m.month(),
            m.date(),
            m.hour(),
            m.minute(),
            m.second(),
            m.millisecond(),
        ];
    }

    function toObject() {
        var m = this;
        return {
            years: m.year(),
            months: m.month(),
            date: m.date(),
            hours: m.hours(),
            minutes: m.minutes(),
            seconds: m.seconds(),
            milliseconds: m.milliseconds(),
        };
    }

    function toJSON() {
        // new Date(NaN).toJSON() === null
        return this.isValid() ? this.toISOString() : null;
    }

    function isValid$2() {
        return isValid(this);
    }

    function parsingFlags() {
        return extend({}, getParsingFlags(this));
    }

    function invalidAt() {
        return getParsingFlags(this).overflow;
    }

    function creationData() {
        return {
            input: this._i,
            format: this._f,
            locale: this._locale,
            isUTC: this._isUTC,
            strict: this._strict,
        };
    }

    addFormatToken('N', 0, 0, 'eraAbbr');
    addFormatToken('NN', 0, 0, 'eraAbbr');
    addFormatToken('NNN', 0, 0, 'eraAbbr');
    addFormatToken('NNNN', 0, 0, 'eraName');
    addFormatToken('NNNNN', 0, 0, 'eraNarrow');

    addFormatToken('y', ['y', 1], 'yo', 'eraYear');
    addFormatToken('y', ['yy', 2], 0, 'eraYear');
    addFormatToken('y', ['yyy', 3], 0, 'eraYear');
    addFormatToken('y', ['yyyy', 4], 0, 'eraYear');

    addRegexToken('N', matchEraAbbr);
    addRegexToken('NN', matchEraAbbr);
    addRegexToken('NNN', matchEraAbbr);
    addRegexToken('NNNN', matchEraName);
    addRegexToken('NNNNN', matchEraNarrow);

    addParseToken(
        ['N', 'NN', 'NNN', 'NNNN', 'NNNNN'],
        function (input, array, config, token) {
            var era = config._locale.erasParse(input, token, config._strict);
            if (era) {
                getParsingFlags(config).era = era;
            } else {
                getParsingFlags(config).invalidEra = input;
            }
        }
    );

    addRegexToken('y', matchUnsigned);
    addRegexToken('yy', matchUnsigned);
    addRegexToken('yyy', matchUnsigned);
    addRegexToken('yyyy', matchUnsigned);
    addRegexToken('yo', matchEraYearOrdinal);

    addParseToken(['y', 'yy', 'yyy', 'yyyy'], YEAR);
    addParseToken(['yo'], function (input, array, config, token) {
        var match;
        if (config._locale._eraYearOrdinalRegex) {
            match = input.match(config._locale._eraYearOrdinalRegex);
        }

        if (config._locale.eraYearOrdinalParse) {
            array[YEAR] = config._locale.eraYearOrdinalParse(input, match);
        } else {
            array[YEAR] = parseInt(input, 10);
        }
    });

    function localeEras(m, format) {
        var i,
            l,
            date,
            eras = this._eras || getLocale('en')._eras;
        for (i = 0, l = eras.length; i < l; ++i) {
            switch (typeof eras[i].since) {
                case 'string':
                    // truncate time
                    date = hooks(eras[i].since).startOf('day');
                    eras[i].since = date.valueOf();
                    break;
            }

            switch (typeof eras[i].until) {
                case 'undefined':
                    eras[i].until = +Infinity;
                    break;
                case 'string':
                    // truncate time
                    date = hooks(eras[i].until).startOf('day').valueOf();
                    eras[i].until = date.valueOf();
                    break;
            }
        }
        return eras;
    }

    function localeErasParse(eraName, format, strict) {
        var i,
            l,
            eras = this.eras(),
            name,
            abbr,
            narrow;
        eraName = eraName.toUpperCase();

        for (i = 0, l = eras.length; i < l; ++i) {
            name = eras[i].name.toUpperCase();
            abbr = eras[i].abbr.toUpperCase();
            narrow = eras[i].narrow.toUpperCase();

            if (strict) {
                switch (format) {
                    case 'N':
                    case 'NN':
                    case 'NNN':
                        if (abbr === eraName) {
                            return eras[i];
                        }
                        break;

                    case 'NNNN':
                        if (name === eraName) {
                            return eras[i];
                        }
                        break;

                    case 'NNNNN':
                        if (narrow === eraName) {
                            return eras[i];
                        }
                        break;
                }
            } else if ([name, abbr, narrow].indexOf(eraName) >= 0) {
                return eras[i];
            }
        }
    }

    function localeErasConvertYear(era, year) {
        var dir = era.since <= era.until ? +1 : -1;
        if (year === undefined) {
            return hooks(era.since).year();
        } else {
            return hooks(era.since).year() + (year - era.offset) * dir;
        }
    }

    function getEraName() {
        var i,
            l,
            val,
            eras = this.localeData().eras();
        for (i = 0, l = eras.length; i < l; ++i) {
            // truncate time
            val = this.clone().startOf('day').valueOf();

            if (eras[i].since <= val && val <= eras[i].until) {
                return eras[i].name;
            }
            if (eras[i].until <= val && val <= eras[i].since) {
                return eras[i].name;
            }
        }

        return '';
    }

    function getEraNarrow() {
        var i,
            l,
            val,
            eras = this.localeData().eras();
        for (i = 0, l = eras.length; i < l; ++i) {
            // truncate time
            val = this.clone().startOf('day').valueOf();

            if (eras[i].since <= val && val <= eras[i].until) {
                return eras[i].narrow;
            }
            if (eras[i].until <= val && val <= eras[i].since) {
                return eras[i].narrow;
            }
        }

        return '';
    }

    function getEraAbbr() {
        var i,
            l,
            val,
            eras = this.localeData().eras();
        for (i = 0, l = eras.length; i < l; ++i) {
            // truncate time
            val = this.clone().startOf('day').valueOf();

            if (eras[i].since <= val && val <= eras[i].until) {
                return eras[i].abbr;
            }
            if (eras[i].until <= val && val <= eras[i].since) {
                return eras[i].abbr;
            }
        }

        return '';
    }

    function getEraYear() {
        var i,
            l,
            dir,
            val,
            eras = this.localeData().eras();
        for (i = 0, l = eras.length; i < l; ++i) {
            dir = eras[i].since <= eras[i].until ? +1 : -1;

            // truncate time
            val = this.clone().startOf('day').valueOf();

            if (
                (eras[i].since <= val && val <= eras[i].until) ||
                (eras[i].until <= val && val <= eras[i].since)
            ) {
                return (
                    (this.year() - hooks(eras[i].since).year()) * dir +
                    eras[i].offset
                );
            }
        }

        return this.year();
    }

    function erasNameRegex(isStrict) {
        if (!hasOwnProp(this, '_erasNameRegex')) {
            computeErasParse.call(this);
        }
        return isStrict ? this._erasNameRegex : this._erasRegex;
    }

    function erasAbbrRegex(isStrict) {
        if (!hasOwnProp(this, '_erasAbbrRegex')) {
            computeErasParse.call(this);
        }
        return isStrict ? this._erasAbbrRegex : this._erasRegex;
    }

    function erasNarrowRegex(isStrict) {
        if (!hasOwnProp(this, '_erasNarrowRegex')) {
            computeErasParse.call(this);
        }
        return isStrict ? this._erasNarrowRegex : this._erasRegex;
    }

    function matchEraAbbr(isStrict, locale) {
        return locale.erasAbbrRegex(isStrict);
    }

    function matchEraName(isStrict, locale) {
        return locale.erasNameRegex(isStrict);
    }

    function matchEraNarrow(isStrict, locale) {
        return locale.erasNarrowRegex(isStrict);
    }

    function matchEraYearOrdinal(isStrict, locale) {
        return locale._eraYearOrdinalRegex || matchUnsigned;
    }

    function computeErasParse() {
        var abbrPieces = [],
            namePieces = [],
            narrowPieces = [],
            mixedPieces = [],
            i,
            l,
            erasName,
            erasAbbr,
            erasNarrow,
            eras = this.eras();

        for (i = 0, l = eras.length; i < l; ++i) {
            erasName = regexEscape(eras[i].name);
            erasAbbr = regexEscape(eras[i].abbr);
            erasNarrow = regexEscape(eras[i].narrow);

            namePieces.push(erasName);
            abbrPieces.push(erasAbbr);
            narrowPieces.push(erasNarrow);
            mixedPieces.push(erasName);
            mixedPieces.push(erasAbbr);
            mixedPieces.push(erasNarrow);
        }

        this._erasRegex = new RegExp('^(' + mixedPieces.join('|') + ')', 'i');
        this._erasNameRegex = new RegExp('^(' + namePieces.join('|') + ')', 'i');
        this._erasAbbrRegex = new RegExp('^(' + abbrPieces.join('|') + ')', 'i');
        this._erasNarrowRegex = new RegExp(
            '^(' + narrowPieces.join('|') + ')',
            'i'
        );
    }

    // FORMATTING

    addFormatToken(0, ['gg', 2], 0, function () {
        return this.weekYear() % 100;
    });

    addFormatToken(0, ['GG', 2], 0, function () {
        return this.isoWeekYear() % 100;
    });

    function addWeekYearFormatToken(token, getter) {
        addFormatToken(0, [token, token.length], 0, getter);
    }

    addWeekYearFormatToken('gggg', 'weekYear');
    addWeekYearFormatToken('ggggg', 'weekYear');
    addWeekYearFormatToken('GGGG', 'isoWeekYear');
    addWeekYearFormatToken('GGGGG', 'isoWeekYear');

    // ALIASES

    // PARSING

    addRegexToken('G', matchSigned);
    addRegexToken('g', matchSigned);
    addRegexToken('GG', match1to2, match2);
    addRegexToken('gg', match1to2, match2);
    addRegexToken('GGGG', match1to4, match4);
    addRegexToken('gggg', match1to4, match4);
    addRegexToken('GGGGG', match1to6, match6);
    addRegexToken('ggggg', match1to6, match6);

    addWeekParseToken(
        ['gggg', 'ggggg', 'GGGG', 'GGGGG'],
        function (input, week, config, token) {
            week[token.substr(0, 2)] = toInt(input);
        }
    );

    addWeekParseToken(['gg', 'GG'], function (input, week, config, token) {
        week[token] = hooks.parseTwoDigitYear(input);
    });

    // MOMENTS

    function getSetWeekYear(input) {
        return getSetWeekYearHelper.call(
            this,
            input,
            this.week(),
            this.weekday() + this.localeData()._week.dow,
            this.localeData()._week.dow,
            this.localeData()._week.doy
        );
    }

    function getSetISOWeekYear(input) {
        return getSetWeekYearHelper.call(
            this,
            input,
            this.isoWeek(),
            this.isoWeekday(),
            1,
            4
        );
    }

    function getISOWeeksInYear() {
        return weeksInYear(this.year(), 1, 4);
    }

    function getISOWeeksInISOWeekYear() {
        return weeksInYear(this.isoWeekYear(), 1, 4);
    }

    function getWeeksInYear() {
        var weekInfo = this.localeData()._week;
        return weeksInYear(this.year(), weekInfo.dow, weekInfo.doy);
    }

    function getWeeksInWeekYear() {
        var weekInfo = this.localeData()._week;
        return weeksInYear(this.weekYear(), weekInfo.dow, weekInfo.doy);
    }

    function getSetWeekYearHelper(input, week, weekday, dow, doy) {
        var weeksTarget;
        if (input == null) {
            return weekOfYear(this, dow, doy).year;
        } else {
            weeksTarget = weeksInYear(input, dow, doy);
            if (week > weeksTarget) {
                week = weeksTarget;
            }
            return setWeekAll.call(this, input, week, weekday, dow, doy);
        }
    }

    function setWeekAll(weekYear, week, weekday, dow, doy) {
        var dayOfYearData = dayOfYearFromWeeks(weekYear, week, weekday, dow, doy),
            date = createUTCDate(dayOfYearData.year, 0, dayOfYearData.dayOfYear);

        this.year(date.getUTCFullYear());
        this.month(date.getUTCMonth());
        this.date(date.getUTCDate());
        return this;
    }

    // FORMATTING

    addFormatToken('Q', 0, 'Qo', 'quarter');

    // PARSING

    addRegexToken('Q', match1);
    addParseToken('Q', function (input, array) {
        array[MONTH] = (toInt(input) - 1) * 3;
    });

    // MOMENTS

    function getSetQuarter(input) {
        return input == null
            ? Math.ceil((this.month() + 1) / 3)
            : this.month((input - 1) * 3 + (this.month() % 3));
    }

    // FORMATTING

    addFormatToken('D', ['DD', 2], 'Do', 'date');

    // PARSING

    addRegexToken('D', match1to2, match1to2NoLeadingZero);
    addRegexToken('DD', match1to2, match2);
    addRegexToken('Do', function (isStrict, locale) {
        // TODO: Remove "ordinalParse" fallback in next major release.
        return isStrict
            ? locale._dayOfMonthOrdinalParse || locale._ordinalParse
            : locale._dayOfMonthOrdinalParseLenient;
    });

    addParseToken(['D', 'DD'], DATE);
    addParseToken('Do', function (input, array) {
        array[DATE] = toInt(input.match(match1to2)[0]);
    });

    // MOMENTS

    var getSetDayOfMonth = makeGetSet('Date', true);

    // FORMATTING

    addFormatToken('DDD', ['DDDD', 3], 'DDDo', 'dayOfYear');

    // PARSING

    addRegexToken('DDD', match1to3);
    addRegexToken('DDDD', match3);
    addParseToken(['DDD', 'DDDD'], function (input, array, config) {
        config._dayOfYear = toInt(input);
    });

    // HELPERS

    // MOMENTS

    function getSetDayOfYear(input) {
        var dayOfYear =
            Math.round(
                (this.clone().startOf('day') - this.clone().startOf('year')) / 864e5
            ) + 1;
        return input == null ? dayOfYear : this.add(input - dayOfYear, 'd');
    }

    // FORMATTING

    addFormatToken('m', ['mm', 2], 0, 'minute');

    // PARSING

    addRegexToken('m', match1to2, match1to2HasZero);
    addRegexToken('mm', match1to2, match2);
    addParseToken(['m', 'mm'], MINUTE);

    // MOMENTS

    var getSetMinute = makeGetSet('Minutes', false);

    // FORMATTING

    addFormatToken('s', ['ss', 2], 0, 'second');

    // PARSING

    addRegexToken('s', match1to2, match1to2HasZero);
    addRegexToken('ss', match1to2, match2);
    addParseToken(['s', 'ss'], SECOND);

    // MOMENTS

    var getSetSecond = makeGetSet('Seconds', false);

    // FORMATTING

    addFormatToken('S', 0, 0, function () {
        return ~~(this.millisecond() / 100);
    });

    addFormatToken(0, ['SS', 2], 0, function () {
        return ~~(this.millisecond() / 10);
    });

    addFormatToken(0, ['SSS', 3], 0, 'millisecond');
    addFormatToken(0, ['SSSS', 4], 0, function () {
        return this.millisecond() * 10;
    });
    addFormatToken(0, ['SSSSS', 5], 0, function () {
        return this.millisecond() * 100;
    });
    addFormatToken(0, ['SSSSSS', 6], 0, function () {
        return this.millisecond() * 1000;
    });
    addFormatToken(0, ['SSSSSSS', 7], 0, function () {
        return this.millisecond() * 10000;
    });
    addFormatToken(0, ['SSSSSSSS', 8], 0, function () {
        return this.millisecond() * 100000;
    });
    addFormatToken(0, ['SSSSSSSSS', 9], 0, function () {
        return this.millisecond() * 1000000;
    });

    // PARSING

    addRegexToken('S', match1to3, match1);
    addRegexToken('SS', match1to3, match2);
    addRegexToken('SSS', match1to3, match3);

    var token, getSetMillisecond;
    for (token = 'SSSS'; token.length <= 9; token += 'S') {
        addRegexToken(token, matchUnsigned);
    }

    function parseMs(input, array) {
        array[MILLISECOND] = toInt(('0.' + input) * 1000);
    }

    for (token = 'S'; token.length <= 9; token += 'S') {
        addParseToken(token, parseMs);
    }

    getSetMillisecond = makeGetSet('Milliseconds', false);

    // FORMATTING

    addFormatToken('z', 0, 0, 'zoneAbbr');
    addFormatToken('zz', 0, 0, 'zoneName');

    // MOMENTS

    function getZoneAbbr() {
        return this._isUTC ? 'UTC' : '';
    }

    function getZoneName() {
        return this._isUTC ? 'Coordinated Universal Time' : '';
    }

    var proto = Moment.prototype;

    proto.add = add;
    proto.calendar = calendar$1;
    proto.clone = clone;
    proto.diff = diff;
    proto.endOf = endOf;
    proto.format = format;
    proto.from = from;
    proto.fromNow = fromNow;
    proto.to = to;
    proto.toNow = toNow;
    proto.get = stringGet;
    proto.invalidAt = invalidAt;
    proto.isAfter = isAfter;
    proto.isBefore = isBefore;
    proto.isBetween = isBetween;
    proto.isSame = isSame;
    proto.isSameOrAfter = isSameOrAfter;
    proto.isSameOrBefore = isSameOrBefore;
    proto.isValid = isValid$2;
    proto.lang = lang;
    proto.locale = locale;
    proto.localeData = localeData;
    proto.max = prototypeMax;
    proto.min = prototypeMin;
    proto.parsingFlags = parsingFlags;
    proto.set = stringSet;
    proto.startOf = startOf;
    proto.subtract = subtract;
    proto.toArray = toArray;
    proto.toObject = toObject;
    proto.toDate = toDate;
    proto.toISOString = toISOString;
    proto.inspect = inspect;
    if (typeof Symbol !== 'undefined' && Symbol.for != null) {
        proto[Symbol.for('nodejs.util.inspect.custom')] = function () {
            return 'Moment<' + this.format() + '>';
        };
    }
    proto.toJSON = toJSON;
    proto.toString = toString;
    proto.unix = unix;
    proto.valueOf = valueOf;
    proto.creationData = creationData;
    proto.eraName = getEraName;
    proto.eraNarrow = getEraNarrow;
    proto.eraAbbr = getEraAbbr;
    proto.eraYear = getEraYear;
    proto.year = getSetYear;
    proto.isLeapYear = getIsLeapYear;
    proto.weekYear = getSetWeekYear;
    proto.isoWeekYear = getSetISOWeekYear;
    proto.quarter = proto.quarters = getSetQuarter;
    proto.month = getSetMonth;
    proto.daysInMonth = getDaysInMonth;
    proto.week = proto.weeks = getSetWeek;
    proto.isoWeek = proto.isoWeeks = getSetISOWeek;
    proto.weeksInYear = getWeeksInYear;
    proto.weeksInWeekYear = getWeeksInWeekYear;
    proto.isoWeeksInYear = getISOWeeksInYear;
    proto.isoWeeksInISOWeekYear = getISOWeeksInISOWeekYear;
    proto.date = getSetDayOfMonth;
    proto.day = proto.days = getSetDayOfWeek;
    proto.weekday = getSetLocaleDayOfWeek;
    proto.isoWeekday = getSetISODayOfWeek;
    proto.dayOfYear = getSetDayOfYear;
    proto.hour = proto.hours = getSetHour;
    proto.minute = proto.minutes = getSetMinute;
    proto.second = proto.seconds = getSetSecond;
    proto.millisecond = proto.milliseconds = getSetMillisecond;
    proto.utcOffset = getSetOffset;
    proto.utc = setOffsetToUTC;
    proto.local = setOffsetToLocal;
    proto.parseZone = setOffsetToParsedOffset;
    proto.hasAlignedHourOffset = hasAlignedHourOffset;
    proto.isDST = isDaylightSavingTime;
    proto.isLocal = isLocal;
    proto.isUtcOffset = isUtcOffset;
    proto.isUtc = isUtc;
    proto.isUTC = isUtc;
    proto.zoneAbbr = getZoneAbbr;
    proto.zoneName = getZoneName;
    proto.dates = deprecate(
        'dates accessor is deprecated. Use date instead.',
        getSetDayOfMonth
    );
    proto.months = deprecate(
        'months accessor is deprecated. Use month instead',
        getSetMonth
    );
    proto.years = deprecate(
        'years accessor is deprecated. Use year instead',
        getSetYear
    );
    proto.zone = deprecate(
        'moment().zone is deprecated, use moment().utcOffset instead. http://momentjs.com/guides/#/warnings/zone/',
        getSetZone
    );
    proto.isDSTShifted = deprecate(
        'isDSTShifted is deprecated. See http://momentjs.com/guides/#/warnings/dst-shifted/ for more information',
        isDaylightSavingTimeShifted
    );

    function createUnix(input) {
        return createLocal(input * 1000);
    }

    function createInZone() {
        return createLocal.apply(null, arguments).parseZone();
    }

    function preParsePostFormat(string) {
        return string;
    }

    var proto$1 = Locale.prototype;

    proto$1.calendar = calendar;
    proto$1.longDateFormat = longDateFormat;
    proto$1.invalidDate = invalidDate;
    proto$1.ordinal = ordinal;
    proto$1.preparse = preParsePostFormat;
    proto$1.postformat = preParsePostFormat;
    proto$1.relativeTime = relativeTime;
    proto$1.pastFuture = pastFuture;
    proto$1.set = set;
    proto$1.eras = localeEras;
    proto$1.erasParse = localeErasParse;
    proto$1.erasConvertYear = localeErasConvertYear;
    proto$1.erasAbbrRegex = erasAbbrRegex;
    proto$1.erasNameRegex = erasNameRegex;
    proto$1.erasNarrowRegex = erasNarrowRegex;

    proto$1.months = localeMonths;
    proto$1.monthsShort = localeMonthsShort;
    proto$1.monthsParse = localeMonthsParse;
    proto$1.monthsRegex = monthsRegex;
    proto$1.monthsShortRegex = monthsShortRegex;
    proto$1.week = localeWeek;
    proto$1.firstDayOfYear = localeFirstDayOfYear;
    proto$1.firstDayOfWeek = localeFirstDayOfWeek;

    proto$1.weekdays = localeWeekdays;
    proto$1.weekdaysMin = localeWeekdaysMin;
    proto$1.weekdaysShort = localeWeekdaysShort;
    proto$1.weekdaysParse = localeWeekdaysParse;

    proto$1.weekdaysRegex = weekdaysRegex;
    proto$1.weekdaysShortRegex = weekdaysShortRegex;
    proto$1.weekdaysMinRegex = weekdaysMinRegex;

    proto$1.isPM = localeIsPM;
    proto$1.meridiem = localeMeridiem;

    function get$1(format, index, field, setter) {
        var locale = getLocale(),
            utc = createUTC().set(setter, index);
        return locale[field](utc, format);
    }

    function listMonthsImpl(format, index, field) {
        if (isNumber(format)) {
            index = format;
            format = undefined;
        }

        format = format || '';

        if (index != null) {
            return get$1(format, index, field, 'month');
        }

        var i,
            out = [];
        for (i = 0; i < 12; i++) {
            out[i] = get$1(format, i, field, 'month');
        }
        return out;
    }

    // ()
    // (5)
    // (fmt, 5)
    // (fmt)
    // (true)
    // (true, 5)
    // (true, fmt, 5)
    // (true, fmt)
    function listWeekdaysImpl(localeSorted, format, index, field) {
        if (typeof localeSorted === 'boolean') {
            if (isNumber(format)) {
                index = format;
                format = undefined;
            }

            format = format || '';
        } else {
            format = localeSorted;
            index = format;
            localeSorted = false;

            if (isNumber(format)) {
                index = format;
                format = undefined;
            }

            format = format || '';
        }

        var locale = getLocale(),
            shift = localeSorted ? locale._week.dow : 0,
            i,
            out = [];

        if (index != null) {
            return get$1(format, (index + shift) % 7, field, 'day');
        }

        for (i = 0; i < 7; i++) {
            out[i] = get$1(format, (i + shift) % 7, field, 'day');
        }
        return out;
    }

    function listMonths(format, index) {
        return listMonthsImpl(format, index, 'months');
    }

    function listMonthsShort(format, index) {
        return listMonthsImpl(format, index, 'monthsShort');
    }

    function listWeekdays(localeSorted, format, index) {
        return listWeekdaysImpl(localeSorted, format, index, 'weekdays');
    }

    function listWeekdaysShort(localeSorted, format, index) {
        return listWeekdaysImpl(localeSorted, format, index, 'weekdaysShort');
    }

    function listWeekdaysMin(localeSorted, format, index) {
        return listWeekdaysImpl(localeSorted, format, index, 'weekdaysMin');
    }

    getSetGlobalLocale('en', {
        eras: [
            {
                since: '0001-01-01',
                until: +Infinity,
                offset: 1,
                name: 'Anno Domini',
                narrow: 'AD',
                abbr: 'AD',
            },
            {
                since: '0000-12-31',
                until: -Infinity,
                offset: 1,
                name: 'Before Christ',
                narrow: 'BC',
                abbr: 'BC',
            },
        ],
        dayOfMonthOrdinalParse: /\d{1,2}(th|st|nd|rd)/,
        ordinal: function (number) {
            var b = number % 10,
                output =
                    toInt((number % 100) / 10) === 1
                        ? 'th'
                        : b === 1
                          ? 'st'
                          : b === 2
                            ? 'nd'
                            : b === 3
                              ? 'rd'
                              : 'th';
            return number + output;
        },
    });

    // Side effect imports

    hooks.lang = deprecate(
        'moment.lang is deprecated. Use moment.locale instead.',
        getSetGlobalLocale
    );
    hooks.langData = deprecate(
        'moment.langData is deprecated. Use moment.localeData instead.',
        getLocale
    );

    var mathAbs = Math.abs;

    function abs() {
        var data = this._data;

        this._milliseconds = mathAbs(this._milliseconds);
        this._days = mathAbs(this._days);
        this._months = mathAbs(this._months);

        data.milliseconds = mathAbs(data.milliseconds);
        data.seconds = mathAbs(data.seconds);
        data.minutes = mathAbs(data.minutes);
        data.hours = mathAbs(data.hours);
        data.months = mathAbs(data.months);
        data.years = mathAbs(data.years);

        return this;
    }

    function addSubtract$1(duration, input, value, direction) {
        var other = createDuration(input, value);

        duration._milliseconds += direction * other._milliseconds;
        duration._days += direction * other._days;
        duration._months += direction * other._months;

        return duration._bubble();
    }

    // supports only 2.0-style add(1, 's') or add(duration)
    function add$1(input, value) {
        return addSubtract$1(this, input, value, 1);
    }

    // supports only 2.0-style subtract(1, 's') or subtract(duration)
    function subtract$1(input, value) {
        return addSubtract$1(this, input, value, -1);
    }

    function absCeil(number) {
        if (number < 0) {
            return Math.floor(number);
        } else {
            return Math.ceil(number);
        }
    }

    function bubble() {
        var milliseconds = this._milliseconds,
            days = this._days,
            months = this._months,
            data = this._data,
            seconds,
            minutes,
            hours,
            years,
            monthsFromDays;

        // if we have a mix of positive and negative values, bubble down first
        // check: https://github.com/moment/moment/issues/2166
        if (
            !(
                (milliseconds >= 0 && days >= 0 && months >= 0) ||
                (milliseconds <= 0 && days <= 0 && months <= 0)
            )
        ) {
            milliseconds += absCeil(monthsToDays(months) + days) * 864e5;
            days = 0;
            months = 0;
        }

        // The following code bubbles up values, see the tests for
        // examples of what that means.
        data.milliseconds = milliseconds % 1000;

        seconds = absFloor(milliseconds / 1000);
        data.seconds = seconds % 60;

        minutes = absFloor(seconds / 60);
        data.minutes = minutes % 60;

        hours = absFloor(minutes / 60);
        data.hours = hours % 24;

        days += absFloor(hours / 24);

        // convert days to months
        monthsFromDays = absFloor(daysToMonths(days));
        months += monthsFromDays;
        days -= absCeil(monthsToDays(monthsFromDays));

        // 12 months -> 1 year
        years = absFloor(months / 12);
        months %= 12;

        data.days = days;
        data.months = months;
        data.years = years;

        return this;
    }

    function daysToMonths(days) {
        // 400 years have 146097 days (taking into account leap year rules)
        // 400 years have 12 months === 4800
        return (days * 4800) / 146097;
    }

    function monthsToDays(months) {
        // the reverse of daysToMonths
        return (months * 146097) / 4800;
    }

    function as(units) {
        if (!this.isValid()) {
            return NaN;
        }
        var days,
            months,
            milliseconds = this._milliseconds;

        units = normalizeUnits(units);

        if (units === 'month' || units === 'quarter' || units === 'year') {
            days = this._days + milliseconds / 864e5;
            months = this._months + daysToMonths(days);
            switch (units) {
                case 'month':
                    return months;
                case 'quarter':
                    return months / 3;
                case 'year':
                    return months / 12;
            }
        } else {
            // handle milliseconds separately because of floating point math errors (issue #1867)
            days = this._days + Math.round(monthsToDays(this._months));
            switch (units) {
                case 'week':
                    return days / 7 + milliseconds / 6048e5;
                case 'day':
                    return days + milliseconds / 864e5;
                case 'hour':
                    return days * 24 + milliseconds / 36e5;
                case 'minute':
                    return days * 1440 + milliseconds / 6e4;
                case 'second':
                    return days * 86400 + milliseconds / 1000;
                // Math.floor prevents floating point math errors here
                case 'millisecond':
                    return Math.floor(days * 864e5) + milliseconds;
                default:
                    throw new Error('Unknown unit ' + units);
            }
        }
    }

    function makeAs(alias) {
        return function () {
            return this.as(alias);
        };
    }

    var asMilliseconds = makeAs('ms'),
        asSeconds = makeAs('s'),
        asMinutes = makeAs('m'),
        asHours = makeAs('h'),
        asDays = makeAs('d'),
        asWeeks = makeAs('w'),
        asMonths = makeAs('M'),
        asQuarters = makeAs('Q'),
        asYears = makeAs('y'),
        valueOf$1 = asMilliseconds;

    function clone$1() {
        return createDuration(this);
    }

    function get$2(units) {
        units = normalizeUnits(units);
        return this.isValid() ? this[units + 's']() : NaN;
    }

    function makeGetter(name) {
        return function () {
            return this.isValid() ? this._data[name] : NaN;
        };
    }

    var milliseconds = makeGetter('milliseconds'),
        seconds = makeGetter('seconds'),
        minutes = makeGetter('minutes'),
        hours = makeGetter('hours'),
        days = makeGetter('days'),
        months = makeGetter('months'),
        years = makeGetter('years');

    function weeks() {
        return absFloor(this.days() / 7);
    }

    var round = Math.round,
        thresholds = {
            ss: 44, // a few seconds to seconds
            s: 45, // seconds to minute
            m: 45, // minutes to hour
            h: 22, // hours to day
            d: 26, // days to month/week
            w: null, // weeks to month
            M: 11, // months to year
        };

    // helper function for moment.fn.from, moment.fn.fromNow, and moment.duration.fn.humanize
    function substituteTimeAgo(string, number, withoutSuffix, isFuture, locale) {
        return locale.relativeTime(number || 1, !!withoutSuffix, string, isFuture);
    }

    function relativeTime$1(posNegDuration, withoutSuffix, thresholds, locale) {
        var duration = createDuration(posNegDuration).abs(),
            seconds = round(duration.as('s')),
            minutes = round(duration.as('m')),
            hours = round(duration.as('h')),
            days = round(duration.as('d')),
            months = round(duration.as('M')),
            weeks = round(duration.as('w')),
            years = round(duration.as('y')),
            a =
                (seconds <= thresholds.ss && ['s', seconds]) ||
                (seconds < thresholds.s && ['ss', seconds]) ||
                (minutes <= 1 && ['m']) ||
                (minutes < thresholds.m && ['mm', minutes]) ||
                (hours <= 1 && ['h']) ||
                (hours < thresholds.h && ['hh', hours]) ||
                (days <= 1 && ['d']) ||
                (days < thresholds.d && ['dd', days]);

        if (thresholds.w != null) {
            a =
                a ||
                (weeks <= 1 && ['w']) ||
                (weeks < thresholds.w && ['ww', weeks]);
        }
        a = a ||
            (months <= 1 && ['M']) ||
            (months < thresholds.M && ['MM', months]) ||
            (years <= 1 && ['y']) || ['yy', years];

        a[2] = withoutSuffix;
        a[3] = +posNegDuration > 0;
        a[4] = locale;
        return substituteTimeAgo.apply(null, a);
    }

    // This function allows you to set the rounding function for relative time strings
    function getSetRelativeTimeRounding(roundingFunction) {
        if (roundingFunction === undefined) {
            return round;
        }
        if (typeof roundingFunction === 'function') {
            round = roundingFunction;
            return true;
        }
        return false;
    }

    // This function allows you to set a threshold for relative time strings
    function getSetRelativeTimeThreshold(threshold, limit) {
        if (thresholds[threshold] === undefined) {
            return false;
        }
        if (limit === undefined) {
            return thresholds[threshold];
        }
        thresholds[threshold] = limit;
        if (threshold === 's') {
            thresholds.ss = limit - 1;
        }
        return true;
    }

    function humanize(argWithSuffix, argThresholds) {
        if (!this.isValid()) {
            return this.localeData().invalidDate();
        }

        var withSuffix = false,
            th = thresholds,
            locale,
            output;

        if (typeof argWithSuffix === 'object') {
            argThresholds = argWithSuffix;
            argWithSuffix = false;
        }
        if (typeof argWithSuffix === 'boolean') {
            withSuffix = argWithSuffix;
        }
        if (typeof argThresholds === 'object') {
            th = Object.assign({}, thresholds, argThresholds);
            if (argThresholds.s != null && argThresholds.ss == null) {
                th.ss = argThresholds.s - 1;
            }
        }

        locale = this.localeData();
        output = relativeTime$1(this, !withSuffix, th, locale);

        if (withSuffix) {
            output = locale.pastFuture(+this, output);
        }

        return locale.postformat(output);
    }

    var abs$1 = Math.abs;

    function sign(x) {
        return (x > 0) - (x < 0) || +x;
    }

    function toISOString$1() {
        // for ISO strings we do not use the normal bubbling rules:
        //  * milliseconds bubble up until they become hours
        //  * days do not bubble at all
        //  * months bubble up until they become years
        // This is because there is no context-free conversion between hours and days
        // (think of clock changes)
        // and also not between days and months (28-31 days per month)
        if (!this.isValid()) {
            return this.localeData().invalidDate();
        }

        var seconds = abs$1(this._milliseconds) / 1000,
            days = abs$1(this._days),
            months = abs$1(this._months),
            minutes,
            hours,
            years,
            s,
            total = this.asSeconds(),
            totalSign,
            ymSign,
            daysSign,
            hmsSign;

        if (!total) {
            // this is the same as C#'s (Noda) and python (isodate)...
            // but not other JS (goog.date)
            return 'P0D';
        }

        // 3600 seconds -> 60 minutes -> 1 hour
        minutes = absFloor(seconds / 60);
        hours = absFloor(minutes / 60);
        seconds %= 60;
        minutes %= 60;

        // 12 months -> 1 year
        years = absFloor(months / 12);
        months %= 12;

        // inspired by https://github.com/dordille/moment-isoduration/blob/master/moment.isoduration.js
        s = seconds ? seconds.toFixed(3).replace(/\.?0+$/, '') : '';

        totalSign = total < 0 ? '-' : '';
        ymSign = sign(this._months) !== sign(total) ? '-' : '';
        daysSign = sign(this._days) !== sign(total) ? '-' : '';
        hmsSign = sign(this._milliseconds) !== sign(total) ? '-' : '';

        return (
            totalSign +
            'P' +
            (years ? ymSign + years + 'Y' : '') +
            (months ? ymSign + months + 'M' : '') +
            (days ? daysSign + days + 'D' : '') +
            (hours || minutes || seconds ? 'T' : '') +
            (hours ? hmsSign + hours + 'H' : '') +
            (minutes ? hmsSign + minutes + 'M' : '') +
            (seconds ? hmsSign + s + 'S' : '')
        );
    }

    var proto$2 = Duration.prototype;

    proto$2.isValid = isValid$1;
    proto$2.abs = abs;
    proto$2.add = add$1;
    proto$2.subtract = subtract$1;
    proto$2.as = as;
    proto$2.asMilliseconds = asMilliseconds;
    proto$2.asSeconds = asSeconds;
    proto$2.asMinutes = asMinutes;
    proto$2.asHours = asHours;
    proto$2.asDays = asDays;
    proto$2.asWeeks = asWeeks;
    proto$2.asMonths = asMonths;
    proto$2.asQuarters = asQuarters;
    proto$2.asYears = asYears;
    proto$2.valueOf = valueOf$1;
    proto$2._bubble = bubble;
    proto$2.clone = clone$1;
    proto$2.get = get$2;
    proto$2.milliseconds = milliseconds;
    proto$2.seconds = seconds;
    proto$2.minutes = minutes;
    proto$2.hours = hours;
    proto$2.days = days;
    proto$2.weeks = weeks;
    proto$2.months = months;
    proto$2.years = years;
    proto$2.humanize = humanize;
    proto$2.toISOString = toISOString$1;
    proto$2.toString = toISOString$1;
    proto$2.toJSON = toISOString$1;
    proto$2.locale = locale;
    proto$2.localeData = localeData;

    proto$2.toIsoString = deprecate(
        'toIsoString() is deprecated. Please use toISOString() instead (notice the capitals)',
        toISOString$1
    );
    proto$2.lang = lang;

    // FORMATTING

    addFormatToken('X', 0, 0, 'unix');
    addFormatToken('x', 0, 0, 'valueOf');

    // PARSING

    addRegexToken('x', matchSigned);
    addRegexToken('X', matchTimestamp);
    addParseToken('X', function (input, array, config) {
        config._d = new Date(parseFloat(input) * 1000);
    });
    addParseToken('x', function (input, array, config) {
        config._d = new Date(toInt(input));
    });

    //! moment.js

    hooks.version = '2.30.1';

    setHookCallback(createLocal);

    hooks.fn = proto;
    hooks.min = min;
    hooks.max = max;
    hooks.now = now;
    hooks.utc = createUTC;
    hooks.unix = createUnix;
    hooks.months = listMonths;
    hooks.isDate = isDate;
    hooks.locale = getSetGlobalLocale;
    hooks.invalid = createInvalid;
    hooks.duration = createDuration;
    hooks.isMoment = isMoment;
    hooks.weekdays = listWeekdays;
    hooks.parseZone = createInZone;
    hooks.localeData = getLocale;
    hooks.isDuration = isDuration;
    hooks.monthsShort = listMonthsShort;
    hooks.weekdaysMin = listWeekdaysMin;
    hooks.defineLocale = defineLocale;
    hooks.updateLocale = updateLocale;
    hooks.locales = listLocales;
    hooks.weekdaysShort = listWeekdaysShort;
    hooks.normalizeUnits = normalizeUnits;
    hooks.relativeTimeRounding = getSetRelativeTimeRounding;
    hooks.relativeTimeThreshold = getSetRelativeTimeThreshold;
    hooks.calendarFormat = getCalendarFormat;
    hooks.prototype = proto;

    // currently HTML5 input type only supports 24-hour formats
    hooks.HTML5_FMT = {
        DATETIME_LOCAL: 'YYYY-MM-DDTHH:mm', // <input type="datetime-local" />
        DATETIME_LOCAL_SECONDS: 'YYYY-MM-DDTHH:mm:ss', // <input type="datetime-local" step="1" />
        DATETIME_LOCAL_MS: 'YYYY-MM-DDTHH:mm:ss.SSS', // <input type="datetime-local" step="0.001" />
        DATE: 'YYYY-MM-DD', // <input type="date" />
        TIME: 'HH:mm', // <input type="time" />
        TIME_SECONDS: 'HH:mm:ss', // <input type="time" step="1" />
        TIME_MS: 'HH:mm:ss.SSS', // <input type="time" step="0.001" />
        WEEK: 'GGGG-[W]WW', // <input type="week" />
        MONTH: 'YYYY-MM', // <input type="month" />
    };

    return hooks;

})));

/*! hellojs v2.0.0-4 | (c) 2012-2017 Andrew Dodson | MIT https://adodson.com/hello.js/LICENSE */!function(t){if("object"==typeof exports&&"undefined"!=typeof module)module.exports=t();else if("function"==typeof define&&define.amd)define([],t);else{("undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:this).hello=t()}}(function(){return function t(e,n,r){function o(a,u){if(!n[a]){if(!e[a]){var s="function"==typeof require&&require;if(!u&&s)return s(a,!0);if(i)return i(a,!0);var c=new Error("Cannot find module '"+a+"'");throw c.code="MODULE_NOT_FOUND",c}var f=n[a]={exports:{}};e[a][0].call(f.exports,function(t){var n=e[a][1][t];return o(n||t)},f,f.exports,t,e,n,r)}return n[a].exports}for(var i="function"==typeof require&&require,a=0;a<r.length;a++)o(r[a]);return o}({1:[function(t,e,n){(function(e){"use strict";function n(t,e,n){t[e]||Object[r](t,e,{writable:!0,configurable:!0,value:n})}if(t(295),t(296),t(2),e._babelPolyfill)throw new Error("only one instance of babel-polyfill is allowed");e._babelPolyfill=!0;var r="defineProperty";n(String.prototype,"padLeft","".padStart),n(String.prototype,"padRight","".padEnd),"pop,reverse,shift,keys,values,entries,indexOf,every,some,forEach,map,filter,find,findIndex,includes,join,slice,concat,push,splice,unshift,sort,lastIndexOf,reduce,reduceRight,copyWithin,fill".split(",").forEach(function(t){[][t]&&n(Array,t,Function.call.bind([][t]))})}).call(this,"undefined"!=typeof global?global:"undefined"!=typeof self?self:"undefined"!=typeof window?window:{})},{2:2,295:295,296:296}],2:[function(t,e,n){t(119),e.exports=t(23).RegExp.escape},{119:119,23:23}],3:[function(t,e,n){e.exports=function(t){if("function"!=typeof t)throw TypeError(t+" is not a function!");return t}},{}],4:[function(t,e,n){var r=t(18);e.exports=function(t,e){if("number"!=typeof t&&"Number"!=r(t))throw TypeError(e);return+t}},{18:18}],5:[function(t,e,n){var r=t(117)("unscopables"),o=Array.prototype;void 0==o[r]&&t(40)(o,r,{}),e.exports=function(t){o[r][t]=!0}},{117:117,40:40}],6:[function(t,e,n){e.exports=function(t,e,n,r){if(!(t instanceof e)||void 0!==r&&r in t)throw TypeError(n+": incorrect invocation!");return t}},{}],7:[function(t,e,n){var r=t(49);e.exports=function(t){if(!r(t))throw TypeError(t+" is not an object!");return t}},{49:49}],8:[function(t,e,n){"use strict";var r=t(109),o=t(105),i=t(108);e.exports=[].copyWithin||function(t,e){var n=r(this),a=i(n.length),u=o(t,a),s=o(e,a),c=arguments.length>2?arguments[2]:void 0,f=Math.min((void 0===c?a:o(c,a))-s,a-u),l=1;for(s<u&&u<s+f&&(l=-1,s+=f-1,u+=f-1);f-- >0;)s in n?n[u]=n[s]:delete n[u],u+=l,s+=l;return n}},{105:105,108:108,109:109}],9:[function(t,e,n){"use strict";var r=t(109),o=t(105),i=t(108);e.exports=function(t){for(var e=r(this),n=i(e.length),a=arguments.length,u=o(a>1?arguments[1]:void 0,n),s=a>2?arguments[2]:void 0,c=void 0===s?n:o(s,n);c>u;)e[u++]=t;return e}},{105:105,108:108,109:109}],10:[function(t,e,n){var r=t(37);e.exports=function(t,e){var n=[];return r(t,!1,n.push,n,e),n}},{37:37}],11:[function(t,e,n){var r=t(107),o=t(108),i=t(105);e.exports=function(t){return function(e,n,a){var u,s=r(e),c=o(s.length),f=i(a,c);if(t&&n!=n){for(;c>f;)if((u=s[f++])!=u)return!0}else for(;c>f;f++)if((t||f in s)&&s[f]===n)return t||f||0;return!t&&-1}}},{105:105,107:107,108:108}],12:[function(t,e,n){var r=t(25),o=t(45),i=t(109),a=t(108),u=t(15);e.exports=function(t,e){var n=1==t,s=2==t,c=3==t,f=4==t,l=6==t,p=5==t||l,h=e||u;return function(e,u,d){for(var m,v,y=i(e),g=o(y),b=r(u,d,3),w=a(g.length),_=0,x=n?h(e,w):s?h(e,0):void 0;w>_;_++)if((p||_ in g)&&(m=g[_],v=b(m,_,y),t))if(n)x[_]=v;else if(v)switch(t){case 3:return!0;case 5:return m;case 6:return _;case 2:x.push(m)}else if(f)return!1;return l?-1:c||f?f:x}}},{108:108,109:109,15:15,25:25,45:45}],13:[function(t,e,n){var r=t(3),o=t(109),i=t(45),a=t(108);e.exports=function(t,e,n,u,s){r(e);var c=o(t),f=i(c),l=a(c.length),p=s?l-1:0,h=s?-1:1;if(n<2)for(;;){if(p in f){u=f[p],p+=h;break}if(p+=h,s?p<0:l<=p)throw TypeError("Reduce of empty array with no initial value")}for(;s?p>=0:l>p;p+=h)p in f&&(u=e(u,f[p],p,c));return u}},{108:108,109:109,3:3,45:45}],14:[function(t,e,n){var r=t(49),o=t(47),i=t(117)("species");e.exports=function(t){var e;return o(t)&&("function"!=typeof(e=t.constructor)||e!==Array&&!o(e.prototype)||(e=void 0),r(e)&&null===(e=e[i])&&(e=void 0)),void 0===e?Array:e}},{117:117,47:47,49:49}],15:[function(t,e,n){var r=t(14);e.exports=function(t,e){return new(r(t))(e)}},{14:14}],16:[function(t,e,n){"use strict";var r=t(3),o=t(49),i=t(44),a=[].slice,u={},s=function(t,e,n){if(!(e in u)){for(var r=[],o=0;o<e;o++)r[o]="a["+o+"]";u[e]=Function("F,a","return new F("+r.join(",")+")")}return u[e](t,n)};e.exports=Function.bind||function(t){var e=r(this),n=a.call(arguments,1),u=function(){var r=n.concat(a.call(arguments));return this instanceof u?s(e,r.length,r):i(e,r,t)};return o(e.prototype)&&(u.prototype=e.prototype),u}},{3:3,44:44,49:49}],17:[function(t,e,n){var r=t(18),o=t(117)("toStringTag"),i="Arguments"==r(function(){return arguments}()),a=function(t,e){try{return t[e]}catch(t){}};e.exports=function(t){var e,n,u;return void 0===t?"Undefined":null===t?"Null":"string"==typeof(n=a(e=Object(t),o))?n:i?r(e):"Object"==(u=r(e))&&"function"==typeof e.callee?"Arguments":u}},{117:117,18:18}],18:[function(t,e,n){var r={}.toString;e.exports=function(t){return r.call(t).slice(8,-1)}},{}],19:[function(t,e,n){"use strict";var r=t(67).f,o=t(66),i=t(86),a=t(25),u=t(6),s=t(27),c=t(37),f=t(53),l=t(55),p=t(91),h=t(28),d=t(62).fastKey,m=h?"_s":"size",v=function(t,e){var n,r=d(e);if("F"!==r)return t._i[r];for(n=t._f;n;n=n.n)if(n.k==e)return n};e.exports={getConstructor:function(t,e,n,f){var l=t(function(t,r){u(t,l,e,"_i"),t._i=o(null),t._f=void 0,t._l=void 0,t[m]=0,void 0!=r&&c(r,n,t[f],t)});return i(l.prototype,{clear:function(){for(var t=this,e=t._i,n=t._f;n;n=n.n)n.r=!0,n.p&&(n.p=n.p.n=void 0),delete e[n.i];t._f=t._l=void 0,t[m]=0},delete:function(t){var e=this,n=v(e,t);if(n){var r=n.n,o=n.p;delete e._i[n.i],n.r=!0,o&&(o.n=r),r&&(r.p=o),e._f==n&&(e._f=r),e._l==n&&(e._l=o),e[m]--}return!!n},forEach:function(t){u(this,l,"forEach");for(var e,n=a(t,arguments.length>1?arguments[1]:void 0,3);e=e?e.n:this._f;)for(n(e.v,e.k,this);e&&e.r;)e=e.p},has:function(t){return!!v(this,t)}}),h&&r(l.prototype,"size",{get:function(){return s(this[m])}}),l},def:function(t,e,n){var r,o,i=v(t,e);return i?i.v=n:(t._l=i={i:o=d(e,!0),k:e,v:n,p:r=t._l,n:void 0,r:!1},t._f||(t._f=i),r&&(r.n=i),t[m]++,"F"!==o&&(t._i[o]=i)),t},getEntry:v,setStrong:function(t,e,n){f(t,e,function(t,e){this._t=t,this._k=e,this._l=void 0},function(){for(var t=this,e=t._k,n=t._l;n&&n.r;)n=n.p;return t._t&&(t._l=n=n?n.n:t._t._f)?"keys"==e?l(0,n.k):"values"==e?l(0,n.v):l(0,[n.k,n.v]):(t._t=void 0,l(1))},n?"entries":"values",!n,!0),p(e)}}},{25:25,27:27,28:28,37:37,53:53,55:55,6:6,62:62,66:66,67:67,86:86,91:91}],20:[function(t,e,n){var r=t(17),o=t(10);e.exports=function(t){return function(){if(r(this)!=t)throw TypeError(t+"#toJSON isn't generic");return o(this)}}},{10:10,17:17}],21:[function(t,e,n){"use strict";var r=t(86),o=t(62).getWeak,i=t(7),a=t(49),u=t(6),s=t(37),c=t(12),f=t(39),l=c(5),p=c(6),h=0,d=function(t){return t._l||(t._l=new m)},m=function(){this.a=[]},v=function(t,e){return l(t.a,function(t){return t[0]===e})};m.prototype={get:function(t){var e=v(this,t);if(e)return e[1]},has:function(t){return!!v(this,t)},set:function(t,e){var n=v(this,t);n?n[1]=e:this.a.push([t,e])},delete:function(t){var e=p(this.a,function(e){return e[0]===t});return~e&&this.a.splice(e,1),!!~e}},e.exports={getConstructor:function(t,e,n,i){var c=t(function(t,r){u(t,c,e,"_i"),t._i=h++,t._l=void 0,void 0!=r&&s(r,n,t[i],t)});return r(c.prototype,{delete:function(t){if(!a(t))return!1;var e=o(t);return!0===e?d(this).delete(t):e&&f(e,this._i)&&delete e[this._i]},has:function(t){if(!a(t))return!1;var e=o(t);return!0===e?d(this).has(t):e&&f(e,this._i)}}),c},def:function(t,e,n){var r=o(i(e),!0);return!0===r?d(t).set(e,n):r[t._i]=n,t},ufstore:d}},{12:12,37:37,39:39,49:49,6:6,62:62,7:7,86:86}],22:[function(t,e,n){"use strict";var r=t(38),o=t(32),i=t(87),a=t(86),u=t(62),s=t(37),c=t(6),f=t(49),l=t(34),p=t(54),h=t(92),d=t(43);e.exports=function(t,e,n,m,v,y){var g=r[t],b=g,w=v?"set":"add",_=b&&b.prototype,x={},S=function(t){var e=_[t];i(_,t,"delete"==t?function(t){return!(y&&!f(t))&&e.call(this,0===t?0:t)}:"has"==t?function(t){return!(y&&!f(t))&&e.call(this,0===t?0:t)}:"get"==t?function(t){return y&&!f(t)?void 0:e.call(this,0===t?0:t)}:"add"==t?function(t){return e.call(this,0===t?0:t),this}:function(t,n){return e.call(this,0===t?0:t,n),this})};if("function"==typeof b&&(y||_.forEach&&!l(function(){(new b).entries().next()}))){var k=new b,E=k[w](y?{}:-0,1)!=k,O=l(function(){k.has(1)}),j=p(function(t){new b(t)}),A=!y&&l(function(){for(var t=new b,e=5;e--;)t[w](e,e);return!t.has(-0)});j||((b=e(function(e,n){c(e,b,t);var r=d(new g,e,b);return void 0!=n&&s(n,v,r[w],r),r})).prototype=_,_.constructor=b),(O||A)&&(S("delete"),S("has"),v&&S("get")),(A||E)&&S(w),y&&_.clear&&delete _.clear}else b=m.getConstructor(e,t,v,w),a(b.prototype,n),u.NEED=!0;return h(b,t),x[t]=b,o(o.G+o.W+o.F*(b!=g),x),y||m.setStrong(b,t,v),b}},{32:32,34:34,37:37,38:38,43:43,49:49,54:54,6:6,62:62,86:86,87:87,92:92}],23:[function(t,e,n){var r=e.exports={version:"2.4.0"};"number"==typeof __e&&(__e=r)},{}],24:[function(t,e,n){"use strict";var r=t(67),o=t(85);e.exports=function(t,e,n){e in t?r.f(t,e,o(0,n)):t[e]=n}},{67:67,85:85}],25:[function(t,e,n){var r=t(3);e.exports=function(t,e,n){if(r(t),void 0===e)return t;switch(n){case 1:return function(n){return t.call(e,n)};case 2:return function(n,r){return t.call(e,n,r)};case 3:return function(n,r,o){return t.call(e,n,r,o)}}return function(){return t.apply(e,arguments)}}},{3:3}],26:[function(t,e,n){"use strict";var r=t(7),o=t(110);e.exports=function(t){if("string"!==t&&"number"!==t&&"default"!==t)throw TypeError("Incorrect hint");return o(r(this),"number"!=t)}},{110:110,7:7}],27:[function(t,e,n){e.exports=function(t){if(void 0==t)throw TypeError("Can't call method on  "+t);return t}},{}],28:[function(t,e,n){e.exports=!t(34)(function(){return 7!=Object.defineProperty({},"a",{get:function(){return 7}}).a})},{34:34}],29:[function(t,e,n){var r=t(49),o=t(38).document,i=r(o)&&r(o.createElement);e.exports=function(t){return i?o.createElement(t):{}}},{38:38,49:49}],30:[function(t,e,n){e.exports="constructor,hasOwnProperty,isPrototypeOf,propertyIsEnumerable,toLocaleString,toString,valueOf".split(",")},{}],31:[function(t,e,n){var r=t(76),o=t(73),i=t(77);e.exports=function(t){var e=r(t),n=o.f;if(n)for(var a,u=n(t),s=i.f,c=0;u.length>c;)s.call(t,a=u[c++])&&e.push(a);return e}},{73:73,76:76,77:77}],32:[function(t,e,n){var r=t(38),o=t(23),i=t(40),a=t(87),u=t(25),s=function(t,e,n){var c,f,l,p,h=t&s.F,d=t&s.G,m=t&s.S,v=t&s.P,y=t&s.B,g=d?r:m?r[e]||(r[e]={}):(r[e]||{}).prototype,b=d?o:o[e]||(o[e]={}),w=b.prototype||(b.prototype={});d&&(n=e);for(c in n)l=((f=!h&&g&&void 0!==g[c])?g:n)[c],p=y&&f?u(l,r):v&&"function"==typeof l?u(Function.call,l):l,g&&a(g,c,l,t&s.U),b[c]!=l&&i(b,c,p),v&&w[c]!=l&&(w[c]=l)};r.core=o,s.F=1,s.G=2,s.S=4,s.P=8,s.B=16,s.W=32,s.U=64,s.R=128,e.exports=s},{23:23,25:25,38:38,40:40,87:87}],33:[function(t,e,n){var r=t(117)("match");e.exports=function(t){var e=/./;try{"/./"[t](e)}catch(n){try{return e[r]=!1,!"/./"[t](e)}catch(t){}}return!0}},{117:117}],34:[function(t,e,n){e.exports=function(t){try{return!!t()}catch(t){return!0}}},{}],35:[function(t,e,n){"use strict";var r=t(40),o=t(87),i=t(34),a=t(27),u=t(117);e.exports=function(t,e,n){var s=u(t),c=n(a,s,""[t]),f=c[0],l=c[1];i(function(){var e={};return e[s]=function(){return 7},7!=""[t](e)})&&(o(String.prototype,t,f),r(RegExp.prototype,s,2==e?function(t,e){return l.call(t,this,e)}:function(t){return l.call(t,this)}))}},{117:117,27:27,34:34,40:40,87:87}],36:[function(t,e,n){"use strict";var r=t(7);e.exports=function(){var t=r(this),e="";return t.global&&(e+="g"),t.ignoreCase&&(e+="i"),t.multiline&&(e+="m"),t.unicode&&(e+="u"),t.sticky&&(e+="y"),e}},{7:7}],37:[function(t,e,n){var r=t(25),o=t(51),i=t(46),a=t(7),u=t(108),s=t(118),c={},f={};(n=e.exports=function(t,e,n,l,p){var h,d,m,v,y=p?function(){return t}:s(t),g=r(n,l,e?2:1),b=0;if("function"!=typeof y)throw TypeError(t+" is not iterable!");if(i(y)){for(h=u(t.length);h>b;b++)if((v=e?g(a(d=t[b])[0],d[1]):g(t[b]))===c||v===f)return v}else for(m=y.call(t);!(d=m.next()).done;)if((v=o(m,g,d.value,e))===c||v===f)return v}).BREAK=c,n.RETURN=f},{108:108,118:118,25:25,46:46,51:51,7:7}],38:[function(t,e,n){var r=e.exports="undefined"!=typeof window&&window.Math==Math?window:"undefined"!=typeof self&&self.Math==Math?self:Function("return this")();"number"==typeof __g&&(__g=r)},{}],39:[function(t,e,n){var r={}.hasOwnProperty;e.exports=function(t,e){return r.call(t,e)}},{}],40:[function(t,e,n){var r=t(67),o=t(85);e.exports=t(28)?function(t,e,n){return r.f(t,e,o(1,n))}:function(t,e,n){return t[e]=n,t}},{28:28,67:67,85:85}],41:[function(t,e,n){e.exports=t(38).document&&document.documentElement},{38:38}],42:[function(t,e,n){e.exports=!t(28)&&!t(34)(function(){return 7!=Object.defineProperty(t(29)("div"),"a",{get:function(){return 7}}).a})},{28:28,29:29,34:34}],43:[function(t,e,n){var r=t(49),o=t(90).set;e.exports=function(t,e,n){var i,a=e.constructor;return a!==n&&"function"==typeof a&&(i=a.prototype)!==n.prototype&&r(i)&&o&&o(t,i),t}},{49:49,90:90}],44:[function(t,e,n){e.exports=function(t,e,n){var r=void 0===n;switch(e.length){case 0:return r?t():t.call(n);case 1:return r?t(e[0]):t.call(n,e[0]);case 2:return r?t(e[0],e[1]):t.call(n,e[0],e[1]);case 3:return r?t(e[0],e[1],e[2]):t.call(n,e[0],e[1],e[2]);case 4:return r?t(e[0],e[1],e[2],e[3]):t.call(n,e[0],e[1],e[2],e[3])}return t.apply(n,e)}},{}],45:[function(t,e,n){var r=t(18);e.exports=Object("z").propertyIsEnumerable(0)?Object:function(t){return"String"==r(t)?t.split(""):Object(t)}},{18:18}],46:[function(t,e,n){var r=t(56),o=t(117)("iterator"),i=Array.prototype;e.exports=function(t){return void 0!==t&&(r.Array===t||i[o]===t)}},{117:117,56:56}],47:[function(t,e,n){var r=t(18);e.exports=Array.isArray||function(t){return"Array"==r(t)}},{18:18}],48:[function(t,e,n){var r=t(49),o=Math.floor;e.exports=function(t){return!r(t)&&isFinite(t)&&o(t)===t}},{49:49}],49:[function(t,e,n){e.exports=function(t){return"object"==typeof t?null!==t:"function"==typeof t}},{}],50:[function(t,e,n){var r=t(49),o=t(18),i=t(117)("match");e.exports=function(t){var e;return r(t)&&(void 0!==(e=t[i])?!!e:"RegExp"==o(t))}},{117:117,18:18,49:49}],51:[function(t,e,n){var r=t(7);e.exports=function(t,e,n,o){try{return o?e(r(n)[0],n[1]):e(n)}catch(e){var i=t.return;throw void 0!==i&&r(i.call(t)),e}}},{7:7}],52:[function(t,e,n){"use strict";var r=t(66),o=t(85),i=t(92),a={};t(40)(a,t(117)("iterator"),function(){return this}),e.exports=function(t,e,n){t.prototype=r(a,{next:o(1,n)}),i(t,e+" Iterator")}},{117:117,40:40,66:66,85:85,92:92}],53:[function(t,e,n){"use strict";var r=t(58),o=t(32),i=t(87),a=t(40),u=t(39),s=t(56),c=t(52),f=t(92),l=t(74),p=t(117)("iterator"),h=!([].keys&&"next"in[].keys()),d=function(){return this};e.exports=function(t,e,n,m,v,y,g){c(n,e,m);var b,w,_,x=function(t){if(!h&&t in O)return O[t];switch(t){case"keys":case"values":return function(){return new n(this,t)}}return function(){return new n(this,t)}},S=e+" Iterator",k="values"==v,E=!1,O=t.prototype,j=O[p]||O["@@iterator"]||v&&O[v],A=j||x(v),P=v?k?x("entries"):A:void 0,I="Array"==e?O.entries||j:j;if(I&&(_=l(I.call(new t)))!==Object.prototype&&(f(_,S,!0),r||u(_,p)||a(_,p,d)),k&&j&&"values"!==j.name&&(E=!0,A=function(){return j.call(this)}),r&&!g||!h&&!E&&O[p]||a(O,p,A),s[e]=A,s[S]=d,v)if(b={values:k?A:x("values"),keys:y?A:x("keys"),entries:P},g)for(w in b)w in O||i(O,w,b[w]);else o(o.P+o.F*(h||E),e,b);return b}},{117:117,32:32,39:39,40:40,52:52,56:56,58:58,74:74,87:87,92:92}],54:[function(t,e,n){var r=t(117)("iterator"),o=!1;try{var i=[7][r]();i.return=function(){o=!0},Array.from(i,function(){throw 2})}catch(t){}e.exports=function(t,e){if(!e&&!o)return!1;var n=!1;try{var i=[7],a=i[r]();a.next=function(){return{done:n=!0}},i[r]=function(){return a},t(i)}catch(t){}return n}},{117:117}],55:[function(t,e,n){e.exports=function(t,e){return{value:e,done:!!t}}},{}],56:[function(t,e,n){e.exports={}},{}],57:[function(t,e,n){var r=t(76),o=t(107);e.exports=function(t,e){for(var n,i=o(t),a=r(i),u=a.length,s=0;u>s;)if(i[n=a[s++]]===e)return n}},{107:107,76:76}],58:[function(t,e,n){e.exports=!1},{}],59:[function(t,e,n){var r=Math.expm1;e.exports=!r||r(10)>22025.465794806718||r(10)<22025.465794806718||-2e-17!=r(-2e-17)?function(t){return 0==(t=+t)?t:t>-1e-6&&t<1e-6?t+t*t/2:Math.exp(t)-1}:r},{}],60:[function(t,e,n){e.exports=Math.log1p||function(t){return(t=+t)>-1e-8&&t<1e-8?t-t*t/2:Math.log(1+t)}},{}],61:[function(t,e,n){e.exports=Math.sign||function(t){return 0==(t=+t)||t!=t?t:t<0?-1:1}},{}],62:[function(t,e,n){var r=t(114)("meta"),o=t(49),i=t(39),a=t(67).f,u=0,s=Object.isExtensible||function(){return!0},c=!t(34)(function(){return s(Object.preventExtensions({}))}),f=function(t){a(t,r,{value:{i:"O"+ ++u,w:{}}})},l=e.exports={KEY:r,NEED:!1,fastKey:function(t,e){if(!o(t))return"symbol"==typeof t?t:("string"==typeof t?"S":"P")+t;if(!i(t,r)){if(!s(t))return"F";if(!e)return"E";f(t)}return t[r].i},getWeak:function(t,e){if(!i(t,r)){if(!s(t))return!0;if(!e)return!1;f(t)}return t[r].w},onFreeze:function(t){return c&&l.NEED&&s(t)&&!i(t,r)&&f(t),t}}},{114:114,34:34,39:39,49:49,67:67}],63:[function(t,e,n){var r=t(149),o=t(32),i=t(94)("metadata"),a=i.store||(i.store=new(t(255))),u=function(t,e,n){var o=a.get(t);if(!o){if(!n)return;a.set(t,o=new r)}var i=o.get(e);if(!i){if(!n)return;o.set(e,i=new r)}return i};e.exports={store:a,map:u,has:function(t,e,n){var r=u(e,n,!1);return void 0!==r&&r.has(t)},get:function(t,e,n){var r=u(e,n,!1);return void 0===r?void 0:r.get(t)},set:function(t,e,n,r){u(n,r,!0).set(t,e)},keys:function(t,e){var n=u(t,e,!1),r=[];return n&&n.forEach(function(t,e){r.push(e)}),r},key:function(t){return void 0===t||"symbol"==typeof t?t:String(t)},exp:function(t){o(o.S,"Reflect",t)}}},{149:149,255:255,32:32,94:94}],64:[function(t,e,n){var r=t(38),o=t(104).set,i=r.MutationObserver||r.WebKitMutationObserver,a=r.process,u=r.Promise,s="process"==t(18)(a);e.exports=function(){var t,e,n,c=function(){var r,o;for(s&&(r=a.domain)&&r.exit();t;){o=t.fn,t=t.next;try{o()}catch(r){throw t?n():e=void 0,r}}e=void 0,r&&r.enter()};if(s)n=function(){a.nextTick(c)};else if(i){var f=!0,l=document.createTextNode("");new i(c).observe(l,{characterData:!0}),n=function(){l.data=f=!f}}else if(u&&u.resolve){var p=u.resolve();n=function(){p.then(c)}}else n=function(){o.call(r,c)};return function(r){var o={fn:r,next:void 0};e&&(e.next=o),t||(t=o,n()),e=o}}},{104:104,18:18,38:38}],65:[function(t,e,n){"use strict";var r=t(76),o=t(73),i=t(77),a=t(109),u=t(45),s=Object.assign;e.exports=!s||t(34)(function(){var t={},e={},n=Symbol(),r="abcdefghijklmnopqrst";return t[n]=7,r.split("").forEach(function(t){e[t]=t}),7!=s({},t)[n]||Object.keys(s({},e)).join("")!=r})?function(t,e){for(var n=a(t),s=arguments.length,c=1,f=o.f,l=i.f;s>c;)for(var p,h=u(arguments[c++]),d=f?r(h).concat(f(h)):r(h),m=d.length,v=0;m>v;)l.call(h,p=d[v++])&&(n[p]=h[p]);return n}:s},{109:109,34:34,45:45,73:73,76:76,77:77}],66:[function(t,e,n){var r=t(7),o=t(68),i=t(30),a=t(93)("IE_PROTO"),u=function(){},s=function(){var e,n=t(29)("iframe"),r=i.length;for(n.style.display="none",t(41).appendChild(n),n.src="javascript:",(e=n.contentWindow.document).open(),e.write("<script>document.F=Object<\/script>"),e.close(),s=e.F;r--;)delete s.prototype[i[r]];return s()};e.exports=Object.create||function(t,e){var n;return null!==t?(u.prototype=r(t),n=new u,u.prototype=null,n[a]=t):n=s(),void 0===e?n:o(n,e)}},{29:29,30:30,41:41,68:68,7:7,93:93}],67:[function(t,e,n){var r=t(7),o=t(42),i=t(110),a=Object.defineProperty;n.f=t(28)?Object.defineProperty:function(t,e,n){if(r(t),e=i(e,!0),r(n),o)try{return a(t,e,n)}catch(t){}if("get"in n||"set"in n)throw TypeError("Accessors not supported!");return"value"in n&&(t[e]=n.value),t}},{110:110,28:28,42:42,7:7}],68:[function(t,e,n){var r=t(67),o=t(7),i=t(76);e.exports=t(28)?Object.defineProperties:function(t,e){o(t);for(var n,a=i(e),u=a.length,s=0;u>s;)r.f(t,n=a[s++],e[n]);return t}},{28:28,67:67,7:7,76:76}],69:[function(t,e,n){e.exports=t(58)||!t(34)(function(){var e=Math.random();__defineSetter__.call(null,e,function(){}),delete t(38)[e]})},{34:34,38:38,58:58}],70:[function(t,e,n){var r=t(77),o=t(85),i=t(107),a=t(110),u=t(39),s=t(42),c=Object.getOwnPropertyDescriptor;n.f=t(28)?c:function(t,e){if(t=i(t),e=a(e,!0),s)try{return c(t,e)}catch(t){}if(u(t,e))return o(!r.f.call(t,e),t[e])}},{107:107,110:110,28:28,39:39,42:42,77:77,85:85}],71:[function(t,e,n){var r=t(107),o=t(72).f,i={}.toString,a="object"==typeof window&&window&&Object.getOwnPropertyNames?Object.getOwnPropertyNames(window):[],u=function(t){try{return o(t)}catch(t){return a.slice()}};e.exports.f=function(t){return a&&"[object Window]"==i.call(t)?u(t):o(r(t))}},{107:107,72:72}],72:[function(t,e,n){var r=t(75),o=t(30).concat("length","prototype");n.f=Object.getOwnPropertyNames||function(t){return r(t,o)}},{30:30,75:75}],73:[function(t,e,n){n.f=Object.getOwnPropertySymbols},{}],74:[function(t,e,n){var r=t(39),o=t(109),i=t(93)("IE_PROTO"),a=Object.prototype;e.exports=Object.getPrototypeOf||function(t){return t=o(t),r(t,i)?t[i]:"function"==typeof t.constructor&&t instanceof t.constructor?t.constructor.prototype:t instanceof Object?a:null}},{109:109,39:39,93:93}],75:[function(t,e,n){var r=t(39),o=t(107),i=t(11)(!1),a=t(93)("IE_PROTO");e.exports=function(t,e){var n,u=o(t),s=0,c=[];for(n in u)n!=a&&r(u,n)&&c.push(n);for(;e.length>s;)r(u,n=e[s++])&&(~i(c,n)||c.push(n));return c}},{107:107,11:11,39:39,93:93}],76:[function(t,e,n){var r=t(75),o=t(30);e.exports=Object.keys||function(t){return r(t,o)}},{30:30,75:75}],77:[function(t,e,n){n.f={}.propertyIsEnumerable},{}],78:[function(t,e,n){var r=t(32),o=t(23),i=t(34);e.exports=function(t,e){var n=(o.Object||{})[t]||Object[t],a={};a[t]=e(n),r(r.S+r.F*i(function(){n(1)}),"Object",a)}},{23:23,32:32,34:34}],79:[function(t,e,n){var r=t(76),o=t(107),i=t(77).f;e.exports=function(t){return function(e){for(var n,a=o(e),u=r(a),s=u.length,c=0,f=[];s>c;)i.call(a,n=u[c++])&&f.push(t?[n,a[n]]:a[n]);return f}}},{107:107,76:76,77:77}],80:[function(t,e,n){var r=t(72),o=t(73),i=t(7),a=t(38).Reflect;e.exports=a&&a.ownKeys||function(t){var e=r.f(i(t)),n=o.f;return n?e.concat(n(t)):e}},{38:38,7:7,72:72,73:73}],81:[function(t,e,n){var r=t(38).parseFloat,o=t(102).trim;e.exports=1/r(t(103)+"-0")!=-1/0?function(t){var e=o(String(t),3),n=r(e);return 0===n&&"-"==e.charAt(0)?-0:n}:r},{102:102,103:103,38:38}],82:[function(t,e,n){var r=t(38).parseInt,o=t(102).trim,i=t(103),a=/^[\-+]?0[xX]/;e.exports=8!==r(i+"08")||22!==r(i+"0x16")?function(t,e){var n=o(String(t),3);return r(n,e>>>0||(a.test(n)?16:10))}:r},{102:102,103:103,38:38}],83:[function(t,e,n){"use strict";var r=t(84),o=t(44),i=t(3);e.exports=function(){for(var t=i(this),e=arguments.length,n=Array(e),a=0,u=r._,s=!1;e>a;)(n[a]=arguments[a++])===u&&(s=!0);return function(){var r,i=this,a=arguments.length,c=0,f=0;if(!s&&!a)return o(t,n,i);if(r=n.slice(),s)for(;e>c;c++)r[c]===u&&(r[c]=arguments[f++]);for(;a>f;)r.push(arguments[f++]);return o(t,r,i)}}},{3:3,44:44,84:84}],84:[function(t,e,n){e.exports=t(38)},{38:38}],85:[function(t,e,n){e.exports=function(t,e){return{enumerable:!(1&t),configurable:!(2&t),writable:!(4&t),value:e}}},{}],86:[function(t,e,n){var r=t(87);e.exports=function(t,e,n){for(var o in e)r(t,o,e[o],n);return t}},{87:87}],87:[function(t,e,n){var r=t(38),o=t(40),i=t(39),a=t(114)("src"),u=Function.toString,s=(""+u).split("toString");t(23).inspectSource=function(t){return u.call(t)},(e.exports=function(t,e,n,u){var c="function"==typeof n;c&&(i(n,"name")||o(n,"name",e)),t[e]!==n&&(c&&(i(n,a)||o(n,a,t[e]?""+t[e]:s.join(String(e)))),t===r?t[e]=n:u?t[e]?t[e]=n:o(t,e,n):(delete t[e],o(t,e,n)))})(Function.prototype,"toString",function(){return"function"==typeof this&&this[a]||u.call(this)})},{114:114,23:23,38:38,39:39,40:40}],88:[function(t,e,n){e.exports=function(t,e){var n=e===Object(e)?function(t){return e[t]}:e;return function(e){return String(e).replace(t,n)}}},{}],89:[function(t,e,n){e.exports=Object.is||function(t,e){return t===e?0!==t||1/t==1/e:t!=t&&e!=e}},{}],90:[function(t,e,n){var r=t(49),o=t(7),i=function(t,e){if(o(t),!r(e)&&null!==e)throw TypeError(e+": can't set as prototype!")};e.exports={set:Object.setPrototypeOf||("__proto__"in{}?function(e,n,r){try{(r=t(25)(Function.call,t(70).f(Object.prototype,"__proto__").set,2))(e,[]),n=!(e instanceof Array)}catch(t){n=!0}return function(t,e){return i(t,e),n?t.__proto__=e:r(t,e),t}}({},!1):void 0),check:i}},{25:25,49:49,7:7,70:70}],91:[function(t,e,n){"use strict";var r=t(38),o=t(67),i=t(28),a=t(117)("species");e.exports=function(t){var e=r[t];i&&e&&!e[a]&&o.f(e,a,{configurable:!0,get:function(){return this}})}},{117:117,28:28,38:38,67:67}],92:[function(t,e,n){var r=t(67).f,o=t(39),i=t(117)("toStringTag");e.exports=function(t,e,n){t&&!o(t=n?t:t.prototype,i)&&r(t,i,{configurable:!0,value:e})}},{117:117,39:39,67:67}],93:[function(t,e,n){var r=t(94)("keys"),o=t(114);e.exports=function(t){return r[t]||(r[t]=o(t))}},{114:114,94:94}],94:[function(t,e,n){var r=t(38),o=r["__core-js_shared__"]||(r["__core-js_shared__"]={});e.exports=function(t){return o[t]||(o[t]={})}},{38:38}],95:[function(t,e,n){var r=t(7),o=t(3),i=t(117)("species");e.exports=function(t,e){var n,a=r(t).constructor;return void 0===a||void 0==(n=r(a)[i])?e:o(n)}},{117:117,3:3,7:7}],96:[function(t,e,n){var r=t(34);e.exports=function(t,e){return!!t&&r(function(){e?t.call(null,function(){},1):t.call(null)})}},{34:34}],97:[function(t,e,n){var r=t(106),o=t(27);e.exports=function(t){return function(e,n){var i,a,u=String(o(e)),s=r(n),c=u.length;return s<0||s>=c?t?"":void 0:(i=u.charCodeAt(s))<55296||i>56319||s+1===c||(a=u.charCodeAt(s+1))<56320||a>57343?t?u.charAt(s):i:t?u.slice(s,s+2):a-56320+(i-55296<<10)+65536}}},{106:106,27:27}],98:[function(t,e,n){var r=t(50),o=t(27);e.exports=function(t,e,n){if(r(e))throw TypeError("String#"+n+" doesn't accept regex!");return String(o(t))}},{27:27,50:50}],99:[function(t,e,n){var r=t(32),o=t(34),i=t(27),a=/"/g,u=function(t,e,n,r){var o=String(i(t)),u="<"+e;return""!==n&&(u+=" "+n+'="'+String(r).replace(a,"&quot;")+'"'),u+">"+o+"</"+e+">"};e.exports=function(t,e){var n={};n[t]=e(u),r(r.P+r.F*o(function(){var e=""[t]('"');return e!==e.toLowerCase()||e.split('"').length>3}),"String",n)}},{27:27,32:32,34:34}],100:[function(t,e,n){var r=t(108),o=t(101),i=t(27);e.exports=function(t,e,n,a){var u=String(i(t)),s=u.length,c=void 0===n?" ":String(n),f=r(e);if(f<=s||""==c)return u;var l=f-s,p=o.call(c,Math.ceil(l/c.length));return p.length>l&&(p=p.slice(0,l)),a?p+u:u+p}},{101:101,108:108,27:27}],101:[function(t,e,n){"use strict";var r=t(106),o=t(27);e.exports=function(t){var e=String(o(this)),n="",i=r(t);if(i<0||i==1/0)throw RangeError("Count can't be negative");for(;i>0;(i>>>=1)&&(e+=e))1&i&&(n+=e);return n}},{106:106,27:27}],102:[function(t,e,n){var r=t(32),o=t(27),i=t(34),a=t(103),u="["+a+"]",s=RegExp("^"+u+u+"*"),c=RegExp(u+u+"*$"),f=function(t,e,n){var o={},u=i(function(){return!!a[t]()||"​"!="​"[t]()}),s=o[t]=u?e(l):a[t];n&&(o[n]=s),r(r.P+r.F*u,"String",o)},l=f.trim=function(t,e){return t=String(o(t)),1&e&&(t=t.replace(s,"")),2&e&&(t=t.replace(c,"")),t};e.exports=f},{103:103,27:27,32:32,34:34}],103:[function(t,e,n){e.exports="\t\n\v\f\r   ᠎             　\u2028\u2029\ufeff"},{}],104:[function(t,e,n){var r,o,i,a=t(25),u=t(44),s=t(41),c=t(29),f=t(38),l=f.process,p=f.setImmediate,h=f.clearImmediate,d=f.MessageChannel,m=0,v={},y=function(){var t=+this;if(v.hasOwnProperty(t)){var e=v[t];delete v[t],e()}},g=function(t){y.call(t.data)};p&&h||(p=function(t){for(var e=[],n=1;arguments.length>n;)e.push(arguments[n++]);return v[++m]=function(){u("function"==typeof t?t:Function(t),e)},r(m),m},h=function(t){delete v[t]},"process"==t(18)(l)?r=function(t){l.nextTick(a(y,t,1))}:d?(i=(o=new d).port2,o.port1.onmessage=g,r=a(i.postMessage,i,1)):f.addEventListener&&"function"==typeof postMessage&&!f.importScripts?(r=function(t){f.postMessage(t+"","*")},f.addEventListener("message",g,!1)):r="onreadystatechange"in c("script")?function(t){s.appendChild(c("script")).onreadystatechange=function(){s.removeChild(this),y.call(t)}}:function(t){setTimeout(a(y,t,1),0)}),e.exports={set:p,clear:h}},{18:18,25:25,29:29,38:38,41:41,44:44}],105:[function(t,e,n){var r=t(106),o=Math.max,i=Math.min;e.exports=function(t,e){return(t=r(t))<0?o(t+e,0):i(t,e)}},{106:106}],106:[function(t,e,n){var r=Math.ceil,o=Math.floor;e.exports=function(t){return isNaN(t=+t)?0:(t>0?o:r)(t)}},{}],107:[function(t,e,n){var r=t(45),o=t(27);e.exports=function(t){return r(o(t))}},{27:27,45:45}],108:[function(t,e,n){var r=t(106),o=Math.min;e.exports=function(t){return t>0?o(r(t),9007199254740991):0}},{106:106}],109:[function(t,e,n){var r=t(27);e.exports=function(t){return Object(r(t))}},{27:27}],110:[function(t,e,n){var r=t(49);e.exports=function(t,e){if(!r(t))return t;var n,o;if(e&&"function"==typeof(n=t.toString)&&!r(o=n.call(t)))return o;if("function"==typeof(n=t.valueOf)&&!r(o=n.call(t)))return o;if(!e&&"function"==typeof(n=t.toString)&&!r(o=n.call(t)))return o;throw TypeError("Can't convert object to primitive value")}},{49:49}],111:[function(t,e,n){"use strict";if(t(28)){var r=t(58),o=t(38),i=t(34),a=t(32),u=t(113),s=t(112),c=t(25),f=t(6),l=t(85),p=t(40),h=t(86),d=t(106),m=t(108),v=t(105),y=t(110),g=t(39),b=t(89),w=t(17),_=t(49),x=t(109),S=t(46),k=t(66),E=t(74),O=t(72).f,j=t(118),A=t(114),P=t(117),I=t(12),F=t(11),N=t(95),M=t(130),T=t(56),R=t(54),q=t(91),L=t(9),C=t(8),D=t(67),U=t(70),W=D.f,B=U.f,$=o.RangeError,z=o.TypeError,G=o.Uint8Array,H=Array.prototype,J=s.ArrayBuffer,V=s.DataView,Y=I(0),K=I(2),X=I(3),Z=I(4),Q=I(5),tt=I(6),et=F(!0),nt=F(!1),rt=M.values,ot=M.keys,it=M.entries,at=H.lastIndexOf,ut=H.reduce,st=H.reduceRight,ct=H.join,ft=H.sort,lt=H.slice,pt=H.toString,ht=H.toLocaleString,dt=P("iterator"),mt=P("toStringTag"),vt=A("typed_constructor"),yt=A("def_constructor"),gt=u.CONSTR,bt=u.TYPED,wt=u.VIEW,_t=I(1,function(t,e){return jt(N(t,t[yt]),e)}),xt=i(function(){return 1===new G(new Uint16Array([1]).buffer)[0]}),St=!!G&&!!G.prototype.set&&i(function(){new G(1).set({})}),kt=function(t,e){if(void 0===t)throw z("Wrong length!");var n=+t,r=m(t);if(e&&!b(n,r))throw $("Wrong length!");return r},Et=function(t,e){var n=d(t);if(n<0||n%e)throw $("Wrong offset!");return n},Ot=function(t){if(_(t)&&bt in t)return t;throw z(t+" is not a typed array!")},jt=function(t,e){if(!(_(t)&&vt in t))throw z("It is not a typed array constructor!");return new t(e)},At=function(t,e){return Pt(N(t,t[yt]),e)},Pt=function(t,e){for(var n=0,r=e.length,o=jt(t,r);r>n;)o[n]=e[n++];return o},It=function(t,e,n){W(t,e,{get:function(){return this._d[n]}})},Ft=function(t){var e,n,r,o,i,a,u=x(t),s=arguments.length,f=s>1?arguments[1]:void 0,l=void 0!==f,p=j(u);if(void 0!=p&&!S(p)){for(a=p.call(u),r=[],e=0;!(i=a.next()).done;e++)r.push(i.value);u=r}for(l&&s>2&&(f=c(f,arguments[2],2)),e=0,n=m(u.length),o=jt(this,n);n>e;e++)o[e]=l?f(u[e],e):u[e];return o},Nt=function(){for(var t=0,e=arguments.length,n=jt(this,e);e>t;)n[t]=arguments[t++];return n},Mt=!!G&&i(function(){ht.call(new G(1))}),Tt=function(){return ht.apply(Mt?lt.call(Ot(this)):Ot(this),arguments)},Rt={copyWithin:function(t,e){return C.call(Ot(this),t,e,arguments.length>2?arguments[2]:void 0)},every:function(t){return Z(Ot(this),t,arguments.length>1?arguments[1]:void 0)},fill:function(t){return L.apply(Ot(this),arguments)},filter:function(t){return At(this,K(Ot(this),t,arguments.length>1?arguments[1]:void 0))},find:function(t){return Q(Ot(this),t,arguments.length>1?arguments[1]:void 0)},findIndex:function(t){return tt(Ot(this),t,arguments.length>1?arguments[1]:void 0)},forEach:function(t){Y(Ot(this),t,arguments.length>1?arguments[1]:void 0)},indexOf:function(t){return nt(Ot(this),t,arguments.length>1?arguments[1]:void 0)},includes:function(t){return et(Ot(this),t,arguments.length>1?arguments[1]:void 0)},join:function(t){return ct.apply(Ot(this),arguments)},lastIndexOf:function(t){return at.apply(Ot(this),arguments)},map:function(t){return _t(Ot(this),t,arguments.length>1?arguments[1]:void 0)},reduce:function(t){return ut.apply(Ot(this),arguments)},reduceRight:function(t){return st.apply(Ot(this),arguments)},reverse:function(){for(var t,e=this,n=Ot(e).length,r=Math.floor(n/2),o=0;o<r;)t=e[o],e[o++]=e[--n],e[n]=t;return e},some:function(t){return X(Ot(this),t,arguments.length>1?arguments[1]:void 0)},sort:function(t){return ft.call(Ot(this),t)},subarray:function(t,e){var n=Ot(this),r=n.length,o=v(t,r);return new(N(n,n[yt]))(n.buffer,n.byteOffset+o*n.BYTES_PER_ELEMENT,m((void 0===e?r:v(e,r))-o))}},qt=function(t,e){return At(this,lt.call(Ot(this),t,e))},Lt=function(t){Ot(this);var e=Et(arguments[1],1),n=this.length,r=x(t),o=m(r.length),i=0;if(o+e>n)throw $("Wrong length!");for(;i<o;)this[e+i]=r[i++]},Ct={entries:function(){return it.call(Ot(this))},keys:function(){return ot.call(Ot(this))},values:function(){return rt.call(Ot(this))}},Dt=function(t,e){return _(t)&&t[bt]&&"symbol"!=typeof e&&e in t&&String(+e)==String(e)},Ut=function(t,e){return Dt(t,e=y(e,!0))?l(2,t[e]):B(t,e)},Wt=function(t,e,n){return!(Dt(t,e=y(e,!0))&&_(n)&&g(n,"value"))||g(n,"get")||g(n,"set")||n.configurable||g(n,"writable")&&!n.writable||g(n,"enumerable")&&!n.enumerable?W(t,e,n):(t[e]=n.value,t)};gt||(U.f=Ut,D.f=Wt),a(a.S+a.F*!gt,"Object",{getOwnPropertyDescriptor:Ut,defineProperty:Wt}),i(function(){pt.call({})})&&(pt=ht=function(){return ct.call(this)});var Bt=h({},Rt);h(Bt,Ct),p(Bt,dt,Ct.values),h(Bt,{slice:qt,set:Lt,constructor:function(){},toString:pt,toLocaleString:Tt}),It(Bt,"buffer","b"),It(Bt,"byteOffset","o"),It(Bt,"byteLength","l"),It(Bt,"length","e"),W(Bt,mt,{get:function(){return this[bt]}}),e.exports=function(t,e,n,s){var c=t+((s=!!s)?"Clamped":"")+"Array",l="Uint8Array"!=c,h="get"+t,d="set"+t,v=o[c],y=v||{},g=v&&E(v),b=!v||!u.ABV,x={},S=v&&v.prototype,j=function(t,n){var r=t._d;return r.v[h](n*e+r.o,xt)},A=function(t,n,r){var o=t._d;s&&(r=(r=Math.round(r))<0?0:r>255?255:255&r),o.v[d](n*e+o.o,r,xt)},P=function(t,e){W(t,e,{get:function(){return j(this,e)},set:function(t){return A(this,e,t)},enumerable:!0})};b?(v=n(function(t,n,r,o){f(t,v,c,"_d");var i,a,u,s,l=0,h=0;if(_(n)){if(!(n instanceof J||"ArrayBuffer"==(s=w(n))||"SharedArrayBuffer"==s))return bt in n?Pt(v,n):Ft.call(v,n);i=n,h=Et(r,e);var d=n.byteLength;if(void 0===o){if(d%e)throw $("Wrong length!");if((a=d-h)<0)throw $("Wrong length!")}else if((a=m(o)*e)+h>d)throw $("Wrong length!");u=a/e}else u=kt(n,!0),i=new J(a=u*e);for(p(t,"_d",{b:i,o:h,l:a,e:u,v:new V(i)});l<u;)P(t,l++)}),S=v.prototype=k(Bt),p(S,"constructor",v)):R(function(t){new v(null),new v(t)},!0)||(v=n(function(t,n,r,o){f(t,v,c);var i;return _(n)?n instanceof J||"ArrayBuffer"==(i=w(n))||"SharedArrayBuffer"==i?void 0!==o?new y(n,Et(r,e),o):void 0!==r?new y(n,Et(r,e)):new y(n):bt in n?Pt(v,n):Ft.call(v,n):new y(kt(n,l))}),Y(g!==Function.prototype?O(y).concat(O(g)):O(y),function(t){t in v||p(v,t,y[t])}),v.prototype=S,r||(S.constructor=v));var I=S[dt],F=!!I&&("values"==I.name||void 0==I.name),N=Ct.values;p(v,vt,!0),p(S,bt,c),p(S,wt,!0),p(S,yt,v),(s?new v(1)[mt]==c:mt in S)||W(S,mt,{get:function(){return c}}),x[c]=v,a(a.G+a.W+a.F*(v!=y),x),a(a.S,c,{BYTES_PER_ELEMENT:e,from:Ft,of:Nt}),"BYTES_PER_ELEMENT"in S||p(S,"BYTES_PER_ELEMENT",e),a(a.P,c,Rt),q(c),a(a.P+a.F*St,c,{set:Lt}),a(a.P+a.F*!F,c,Ct),a(a.P+a.F*(S.toString!=pt),c,{toString:pt}),a(a.P+a.F*i(function(){new v(1).slice()}),c,{slice:qt}),a(a.P+a.F*(i(function(){return[1,2].toLocaleString()!=new v([1,2]).toLocaleString()})||!i(function(){S.toLocaleString.call([1,2])})),c,{toLocaleString:Tt}),T[c]=F?I:N,r||F||p(S,dt,N)}}else e.exports=function(){}},{105:105,106:106,108:108,109:109,11:11,110:110,112:112,113:113,114:114,117:117,118:118,12:12,130:130,17:17,25:25,28:28,32:32,34:34,38:38,39:39,40:40,46:46,49:49,54:54,56:56,58:58,6:6,66:66,67:67,70:70,72:72,74:74,8:8,85:85,86:86,89:89,9:9,91:91,95:95}],112:[function(t,e,n){"use strict";var r=t(38),o=t(28),i=t(58),a=t(113),u=t(40),s=t(86),c=t(34),f=t(6),l=t(106),p=t(108),h=t(72).f,d=t(67).f,m=t(9),v=t(92),y=r.ArrayBuffer,g=r.DataView,b=r.Math,w=r.RangeError,_=r.Infinity,x=y,S=b.abs,k=b.pow,E=b.floor,O=b.log,j=b.LN2,A=o?"_b":"buffer",P=o?"_l":"byteLength",I=o?"_o":"byteOffset",F=function(t,e,n){var r,o,i,a=Array(n),u=8*n-e-1,s=(1<<u)-1,c=s>>1,f=23===e?k(2,-24)-k(2,-77):0,l=0,p=t<0||0===t&&1/t<0?1:0;for((t=S(t))!=t||t===_?(o=t!=t?1:0,r=s):(r=E(O(t)/j),t*(i=k(2,-r))<1&&(r--,i*=2),(t+=r+c>=1?f/i:f*k(2,1-c))*i>=2&&(r++,i/=2),r+c>=s?(o=0,r=s):r+c>=1?(o=(t*i-1)*k(2,e),r+=c):(o=t*k(2,c-1)*k(2,e),r=0));e>=8;a[l++]=255&o,o/=256,e-=8);for(r=r<<e|o,u+=e;u>0;a[l++]=255&r,r/=256,u-=8);return a[--l]|=128*p,a},N=function(t,e,n){var r,o=8*n-e-1,i=(1<<o)-1,a=i>>1,u=o-7,s=n-1,c=t[s--],f=127&c;for(c>>=7;u>0;f=256*f+t[s],s--,u-=8);for(r=f&(1<<-u)-1,f>>=-u,u+=e;u>0;r=256*r+t[s],s--,u-=8);if(0===f)f=1-a;else{if(f===i)return r?NaN:c?-_:_;r+=k(2,e),f-=a}return(c?-1:1)*r*k(2,f-e)},M=function(t){return t[3]<<24|t[2]<<16|t[1]<<8|t[0]},T=function(t){return[255&t]},R=function(t){return[255&t,t>>8&255]},q=function(t){return[255&t,t>>8&255,t>>16&255,t>>24&255]},L=function(t){return F(t,52,8)},C=function(t){return F(t,23,4)},D=function(t,e,n){d(t.prototype,e,{get:function(){return this[n]}})},U=function(t,e,n,r){var o=+n,i=l(o);if(o!=i||i<0||i+e>t[P])throw w("Wrong index!");var a=t[A]._b,u=i+t[I],s=a.slice(u,u+e);return r?s:s.reverse()},W=function(t,e,n,r,o,i){var a=+n,u=l(a);if(a!=u||u<0||u+e>t[P])throw w("Wrong index!");for(var s=t[A]._b,c=u+t[I],f=r(+o),p=0;p<e;p++)s[c+p]=f[i?p:e-p-1]},B=function(t,e){f(t,y,"ArrayBuffer");var n=+e,r=p(n);if(n!=r)throw w("Wrong length!");return r};if(a.ABV){if(!c(function(){new y})||!c(function(){new y(.5)})){for(var $,z=(y=function(t){return new x(B(this,t))}).prototype=x.prototype,G=h(x),H=0;G.length>H;)($=G[H++])in y||u(y,$,x[$]);i||(z.constructor=y)}var J=new g(new y(2)),V=g.prototype.setInt8;J.setInt8(0,2147483648),J.setInt8(1,2147483649),!J.getInt8(0)&&J.getInt8(1)||s(g.prototype,{setInt8:function(t,e){V.call(this,t,e<<24>>24)},setUint8:function(t,e){V.call(this,t,e<<24>>24)}},!0)}else y=function(t){var e=B(this,t);this._b=m.call(Array(e),0),this[P]=e},g=function(t,e,n){f(this,g,"DataView"),f(t,y,"DataView");var r=t[P],o=l(e);if(o<0||o>r)throw w("Wrong offset!");if(n=void 0===n?r-o:p(n),o+n>r)throw w("Wrong length!");this[A]=t,this[I]=o,this[P]=n},o&&(D(y,"byteLength","_l"),D(g,"buffer","_b"),D(g,"byteLength","_l"),D(g,"byteOffset","_o")),s(g.prototype,{getInt8:function(t){return U(this,1,t)[0]<<24>>24},getUint8:function(t){return U(this,1,t)[0]},getInt16:function(t){var e=U(this,2,t,arguments[1]);return(e[1]<<8|e[0])<<16>>16},getUint16:function(t){var e=U(this,2,t,arguments[1]);return e[1]<<8|e[0]},getInt32:function(t){return M(U(this,4,t,arguments[1]))},getUint32:function(t){return M(U(this,4,t,arguments[1]))>>>0},getFloat32:function(t){return N(U(this,4,t,arguments[1]),23,4)},getFloat64:function(t){return N(U(this,8,t,arguments[1]),52,8)},setInt8:function(t,e){W(this,1,t,T,e)},setUint8:function(t,e){W(this,1,t,T,e)},setInt16:function(t,e){W(this,2,t,R,e,arguments[2])},setUint16:function(t,e){W(this,2,t,R,e,arguments[2])},setInt32:function(t,e){W(this,4,t,q,e,arguments[2])},setUint32:function(t,e){W(this,4,t,q,e,arguments[2])},setFloat32:function(t,e){W(this,4,t,C,e,arguments[2])},setFloat64:function(t,e){W(this,8,t,L,e,arguments[2])}});v(y,"ArrayBuffer"),v(g,"DataView"),u(g.prototype,a.VIEW,!0),n.ArrayBuffer=y,n.DataView=g},{106:106,108:108,113:113,28:28,34:34,38:38,40:40,58:58,6:6,67:67,72:72,86:86,9:9,92:92}],113:[function(t,e,n){for(var r,o=t(38),i=t(40),a=t(114),u=a("typed_array"),s=a("view"),c=!(!o.ArrayBuffer||!o.DataView),f=c,l=0,p="Int8Array,Uint8Array,Uint8ClampedArray,Int16Array,Uint16Array,Int32Array,Uint32Array,Float32Array,Float64Array".split(",");l<9;)(r=o[p[l++]])?(i(r.prototype,u,!0),i(r.prototype,s,!0)):f=!1;e.exports={ABV:c,CONSTR:f,TYPED:u,VIEW:s}},{114:114,38:38,40:40}],114:[function(t,e,n){var r=0,o=Math.random();e.exports=function(t){return"Symbol(".concat(void 0===t?"":t,")_",(++r+o).toString(36))}},{}],115:[function(t,e,n){var r=t(38),o=t(23),i=t(58),a=t(116),u=t(67).f;e.exports=function(t){var e=o.Symbol||(o.Symbol=i?{}:r.Symbol||{});"_"==t.charAt(0)||t in e||u(e,t,{value:a.f(t)})}},{116:116,23:23,38:38,58:58,67:67}],116:[function(t,e,n){n.f=t(117)},{117:117}],117:[function(t,e,n){var r=t(94)("wks"),o=t(114),i=t(38).Symbol,a="function"==typeof i;(e.exports=function(t){return r[t]||(r[t]=a&&i[t]||(a?i:o)("Symbol."+t))}).store=r},{114:114,38:38,94:94}],118:[function(t,e,n){var r=t(17),o=t(117)("iterator"),i=t(56);e.exports=t(23).getIteratorMethod=function(t){if(void 0!=t)return t[o]||t["@@iterator"]||i[r(t)]}},{117:117,17:17,23:23,56:56}],119:[function(t,e,n){var r=t(32),o=t(88)(/[\\^$*+?.()|[\]{}]/g,"\\$&");r(r.S,"RegExp",{escape:function(t){return o(t)}})},{32:32,88:88}],120:[function(t,e,n){var r=t(32);r(r.P,"Array",{copyWithin:t(8)}),t(5)("copyWithin")},{32:32,5:5,8:8}],121:[function(t,e,n){"use strict";var r=t(32),o=t(12)(4);r(r.P+r.F*!t(96)([].every,!0),"Array",{every:function(t){return o(this,t,arguments[1])}})},{12:12,32:32,96:96}],122:[function(t,e,n){var r=t(32);r(r.P,"Array",{fill:t(9)}),t(5)("fill")},{32:32,5:5,9:9}],123:[function(t,e,n){"use strict";var r=t(32),o=t(12)(2);r(r.P+r.F*!t(96)([].filter,!0),"Array",{filter:function(t){return o(this,t,arguments[1])}})},{12:12,32:32,96:96}],124:[function(t,e,n){"use strict";var r=t(32),o=t(12)(6),i="findIndex",a=!0;i in[]&&Array(1)[i](function(){a=!1}),r(r.P+r.F*a,"Array",{findIndex:function(t){return o(this,t,arguments.length>1?arguments[1]:void 0)}}),t(5)(i)},{12:12,32:32,5:5}],125:[function(t,e,n){"use strict";var r=t(32),o=t(12)(5),i=!0;"find"in[]&&Array(1).find(function(){i=!1}),r(r.P+r.F*i,"Array",{find:function(t){return o(this,t,arguments.length>1?arguments[1]:void 0)}}),t(5)("find")},{12:12,32:32,5:5}],126:[function(t,e,n){"use strict";var r=t(32),o=t(12)(0),i=t(96)([].forEach,!0);r(r.P+r.F*!i,"Array",{forEach:function(t){return o(this,t,arguments[1])}})},{12:12,32:32,96:96}],127:[function(t,e,n){"use strict";var r=t(25),o=t(32),i=t(109),a=t(51),u=t(46),s=t(108),c=t(24),f=t(118);o(o.S+o.F*!t(54)(function(t){Array.from(t)}),"Array",{from:function(t){var e,n,o,l,p=i(t),h="function"==typeof this?this:Array,d=arguments.length,m=d>1?arguments[1]:void 0,v=void 0!==m,y=0,g=f(p);if(v&&(m=r(m,d>2?arguments[2]:void 0,2)),void 0==g||h==Array&&u(g))for(n=new h(e=s(p.length));e>y;y++)c(n,y,v?m(p[y],y):p[y]);else for(l=g.call(p),n=new h;!(o=l.next()).done;y++)c(n,y,v?a(l,m,[o.value,y],!0):o.value);return n.length=y,n}})},{108:108,109:109,118:118,24:24,25:25,32:32,46:46,51:51,54:54}],128:[function(t,e,n){"use strict";var r=t(32),o=t(11)(!1),i=[].indexOf,a=!!i&&1/[1].indexOf(1,-0)<0;r(r.P+r.F*(a||!t(96)(i)),"Array",{indexOf:function(t){return a?i.apply(this,arguments)||0:o(this,t,arguments[1])}})},{11:11,32:32,96:96}],129:[function(t,e,n){var r=t(32);r(r.S,"Array",{isArray:t(47)})},{32:32,47:47}],130:[function(t,e,n){"use strict";var r=t(5),o=t(55),i=t(56),a=t(107);e.exports=t(53)(Array,"Array",function(t,e){this._t=a(t),this._i=0,this._k=e},function(){var t=this._t,e=this._k,n=this._i++;return!t||n>=t.length?(this._t=void 0,o(1)):"keys"==e?o(0,n):"values"==e?o(0,t[n]):o(0,[n,t[n]])},"values"),i.Arguments=i.Array,r("keys"),r("values"),r("entries")},{107:107,5:5,53:53,55:55,56:56}],131:[function(t,e,n){"use strict";var r=t(32),o=t(107),i=[].join;r(r.P+r.F*(t(45)!=Object||!t(96)(i)),"Array",{join:function(t){return i.call(o(this),void 0===t?",":t)}})},{107:107,32:32,45:45,96:96}],132:[function(t,e,n){"use strict";var r=t(32),o=t(107),i=t(106),a=t(108),u=[].lastIndexOf,s=!!u&&1/[1].lastIndexOf(1,-0)<0;r(r.P+r.F*(s||!t(96)(u)),"Array",{lastIndexOf:function(t){if(s)return u.apply(this,arguments)||0;var e=o(this),n=a(e.length),r=n-1;for(arguments.length>1&&(r=Math.min(r,i(arguments[1]))),r<0&&(r=n+r);r>=0;r--)if(r in e&&e[r]===t)return r||0;return-1}})},{106:106,107:107,108:108,32:32,96:96}],133:[function(t,e,n){"use strict";var r=t(32),o=t(12)(1);r(r.P+r.F*!t(96)([].map,!0),"Array",{map:function(t){return o(this,t,arguments[1])}})},{12:12,32:32,96:96}],134:[function(t,e,n){"use strict";var r=t(32),o=t(24);r(r.S+r.F*t(34)(function(){function t(){}return!(Array.of.call(t)instanceof t)}),"Array",{of:function(){for(var t=0,e=arguments.length,n=new("function"==typeof this?this:Array)(e);e>t;)o(n,t,arguments[t++]);return n.length=e,n}})},{24:24,32:32,34:34}],135:[function(t,e,n){"use strict";var r=t(32),o=t(13);r(r.P+r.F*!t(96)([].reduceRight,!0),"Array",{reduceRight:function(t){return o(this,t,arguments.length,arguments[1],!0)}})},{13:13,32:32,96:96}],136:[function(t,e,n){"use strict";var r=t(32),o=t(13);r(r.P+r.F*!t(96)([].reduce,!0),"Array",{reduce:function(t){return o(this,t,arguments.length,arguments[1],!1)}})},{13:13,32:32,96:96}],137:[function(t,e,n){"use strict";var r=t(32),o=t(41),i=t(18),a=t(105),u=t(108),s=[].slice;r(r.P+r.F*t(34)(function(){o&&s.call(o)}),"Array",{slice:function(t,e){var n=u(this.length),r=i(this);if(e=void 0===e?n:e,"Array"==r)return s.call(this,t,e);for(var o=a(t,n),c=a(e,n),f=u(c-o),l=Array(f),p=0;p<f;p++)l[p]="String"==r?this.charAt(o+p):this[o+p];return l}})},{105:105,108:108,18:18,32:32,34:34,41:41}],138:[function(t,e,n){"use strict";var r=t(32),o=t(12)(3);r(r.P+r.F*!t(96)([].some,!0),"Array",{some:function(t){return o(this,t,arguments[1])}})},{12:12,32:32,96:96}],139:[function(t,e,n){"use strict";var r=t(32),o=t(3),i=t(109),a=t(34),u=[].sort,s=[1,2,3];r(r.P+r.F*(a(function(){s.sort(void 0)})||!a(function(){s.sort(null)})||!t(96)(u)),"Array",{sort:function(t){return void 0===t?u.call(i(this)):u.call(i(this),o(t))}})},{109:109,3:3,32:32,34:34,96:96}],140:[function(t,e,n){t(91)("Array")},{91:91}],141:[function(t,e,n){var r=t(32);r(r.S,"Date",{now:function(){return(new Date).getTime()}})},{32:32}],142:[function(t,e,n){"use strict";var r=t(32),o=t(34),i=Date.prototype.getTime,a=function(t){return t>9?t:"0"+t};r(r.P+r.F*(o(function(){return"0385-07-25T07:06:39.999Z"!=new Date(-5e13-1).toISOString()})||!o(function(){new Date(NaN).toISOString()})),"Date",{toISOString:function(){if(!isFinite(i.call(this)))throw RangeError("Invalid time value");var t=this,e=t.getUTCFullYear(),n=t.getUTCMilliseconds(),r=e<0?"-":e>9999?"+":"";return r+("00000"+Math.abs(e)).slice(r?-6:-4)+"-"+a(t.getUTCMonth()+1)+"-"+a(t.getUTCDate())+"T"+a(t.getUTCHours())+":"+a(t.getUTCMinutes())+":"+a(t.getUTCSeconds())+"."+(n>99?n:"0"+a(n))+"Z"}})},{32:32,34:34}],143:[function(t,e,n){"use strict";var r=t(32),o=t(109),i=t(110);r(r.P+r.F*t(34)(function(){return null!==new Date(NaN).toJSON()||1!==Date.prototype.toJSON.call({toISOString:function(){return 1}})}),"Date",{toJSON:function(t){var e=o(this),n=i(e);return"number"!=typeof n||isFinite(n)?e.toISOString():null}})},{109:109,110:110,32:32,34:34}],144:[function(t,e,n){var r=t(117)("toPrimitive"),o=Date.prototype;r in o||t(40)(o,r,t(26))},{117:117,26:26,40:40}],145:[function(t,e,n){var r=Date.prototype,o=r.toString,i=r.getTime;new Date(NaN)+""!="Invalid Date"&&t(87)(r,"toString",function(){var t=i.call(this);return t===t?o.call(this):"Invalid Date"})},{87:87}],146:[function(t,e,n){var r=t(32);r(r.P,"Function",{bind:t(16)})},{16:16,32:32}],147:[function(t,e,n){"use strict";var r=t(49),o=t(74),i=t(117)("hasInstance"),a=Function.prototype;i in a||t(67).f(a,i,{value:function(t){if("function"!=typeof this||!r(t))return!1;if(!r(this.prototype))return t instanceof this;for(;t=o(t);)if(this.prototype===t)return!0;return!1}})},{117:117,49:49,67:67,74:74}],148:[function(t,e,n){var r=t(67).f,o=t(85),i=t(39),a=Function.prototype,u=/^\s*function ([^ (]*)/,s=Object.isExtensible||function(){return!0};"name"in a||t(28)&&r(a,"name",{configurable:!0,get:function(){try{var t=this,e=(""+t).match(u)[1];return i(t,"name")||!s(t)||r(t,"name",o(5,e)),e}catch(t){return""}}})},{28:28,39:39,67:67,85:85}],149:[function(t,e,n){"use strict";var r=t(19);e.exports=t(22)("Map",function(t){return function(){return t(this,arguments.length>0?arguments[0]:void 0)}},{get:function(t){var e=r.getEntry(this,t);return e&&e.v},set:function(t,e){return r.def(this,0===t?0:t,e)}},r,!0)},{19:19,22:22}],150:[function(t,e,n){var r=t(32),o=t(60),i=Math.sqrt,a=Math.acosh;r(r.S+r.F*!(a&&710==Math.floor(a(Number.MAX_VALUE))&&a(1/0)==1/0),"Math",{acosh:function(t){return(t=+t)<1?NaN:t>94906265.62425156?Math.log(t)+Math.LN2:o(t-1+i(t-1)*i(t+1))}})},{32:32,60:60}],151:[function(t,e,n){function r(t){return isFinite(t=+t)&&0!=t?t<0?-r(-t):Math.log(t+Math.sqrt(t*t+1)):t}var o=t(32),i=Math.asinh;o(o.S+o.F*!(i&&1/i(0)>0),"Math",{asinh:r})},{32:32}],152:[function(t,e,n){var r=t(32),o=Math.atanh;r(r.S+r.F*!(o&&1/o(-0)<0),"Math",{atanh:function(t){return 0==(t=+t)?t:Math.log((1+t)/(1-t))/2}})},{32:32}],153:[function(t,e,n){var r=t(32),o=t(61);r(r.S,"Math",{cbrt:function(t){return o(t=+t)*Math.pow(Math.abs(t),1/3)}})},{32:32,61:61}],154:[function(t,e,n){var r=t(32);r(r.S,"Math",{clz32:function(t){return(t>>>=0)?31-Math.floor(Math.log(t+.5)*Math.LOG2E):32}})},{32:32}],155:[function(t,e,n){var r=t(32),o=Math.exp;r(r.S,"Math",{cosh:function(t){return(o(t=+t)+o(-t))/2}})},{32:32}],156:[function(t,e,n){var r=t(32),o=t(59);r(r.S+r.F*(o!=Math.expm1),"Math",{expm1:o})},{32:32,59:59}],157:[function(t,e,n){var r=t(32),o=t(61),i=Math.pow,a=i(2,-52),u=i(2,-23),s=i(2,127)*(2-u),c=i(2,-126),f=function(t){return t+1/a-1/a};r(r.S,"Math",{fround:function(t){var e,n,r=Math.abs(t),i=o(t);return r<c?i*f(r/c/u)*c*u:(e=(1+u/a)*r,(n=e-(e-r))>s||n!=n?i*(1/0):i*n)}})},{32:32,61:61}],158:[function(t,e,n){var r=t(32),o=Math.abs;r(r.S,"Math",{hypot:function(t,e){for(var n,r,i=0,a=0,u=arguments.length,s=0;a<u;)s<(n=o(arguments[a++]))?(i=i*(r=s/n)*r+1,s=n):i+=n>0?(r=n/s)*r:n;return s===1/0?1/0:s*Math.sqrt(i)}})},{32:32}],159:[function(t,e,n){var r=t(32),o=Math.imul;r(r.S+r.F*t(34)(function(){return-5!=o(4294967295,5)||2!=o.length}),"Math",{imul:function(t,e){var n=+t,r=+e,o=65535&n,i=65535&r;return 0|o*i+((65535&n>>>16)*i+o*(65535&r>>>16)<<16>>>0)}})},{32:32,34:34}],160:[function(t,e,n){var r=t(32);r(r.S,"Math",{log10:function(t){return Math.log(t)/Math.LN10}})},{32:32}],161:[function(t,e,n){var r=t(32);r(r.S,"Math",{log1p:t(60)})},{32:32,60:60}],162:[function(t,e,n){var r=t(32);r(r.S,"Math",{log2:function(t){return Math.log(t)/Math.LN2}})},{32:32}],163:[function(t,e,n){var r=t(32);r(r.S,"Math",{sign:t(61)})},{32:32,61:61}],164:[function(t,e,n){var r=t(32),o=t(59),i=Math.exp;r(r.S+r.F*t(34)(function(){return-2e-17!=!Math.sinh(-2e-17)}),"Math",{sinh:function(t){return Math.abs(t=+t)<1?(o(t)-o(-t))/2:(i(t-1)-i(-t-1))*(Math.E/2)}})},{32:32,34:34,59:59}],165:[function(t,e,n){var r=t(32),o=t(59),i=Math.exp;r(r.S,"Math",{tanh:function(t){var e=o(t=+t),n=o(-t);return e==1/0?1:n==1/0?-1:(e-n)/(i(t)+i(-t))}})},{32:32,59:59}],166:[function(t,e,n){var r=t(32);r(r.S,"Math",{trunc:function(t){return(t>0?Math.floor:Math.ceil)(t)}})},{32:32}],167:[function(t,e,n){"use strict";var r=t(38),o=t(39),i=t(18),a=t(43),u=t(110),s=t(34),c=t(72).f,f=t(70).f,l=t(67).f,p=t(102).trim,h=r.Number,d=h,m=h.prototype,v="Number"==i(t(66)(m)),y="trim"in String.prototype,g=function(t){var e=u(t,!1);if("string"==typeof e&&e.length>2){var n,r,o,i=(e=y?e.trim():p(e,3)).charCodeAt(0);if(43===i||45===i){if(88===(n=e.charCodeAt(2))||120===n)return NaN}else if(48===i){switch(e.charCodeAt(1)){case 66:case 98:r=2,o=49;break;case 79:case 111:r=8,o=55;break;default:return+e}for(var a,s=e.slice(2),c=0,f=s.length;c<f;c++)if((a=s.charCodeAt(c))<48||a>o)return NaN;return parseInt(s,r)}}return+e};if(!h(" 0o1")||!h("0b1")||h("+0x1")){h=function(t){var e=arguments.length<1?0:t,n=this;return n instanceof h&&(v?s(function(){m.valueOf.call(n)}):"Number"!=i(n))?a(new d(g(e)),n,h):g(e)};for(var b,w=t(28)?c(d):"MAX_VALUE,MIN_VALUE,NaN,NEGATIVE_INFINITY,POSITIVE_INFINITY,EPSILON,isFinite,isInteger,isNaN,isSafeInteger,MAX_SAFE_INTEGER,MIN_SAFE_INTEGER,parseFloat,parseInt,isInteger".split(","),_=0;w.length>_;_++)o(d,b=w[_])&&!o(h,b)&&l(h,b,f(d,b));h.prototype=m,m.constructor=h,t(87)(r,"Number",h)}},{102:102,110:110,18:18,28:28,34:34,38:38,39:39,43:43,66:66,67:67,70:70,72:72,87:87}],168:[function(t,e,n){var r=t(32);r(r.S,"Number",{EPSILON:Math.pow(2,-52)})},{32:32}],169:[function(t,e,n){var r=t(32),o=t(38).isFinite;r(r.S,"Number",{isFinite:function(t){return"number"==typeof t&&o(t)}})},{32:32,38:38}],170:[function(t,e,n){var r=t(32);r(r.S,"Number",{isInteger:t(48)})},{32:32,48:48}],171:[function(t,e,n){var r=t(32);r(r.S,"Number",{isNaN:function(t){return t!=t}})},{32:32}],172:[function(t,e,n){var r=t(32),o=t(48),i=Math.abs;r(r.S,"Number",{isSafeInteger:function(t){return o(t)&&i(t)<=9007199254740991}})},{32:32,48:48}],173:[function(t,e,n){var r=t(32);r(r.S,"Number",{MAX_SAFE_INTEGER:9007199254740991})},{32:32}],174:[function(t,e,n){var r=t(32);r(r.S,"Number",{MIN_SAFE_INTEGER:-9007199254740991})},{32:32}],175:[function(t,e,n){var r=t(32),o=t(81);r(r.S+r.F*(Number.parseFloat!=o),"Number",{parseFloat:o})},{32:32,81:81}],176:[function(t,e,n){var r=t(32),o=t(82);r(r.S+r.F*(Number.parseInt!=o),"Number",{parseInt:o})},{32:32,82:82}],177:[function(t,e,n){"use strict";var r=t(32),o=t(106),i=t(4),a=t(101),u=1..toFixed,s=Math.floor,c=[0,0,0,0,0,0],f="Number.toFixed: incorrect invocation!",l=function(t,e){for(var n=-1,r=e;++n<6;)r+=t*c[n],c[n]=r%1e7,r=s(r/1e7)},p=function(t){for(var e=6,n=0;--e>=0;)n+=c[e],c[e]=s(n/t),n=n%t*1e7},h=function(){for(var t=6,e="";--t>=0;)if(""!==e||0===t||0!==c[t]){var n=String(c[t]);e=""===e?n:e+a.call("0",7-n.length)+n}return e},d=function(t,e,n){return 0===e?n:e%2==1?d(t,e-1,n*t):d(t*t,e/2,n)},m=function(t){for(var e=0,n=t;n>=4096;)e+=12,n/=4096;for(;n>=2;)e+=1,n/=2;return e};r(r.P+r.F*(!!u&&("0.000"!==8e-5.toFixed(3)||"1"!==.9.toFixed(0)||"1.25"!==1.255.toFixed(2)||"1000000000000000128"!==(0xde0b6b3a7640080).toFixed(0))||!t(34)(function(){u.call({})})),"Number",{toFixed:function(t){var e,n,r,u,s=i(this,f),c=o(t),v="",y="0";if(c<0||c>20)throw RangeError(f);if(s!=s)return"NaN";if(s<=-1e21||s>=1e21)return String(s);if(s<0&&(v="-",s=-s),s>1e-21)if(e=m(s*d(2,69,1))-69,n=e<0?s*d(2,-e,1):s/d(2,e,1),n*=4503599627370496,(e=52-e)>0){for(l(0,n),r=c;r>=7;)l(1e7,0),r-=7;for(l(d(10,r,1),0),r=e-1;r>=23;)p(1<<23),r-=23;p(1<<r),l(1,1),p(2),y=h()}else l(0,n),l(1<<-e,0),y=h()+a.call("0",c);return y=c>0?v+((u=y.length)<=c?"0."+a.call("0",c-u)+y:y.slice(0,u-c)+"."+y.slice(u-c)):v+y}})},{101:101,106:106,32:32,34:34,4:4}],178:[function(t,e,n){"use strict";var r=t(32),o=t(34),i=t(4),a=1..toPrecision;r(r.P+r.F*(o(function(){return"1"!==a.call(1,void 0)})||!o(function(){a.call({})})),"Number",{toPrecision:function(t){var e=i(this,"Number#toPrecision: incorrect invocation!");return void 0===t?a.call(e):a.call(e,t)}})},{32:32,34:34,4:4}],179:[function(t,e,n){var r=t(32);r(r.S+r.F,"Object",{assign:t(65)})},{32:32,65:65}],180:[function(t,e,n){var r=t(32);r(r.S,"Object",{create:t(66)})},{32:32,66:66}],181:[function(t,e,n){var r=t(32);r(r.S+r.F*!t(28),"Object",{defineProperties:t(68)})},{28:28,32:32,68:68}],182:[function(t,e,n){var r=t(32);r(r.S+r.F*!t(28),"Object",{defineProperty:t(67).f})},{28:28,32:32,67:67}],183:[function(t,e,n){var r=t(49),o=t(62).onFreeze;t(78)("freeze",function(t){return function(e){return t&&r(e)?t(o(e)):e}})},{49:49,62:62,78:78}],184:[function(t,e,n){var r=t(107),o=t(70).f;t(78)("getOwnPropertyDescriptor",function(){return function(t,e){return o(r(t),e)}})},{107:107,70:70,78:78}],185:[function(t,e,n){t(78)("getOwnPropertyNames",function(){return t(71).f})},{71:71,78:78}],186:[function(t,e,n){var r=t(109),o=t(74);t(78)("getPrototypeOf",function(){return function(t){return o(r(t))}})},{109:109,74:74,78:78}],187:[function(t,e,n){var r=t(49);t(78)("isExtensible",function(t){return function(e){return!!r(e)&&(!t||t(e))}})},{49:49,78:78}],188:[function(t,e,n){var r=t(49);t(78)("isFrozen",function(t){return function(e){return!r(e)||!!t&&t(e)}})},{49:49,78:78}],189:[function(t,e,n){var r=t(49);t(78)("isSealed",function(t){return function(e){return!r(e)||!!t&&t(e)}})},{49:49,78:78}],190:[function(t,e,n){var r=t(32);r(r.S,"Object",{is:t(89)})},{32:32,89:89}],191:[function(t,e,n){var r=t(109),o=t(76);t(78)("keys",function(){return function(t){return o(r(t))}})},{109:109,76:76,78:78}],192:[function(t,e,n){var r=t(49),o=t(62).onFreeze;t(78)("preventExtensions",function(t){return function(e){return t&&r(e)?t(o(e)):e}})},{49:49,62:62,78:78}],193:[function(t,e,n){var r=t(49),o=t(62).onFreeze;t(78)("seal",function(t){return function(e){return t&&r(e)?t(o(e)):e}})},{49:49,62:62,78:78}],194:[function(t,e,n){var r=t(32);r(r.S,"Object",{setPrototypeOf:t(90).set})},{32:32,90:90}],195:[function(t,e,n){"use strict";var r=t(17),o={};o[t(117)("toStringTag")]="z",o+""!="[object z]"&&t(87)(Object.prototype,"toString",function(){return"[object "+r(this)+"]"},!0)},{117:117,17:17,87:87}],196:[function(t,e,n){var r=t(32),o=t(81);r(r.G+r.F*(parseFloat!=o),{parseFloat:o})},{32:32,81:81}],197:[function(t,e,n){var r=t(32),o=t(82);r(r.G+r.F*(parseInt!=o),{parseInt:o})},{32:32,82:82}],198:[function(t,e,n){"use strict";var r,o,i,a=t(58),u=t(38),s=t(25),c=t(17),f=t(32),l=t(49),p=t(3),h=t(6),d=t(37),m=t(95),v=t(104).set,y=t(64)(),g=u.TypeError,b=u.process,w=u.Promise,_="process"==c(b=u.process),x=function(){},S=!!function(){try{var e=w.resolve(1),n=(e.constructor={})[t(117)("species")]=function(t){t(x,x)};return(_||"function"==typeof PromiseRejectionEvent)&&e.then(x)instanceof n}catch(t){}}(),k=function(t,e){return t===e||t===w&&e===i},E=function(t){var e;return!(!l(t)||"function"!=typeof(e=t.then))&&e},O=function(t){return k(w,t)?new j(t):new o(t)},j=o=function(t){var e,n;this.promise=new t(function(t,r){if(void 0!==e||void 0!==n)throw g("Bad Promise constructor");e=t,n=r}),this.resolve=p(e),this.reject=p(n)},A=function(t){try{t()}catch(t){return{error:t}}},P=function(t,e){if(!t._n){t._n=!0;var n=t._c;y(function(){for(var r=t._v,o=1==t._s,i=0;n.length>i;)!function(e){var n,i,a=o?e.ok:e.fail,u=e.resolve,s=e.reject,c=e.domain;try{a?(o||(2==t._h&&N(t),t._h=1),!0===a?n=r:(c&&c.enter(),n=a(r),c&&c.exit()),n===e.promise?s(g("Promise-chain cycle")):(i=E(n))?i.call(n,u,s):u(n)):s(r)}catch(t){s(t)}}(n[i++]);t._c=[],t._n=!1,e&&!t._h&&I(t)})}},I=function(t){v.call(u,function(){var e,n,r,o=t._v;if(F(t)&&(e=A(function(){_?b.emit("unhandledRejection",o,t):(n=u.onunhandledrejection)?n({promise:t,reason:o}):(r=u.console)&&r.error&&r.error("Unhandled promise rejection",o)}),t._h=_||F(t)?2:1),t._a=void 0,e)throw e.error})},F=function(t){if(1==t._h)return!1;for(var e,n=t._a||t._c,r=0;n.length>r;)if((e=n[r++]).fail||!F(e.promise))return!1;return!0},N=function(t){v.call(u,function(){var e;_?b.emit("rejectionHandled",t):(e=u.onrejectionhandled)&&e({promise:t,reason:t._v})})},M=function(t){var e=this;e._d||(e._d=!0,(e=e._w||e)._v=t,e._s=2,e._a||(e._a=e._c.slice()),P(e,!0))},T=function(t){var e,n=this;if(!n._d){n._d=!0,n=n._w||n;try{if(n===t)throw g("Promise can't be resolved itself");(e=E(t))?y(function(){var r={_w:n,_d:!1};try{e.call(t,s(T,r,1),s(M,r,1))}catch(t){M.call(r,t)}}):(n._v=t,n._s=1,P(n,!1))}catch(t){M.call({_w:n,_d:!1},t)}}};S||(w=function(t){h(this,w,"Promise","_h"),p(t),r.call(this);try{t(s(T,this,1),s(M,this,1))}catch(t){M.call(this,t)}},(r=function(t){this._c=[],this._a=void 0,this._s=0,this._d=!1,this._v=void 0,this._h=0,this._n=!1}).prototype=t(86)(w.prototype,{then:function(t,e){var n=O(m(this,w));return n.ok="function"!=typeof t||t,n.fail="function"==typeof e&&e,n.domain=_?b.domain:void 0,this._c.push(n),this._a&&this._a.push(n),this._s&&P(this,!1),n.promise},catch:function(t){return this.then(void 0,t)}}),j=function(){var t=new r;this.promise=t,this.resolve=s(T,t,1),this.reject=s(M,t,1)}),f(f.G+f.W+f.F*!S,{Promise:w}),t(92)(w,"Promise"),t(91)("Promise"),i=t(23).Promise,f(f.S+f.F*!S,"Promise",{reject:function(t){var e=O(this);return(0,e.reject)(t),e.promise}}),f(f.S+f.F*(a||!S),"Promise",{resolve:function(t){if(t instanceof w&&k(t.constructor,this))return t;var e=O(this);return(0,e.resolve)(t),e.promise}}),f(f.S+f.F*!(S&&t(54)(function(t){w.all(t).catch(x)})),"Promise",{all:function(t){var e=this,n=O(e),r=n.resolve,o=n.reject,i=A(function(){var n=[],i=0,a=1;d(t,!1,function(t){var u=i++,s=!1;n.push(void 0),a++,e.resolve(t).then(function(t){s||(s=!0,n[u]=t,--a||r(n))},o)}),--a||r(n)});return i&&o(i.error),n.promise},race:function(t){var e=this,n=O(e),r=n.reject,o=A(function(){d(t,!1,function(t){e.resolve(t).then(n.resolve,r)})});return o&&r(o.error),n.promise}})},{104:104,117:117,17:17,23:23,25:25,3:3,32:32,37:37,38:38,49:49,54:54,58:58,6:6,64:64,86:86,91:91,92:92,95:95}],199:[function(t,e,n){var r=t(32),o=t(3),i=t(7),a=(t(38).Reflect||{}).apply,u=Function.apply;r(r.S+r.F*!t(34)(function(){a(function(){})}),"Reflect",{apply:function(t,e,n){var r=o(t),s=i(n);return a?a(r,e,s):u.call(r,e,s)}})},{3:3,32:32,34:34,38:38,7:7}],200:[function(t,e,n){var r=t(32),o=t(66),i=t(3),a=t(7),u=t(49),s=t(34),c=t(16),f=(t(38).Reflect||{}).construct,l=s(function(){function t(){}return!(f(function(){},[],t)instanceof t)}),p=!s(function(){f(function(){})});r(r.S+r.F*(l||p),"Reflect",{construct:function(t,e){i(t),a(e);var n=arguments.length<3?t:i(arguments[2]);if(p&&!l)return f(t,e,n);if(t==n){switch(e.length){case 0:return new t;case 1:return new t(e[0]);case 2:return new t(e[0],e[1]);case 3:return new t(e[0],e[1],e[2]);case 4:return new t(e[0],e[1],e[2],e[3])}var r=[null];return r.push.apply(r,e),new(c.apply(t,r))}var s=n.prototype,h=o(u(s)?s:Object.prototype),d=Function.apply.call(t,h,e);return u(d)?d:h}})},{16:16,3:3,32:32,34:34,38:38,49:49,66:66,7:7}],201:[function(t,e,n){var r=t(67),o=t(32),i=t(7),a=t(110);o(o.S+o.F*t(34)(function(){Reflect.defineProperty(r.f({},1,{value:1}),1,{value:2})}),"Reflect",{defineProperty:function(t,e,n){i(t),e=a(e,!0),i(n);try{return r.f(t,e,n),!0}catch(t){return!1}}})},{110:110,32:32,34:34,67:67,7:7}],202:[function(t,e,n){var r=t(32),o=t(70).f,i=t(7);r(r.S,"Reflect",{deleteProperty:function(t,e){var n=o(i(t),e);return!(n&&!n.configurable)&&delete t[e]}})},{32:32,7:7,70:70}],203:[function(t,e,n){"use strict";var r=t(32),o=t(7),i=function(t){this._t=o(t),this._i=0;var e,n=this._k=[];for(e in t)n.push(e)};t(52)(i,"Object",function(){var t,e=this,n=e._k;do{if(e._i>=n.length)return{value:void 0,done:!0}}while(!((t=n[e._i++])in e._t));return{value:t,done:!1}}),r(r.S,"Reflect",{enumerate:function(t){return new i(t)}})},{32:32,52:52,7:7}],204:[function(t,e,n){var r=t(70),o=t(32),i=t(7);o(o.S,"Reflect",{getOwnPropertyDescriptor:function(t,e){return r.f(i(t),e)}})},{32:32,7:7,70:70}],205:[function(t,e,n){var r=t(32),o=t(74),i=t(7);r(r.S,"Reflect",{getPrototypeOf:function(t){return o(i(t))}})},{32:32,7:7,74:74}],206:[function(t,e,n){function r(t,e){var n,u,f=arguments.length<3?t:arguments[2];return c(t)===f?t[e]:(n=o.f(t,e))?a(n,"value")?n.value:void 0!==n.get?n.get.call(f):void 0:s(u=i(t))?r(u,e,f):void 0}var o=t(70),i=t(74),a=t(39),u=t(32),s=t(49),c=t(7);u(u.S,"Reflect",{get:r})},{32:32,39:39,49:49,7:7,70:70,74:74}],207:[function(t,e,n){var r=t(32);r(r.S,"Reflect",{has:function(t,e){return e in t}})},{32:32}],208:[function(t,e,n){var r=t(32),o=t(7),i=Object.isExtensible;r(r.S,"Reflect",{isExtensible:function(t){return o(t),!i||i(t)}})},{32:32,7:7}],209:[function(t,e,n){var r=t(32);r(r.S,"Reflect",{ownKeys:t(80)})},{32:32,80:80}],210:[function(t,e,n){var r=t(32),o=t(7),i=Object.preventExtensions;r(r.S,"Reflect",{preventExtensions:function(t){o(t);try{return i&&i(t),!0}catch(t){return!1}}})},{32:32,7:7}],211:[function(t,e,n){var r=t(32),o=t(90);o&&r(r.S,"Reflect",{setPrototypeOf:function(t,e){o.check(t,e);try{return o.set(t,e),!0}catch(t){return!1}}})},{32:32,90:90}],212:[function(t,e,n){function r(t,e,n){var s,p,h=arguments.length<4?t:arguments[3],d=i.f(f(t),e);if(!d){if(l(p=a(t)))return r(p,e,n,h);d=c(0)}return u(d,"value")?!(!1===d.writable||!l(h))&&(s=i.f(h,e)||c(0),s.value=n,o.f(h,e,s),!0):void 0!==d.set&&(d.set.call(h,n),!0)}var o=t(67),i=t(70),a=t(74),u=t(39),s=t(32),c=t(85),f=t(7),l=t(49);s(s.S,"Reflect",{set:r})},{32:32,39:39,49:49,67:67,7:7,70:70,74:74,85:85}],213:[function(t,e,n){var r=t(38),o=t(43),i=t(67).f,a=t(72).f,u=t(50),s=t(36),c=r.RegExp,f=c,l=c.prototype,p=/a/g,h=/a/g,d=new c(p)!==p;if(t(28)&&(!d||t(34)(function(){return h[t(117)("match")]=!1,c(p)!=p||c(h)==h||"/a/i"!=c(p,"i")}))){c=function(t,e){var n=this instanceof c,r=u(t),i=void 0===e;return!n&&r&&t.constructor===c&&i?t:o(d?new f(r&&!i?t.source:t,e):f((r=t instanceof c)?t.source:t,r&&i?s.call(t):e),n?this:l,c)};for(var m=a(f),v=0;m.length>v;)!function(t){t in c||i(c,t,{configurable:!0,get:function(){return f[t]},set:function(e){f[t]=e}})}(m[v++]);l.constructor=c,c.prototype=l,t(87)(r,"RegExp",c)}t(91)("RegExp")},{117:117,28:28,34:34,36:36,38:38,43:43,50:50,67:67,72:72,87:87,91:91}],214:[function(t,e,n){t(28)&&"g"!=/./g.flags&&t(67).f(RegExp.prototype,"flags",{configurable:!0,get:t(36)})},{28:28,36:36,67:67}],215:[function(t,e,n){t(35)("match",1,function(t,e,n){return[function(n){"use strict";var r=t(this),o=void 0==n?void 0:n[e];return void 0!==o?o.call(n,r):new RegExp(n)[e](String(r))},n]})},{35:35}],216:[function(t,e,n){t(35)("replace",2,function(t,e,n){return[function(r,o){"use strict";var i=t(this),a=void 0==r?void 0:r[e];return void 0!==a?a.call(r,i,o):n.call(String(i),r,o)},n]})},{35:35}],217:[function(t,e,n){t(35)("search",1,function(t,e,n){return[function(n){"use strict";var r=t(this),o=void 0==n?void 0:n[e];return void 0!==o?o.call(n,r):new RegExp(n)[e](String(r))},n]})},{35:35}],218:[function(t,e,n){t(35)("split",2,function(e,n,r){"use strict";var o=t(50),i=r,a=[].push,u="length";if("c"=="abbc".split(/(b)*/)[1]||4!="test".split(/(?:)/,-1)[u]||2!="ab".split(/(?:ab)*/)[u]||4!=".".split(/(.?)(.?)/)[u]||".".split(/()()/)[u]>1||"".split(/.?/)[u]){var s=void 0===/()??/.exec("")[1];r=function(t,e){var n=String(this);if(void 0===t&&0===e)return[];if(!o(t))return i.call(n,t,e);var r,c,f,l,p,h=[],d=(t.ignoreCase?"i":"")+(t.multiline?"m":"")+(t.unicode?"u":"")+(t.sticky?"y":""),m=0,v=void 0===e?4294967295:e>>>0,y=new RegExp(t.source,d+"g");for(s||(r=new RegExp("^"+y.source+"$(?!\\s)",d));(c=y.exec(n))&&!((f=c.index+c[0][u])>m&&(h.push(n.slice(m,c.index)),!s&&c[u]>1&&c[0].replace(r,function(){for(p=1;p<arguments[u]-2;p++)void 0===arguments[p]&&(c[p]=void 0)}),c[u]>1&&c.index<n[u]&&a.apply(h,c.slice(1)),l=c[0][u],m=f,h[u]>=v));)y.lastIndex===c.index&&y.lastIndex++;return m===n[u]?!l&&y.test("")||h.push(""):h.push(n.slice(m)),h[u]>v?h.slice(0,v):h}}else"0".split(void 0,0)[u]&&(r=function(t,e){return void 0===t&&0===e?[]:i.call(this,t,e)});return[function(t,o){var i=e(this),a=void 0==t?void 0:t[n];return void 0!==a?a.call(t,i,o):r.call(String(i),t,o)},r]})},{35:35,50:50}],219:[function(t,e,n){"use strict";t(214);var r=t(7),o=t(36),i=t(28),a=/./.toString,u=function(e){t(87)(RegExp.prototype,"toString",e,!0)};t(34)(function(){return"/a/b"!=a.call({source:"a",flags:"b"})})?u(function(){var t=r(this);return"/".concat(t.source,"/","flags"in t?t.flags:!i&&t instanceof RegExp?o.call(t):void 0)}):"toString"!=a.name&&u(function(){return a.call(this)})},{214:214,28:28,34:34,36:36,7:7,87:87}],220:[function(t,e,n){"use strict";var r=t(19);e.exports=t(22)("Set",function(t){return function(){return t(this,arguments.length>0?arguments[0]:void 0)}},{add:function(t){return r.def(this,t=0===t?0:t,t)}},r)},{19:19,22:22}],221:[function(t,e,n){"use strict";t(99)("anchor",function(t){return function(e){return t(this,"a","name",e)}})},{99:99}],222:[function(t,e,n){"use strict";t(99)("big",function(t){return function(){return t(this,"big","","")}})},{99:99}],223:[function(t,e,n){"use strict";t(99)("blink",function(t){return function(){return t(this,"blink","","")}})},{99:99}],224:[function(t,e,n){"use strict";t(99)("bold",function(t){return function(){return t(this,"b","","")}})},{99:99}],225:[function(t,e,n){"use strict";var r=t(32),o=t(97)(!1);r(r.P,"String",{codePointAt:function(t){return o(this,t)}})},{32:32,97:97}],226:[function(t,e,n){"use strict";var r=t(32),o=t(108),i=t(98),a="".endsWith;r(r.P+r.F*t(33)("endsWith"),"String",{endsWith:function(t){var e=i(this,t,"endsWith"),n=arguments.length>1?arguments[1]:void 0,r=o(e.length),u=void 0===n?r:Math.min(o(n),r),s=String(t);return a?a.call(e,s,u):e.slice(u-s.length,u)===s}})},{108:108,32:32,33:33,98:98}],227:[function(t,e,n){"use strict";t(99)("fixed",function(t){return function(){return t(this,"tt","","")}})},{99:99}],228:[function(t,e,n){"use strict";t(99)("fontcolor",function(t){return function(e){return t(this,"font","color",e)}})},{99:99}],229:[function(t,e,n){"use strict";t(99)("fontsize",function(t){return function(e){return t(this,"font","size",e)}})},{99:99}],230:[function(t,e,n){var r=t(32),o=t(105),i=String.fromCharCode,a=String.fromCodePoint;r(r.S+r.F*(!!a&&1!=a.length),"String",{fromCodePoint:function(t){for(var e,n=[],r=arguments.length,a=0;r>a;){if(e=+arguments[a++],o(e,1114111)!==e)throw RangeError(e+" is not a valid code point");n.push(e<65536?i(e):i(55296+((e-=65536)>>10),e%1024+56320))}return n.join("")}})},{105:105,32:32}],231:[function(t,e,n){"use strict";var r=t(32),o=t(98);r(r.P+r.F*t(33)("includes"),"String",{includes:function(t){return!!~o(this,t,"includes").indexOf(t,arguments.length>1?arguments[1]:void 0)}})},{32:32,33:33,98:98}],232:[function(t,e,n){"use strict";t(99)("italics",function(t){return function(){return t(this,"i","","")}})},{99:99}],233:[function(t,e,n){"use strict";var r=t(97)(!0);t(53)(String,"String",function(t){this._t=String(t),this._i=0},function(){var t,e=this._t,n=this._i;return n>=e.length?{value:void 0,done:!0}:(t=r(e,n),this._i+=t.length,{value:t,done:!1})})},{53:53,97:97}],234:[function(t,e,n){"use strict";t(99)("link",function(t){return function(e){return t(this,"a","href",e)}})},{99:99}],235:[function(t,e,n){var r=t(32),o=t(107),i=t(108);r(r.S,"String",{raw:function(t){for(var e=o(t.raw),n=i(e.length),r=arguments.length,a=[],u=0;n>u;)a.push(String(e[u++])),u<r&&a.push(String(arguments[u]));return a.join("")}})},{107:107,108:108,32:32}],236:[function(t,e,n){var r=t(32);r(r.P,"String",{repeat:t(101)})},{101:101,32:32}],237:[function(t,e,n){"use strict";t(99)("small",function(t){return function(){return t(this,"small","","")}})},{99:99}],238:[function(t,e,n){"use strict";var r=t(32),o=t(108),i=t(98),a="".startsWith;r(r.P+r.F*t(33)("startsWith"),"String",{startsWith:function(t){var e=i(this,t,"startsWith"),n=o(Math.min(arguments.length>1?arguments[1]:void 0,e.length)),r=String(t);return a?a.call(e,r,n):e.slice(n,n+r.length)===r}})},{108:108,32:32,33:33,98:98}],239:[function(t,e,n){"use strict";t(99)("strike",function(t){return function(){return t(this,"strike","","")}})},{99:99}],240:[function(t,e,n){"use strict";t(99)("sub",function(t){return function(){return t(this,"sub","","")}})},{99:99}],241:[function(t,e,n){"use strict";t(99)("sup",function(t){return function(){return t(this,"sup","","")}})},{99:99}],242:[function(t,e,n){"use strict";t(102)("trim",function(t){return function(){return t(this,3)}})},{102:102}],243:[function(t,e,n){"use strict";var r=t(38),o=t(39),i=t(28),a=t(32),u=t(87),s=t(62).KEY,c=t(34),f=t(94),l=t(92),p=t(114),h=t(117),d=t(116),m=t(115),v=t(57),y=t(31),g=t(47),b=t(7),w=t(107),_=t(110),x=t(85),S=t(66),k=t(71),E=t(70),O=t(67),j=t(76),A=E.f,P=O.f,I=k.f,F=r.Symbol,N=r.JSON,M=N&&N.stringify,T=h("_hidden"),R=h("toPrimitive"),q={}.propertyIsEnumerable,L=f("symbol-registry"),C=f("symbols"),D=f("op-symbols"),U=Object.prototype,W="function"==typeof F,B=r.QObject,$=!B||!B.prototype||!B.prototype.findChild,z=i&&c(function(){return 7!=S(P({},"a",{get:function(){return P(this,"a",{value:7}).a}})).a})?function(t,e,n){var r=A(U,e);r&&delete U[e],P(t,e,n),r&&t!==U&&P(U,e,r)}:P,G=function(t){var e=C[t]=S(F.prototype);return e._k=t,e},H=W&&"symbol"==typeof F.iterator?function(t){return"symbol"==typeof t}:function(t){return t instanceof F},J=function(t,e,n){return t===U&&J(D,e,n),b(t),e=_(e,!0),b(n),o(C,e)?(n.enumerable?(o(t,T)&&t[T][e]&&(t[T][e]=!1),n=S(n,{enumerable:x(0,!1)})):(o(t,T)||P(t,T,x(1,{})),t[T][e]=!0),z(t,e,n)):P(t,e,n)},V=function(t,e){b(t);for(var n,r=y(e=w(e)),o=0,i=r.length;i>o;)J(t,n=r[o++],e[n]);return t},Y=function(t){var e=q.call(this,t=_(t,!0));return!(this===U&&o(C,t)&&!o(D,t))&&(!(e||!o(this,t)||!o(C,t)||o(this,T)&&this[T][t])||e)},K=function(t,e){if(t=w(t),e=_(e,!0),t!==U||!o(C,e)||o(D,e)){var n=A(t,e);return!n||!o(C,e)||o(t,T)&&t[T][e]||(n.enumerable=!0),n}},X=function(t){for(var e,n=I(w(t)),r=[],i=0;n.length>i;)o(C,e=n[i++])||e==T||e==s||r.push(e);return r},Z=function(t){for(var e,n=t===U,r=I(n?D:w(t)),i=[],a=0;r.length>a;)!o(C,e=r[a++])||n&&!o(U,e)||i.push(C[e]);return i};W||(u((F=function(){if(this instanceof F)throw TypeError("Symbol is not a constructor!");var t=p(arguments.length>0?arguments[0]:void 0),e=function(n){this===U&&e.call(D,n),o(this,T)&&o(this[T],t)&&(this[T][t]=!1),z(this,t,x(1,n))};return i&&$&&z(U,t,{configurable:!0,set:e}),G(t)}).prototype,"toString",function(){return this._k}),E.f=K,O.f=J,t(72).f=k.f=X,t(77).f=Y,t(73).f=Z,i&&!t(58)&&u(U,"propertyIsEnumerable",Y,!0),d.f=function(t){return G(h(t))}),a(a.G+a.W+a.F*!W,{Symbol:F});for(var Q="hasInstance,isConcatSpreadable,iterator,match,replace,search,species,split,toPrimitive,toStringTag,unscopables".split(","),tt=0;Q.length>tt;)h(Q[tt++]);for(var Q=j(h.store),tt=0;Q.length>tt;)m(Q[tt++]);a(a.S+a.F*!W,"Symbol",{for:function(t){return o(L,t+="")?L[t]:L[t]=F(t)},keyFor:function(t){if(H(t))return v(L,t);throw TypeError(t+" is not a symbol!")},useSetter:function(){$=!0},useSimple:function(){$=!1}}),a(a.S+a.F*!W,"Object",{create:function(t,e){return void 0===e?S(t):V(S(t),e)},defineProperty:J,defineProperties:V,getOwnPropertyDescriptor:K,getOwnPropertyNames:X,getOwnPropertySymbols:Z}),N&&a(a.S+a.F*(!W||c(function(){var t=F();return"[null]"!=M([t])||"{}"!=M({a:t})||"{}"!=M(Object(t))})),"JSON",{stringify:function(t){if(void 0!==t&&!H(t)){for(var e,n,r=[t],o=1;arguments.length>o;)r.push(arguments[o++]);return"function"==typeof(e=r[1])&&(n=e),!n&&g(e)||(e=function(t,e){if(n&&(e=n.call(this,t,e)),!H(e))return e}),r[1]=e,M.apply(N,r)}}}),F.prototype[R]||t(40)(F.prototype,R,F.prototype.valueOf),l(F,"Symbol"),l(Math,"Math",!0),l(r.JSON,"JSON",!0)},{107:107,110:110,114:114,115:115,116:116,117:117,28:28,31:31,32:32,34:34,38:38,39:39,40:40,47:47,57:57,58:58,62:62,66:66,67:67,7:7,70:70,71:71,72:72,73:73,76:76,77:77,85:85,87:87,92:92,94:94}],244:[function(t,e,n){"use strict";var r=t(32),o=t(113),i=t(112),a=t(7),u=t(105),s=t(108),c=t(49),f=t(38).ArrayBuffer,l=t(95),p=i.ArrayBuffer,h=i.DataView,d=o.ABV&&f.isView,m=p.prototype.slice,v=o.VIEW;r(r.G+r.W+r.F*(f!==p),{ArrayBuffer:p}),r(r.S+r.F*!o.CONSTR,"ArrayBuffer",{isView:function(t){return d&&d(t)||c(t)&&v in t}}),r(r.P+r.U+r.F*t(34)(function(){return!new p(2).slice(1,void 0).byteLength}),"ArrayBuffer",{slice:function(t,e){if(void 0!==m&&void 0===e)return m.call(a(this),t);for(var n=a(this).byteLength,r=u(t,n),o=u(void 0===e?n:e,n),i=new(l(this,p))(s(o-r)),c=new h(this),f=new h(i),d=0;r<o;)f.setUint8(d++,c.getUint8(r++));return i}}),t(91)("ArrayBuffer")},{105:105,108:108,112:112,113:113,32:32,34:34,38:38,49:49,7:7,91:91,95:95}],245:[function(t,e,n){var r=t(32);r(r.G+r.W+r.F*!t(113).ABV,{DataView:t(112).DataView})},{112:112,113:113,32:32}],246:[function(t,e,n){t(111)("Float32",4,function(t){return function(e,n,r){return t(this,e,n,r)}})},{111:111}],247:[function(t,e,n){t(111)("Float64",8,function(t){return function(e,n,r){return t(this,e,n,r)}})},{111:111}],248:[function(t,e,n){t(111)("Int16",2,function(t){return function(e,n,r){return t(this,e,n,r)}})},{111:111}],249:[function(t,e,n){t(111)("Int32",4,function(t){return function(e,n,r){return t(this,e,n,r)}})},{111:111}],250:[function(t,e,n){t(111)("Int8",1,function(t){return function(e,n,r){return t(this,e,n,r)}})},{111:111}],251:[function(t,e,n){t(111)("Uint16",2,function(t){return function(e,n,r){return t(this,e,n,r)}})},{111:111}],252:[function(t,e,n){t(111)("Uint32",4,function(t){return function(e,n,r){return t(this,e,n,r)}})},{111:111}],253:[function(t,e,n){t(111)("Uint8",1,function(t){return function(e,n,r){return t(this,e,n,r)}})},{111:111}],254:[function(t,e,n){t(111)("Uint8",1,function(t){return function(e,n,r){return t(this,e,n,r)}},!0)},{111:111}],255:[function(t,e,n){"use strict";var r,o=t(12)(0),i=t(87),a=t(62),u=t(65),s=t(21),c=t(49),f=a.getWeak,l=Object.isExtensible,p=s.ufstore,h={},d=function(t){return function(){return t(this,arguments.length>0?arguments[0]:void 0)}},m={get:function(t){if(c(t)){var e=f(t);return!0===e?p(this).get(t):e?e[this._i]:void 0}},set:function(t,e){return s.def(this,t,e)}},v=e.exports=t(22)("WeakMap",d,m,s,!0,!0);7!=(new v).set((Object.freeze||Object)(h),7).get(h)&&(u((r=s.getConstructor(d)).prototype,m),a.NEED=!0,o(["delete","has","get","set"],function(t){var e=v.prototype,n=e[t];i(e,t,function(e,o){if(c(e)&&!l(e)){this._f||(this._f=new r);var i=this._f[t](e,o);return"set"==t?this:i}return n.call(this,e,o)})}))},{12:12,21:21,22:22,49:49,62:62,65:65,87:87}],256:[function(t,e,n){"use strict";var r=t(21);t(22)("WeakSet",function(t){return function(){return t(this,arguments.length>0?arguments[0]:void 0)}},{add:function(t){return r.def(this,t,!0)}},r,!1,!0)},{21:21,22:22}],257:[function(t,e,n){"use strict";var r=t(32),o=t(11)(!0);r(r.P,"Array",{includes:function(t){return o(this,t,arguments.length>1?arguments[1]:void 0)}}),t(5)("includes")},{11:11,32:32,5:5}],258:[function(t,e,n){var r=t(32),o=t(64)(),i=t(38).process,a="process"==t(18)(i);r(r.G,{asap:function(t){var e=a&&i.domain;o(e?e.bind(t):t)}})},{18:18,32:32,38:38,64:64}],259:[function(t,e,n){var r=t(32),o=t(18);r(r.S,"Error",{isError:function(t){return"Error"===o(t)}})},{18:18,32:32}],260:[function(t,e,n){var r=t(32);r(r.P+r.R,"Map",{toJSON:t(20)("Map")})},{20:20,32:32}],261:[function(t,e,n){var r=t(32);r(r.S,"Math",{iaddh:function(t,e,n,r){var o=t>>>0,i=n>>>0;return(e>>>0)+(r>>>0)+((o&i|(o|i)&~(o+i>>>0))>>>31)|0}})},{32:32}],262:[function(t,e,n){var r=t(32);r(r.S,"Math",{imulh:function(t,e){var n=+t,r=+e,o=65535&n,i=65535&r,a=n>>16,u=r>>16,s=(a*i>>>0)+(o*i>>>16);return a*u+(s>>16)+((o*u>>>0)+(65535&s)>>16)}})},{32:32}],263:[function(t,e,n){var r=t(32);r(r.S,"Math",{isubh:function(t,e,n,r){var o=t>>>0,i=n>>>0;return(e>>>0)-(r>>>0)-((~o&i|~(o^i)&o-i>>>0)>>>31)|0}})},{32:32}],264:[function(t,e,n){var r=t(32);r(r.S,"Math",{umulh:function(t,e){var n=+t,r=+e,o=65535&n,i=65535&r,a=n>>>16,u=r>>>16,s=(a*i>>>0)+(o*i>>>16);return a*u+(s>>>16)+((o*u>>>0)+(65535&s)>>>16)}})},{32:32}],265:[function(t,e,n){"use strict";var r=t(32),o=t(109),i=t(3),a=t(67);t(28)&&r(r.P+t(69),"Object",{__defineGetter__:function(t,e){a.f(o(this),t,{get:i(e),enumerable:!0,configurable:!0})}})},{109:109,28:28,3:3,32:32,67:67,69:69}],266:[function(t,e,n){"use strict";var r=t(32),o=t(109),i=t(3),a=t(67);t(28)&&r(r.P+t(69),"Object",{__defineSetter__:function(t,e){a.f(o(this),t,{set:i(e),enumerable:!0,configurable:!0})}})},{109:109,28:28,3:3,32:32,67:67,69:69}],267:[function(t,e,n){var r=t(32),o=t(79)(!0);r(r.S,"Object",{entries:function(t){return o(t)}})},{32:32,79:79}],268:[function(t,e,n){var r=t(32),o=t(80),i=t(107),a=t(70),u=t(24);r(r.S,"Object",{getOwnPropertyDescriptors:function(t){for(var e,n=i(t),r=a.f,s=o(n),c={},f=0;s.length>f;)u(c,e=s[f++],r(n,e));return c}})},{107:107,24:24,32:32,70:70,80:80}],269:[function(t,e,n){"use strict";var r=t(32),o=t(109),i=t(110),a=t(74),u=t(70).f;t(28)&&r(r.P+t(69),"Object",{__lookupGetter__:function(t){var e,n=o(this),r=i(t,!0);do{if(e=u(n,r))return e.get}while(n=a(n))}})},{109:109,110:110,28:28,32:32,69:69,70:70,74:74}],270:[function(t,e,n){"use strict";var r=t(32),o=t(109),i=t(110),a=t(74),u=t(70).f;t(28)&&r(r.P+t(69),"Object",{__lookupSetter__:function(t){var e,n=o(this),r=i(t,!0);do{if(e=u(n,r))return e.set}while(n=a(n))}})},{109:109,110:110,28:28,32:32,69:69,70:70,74:74}],271:[function(t,e,n){var r=t(32),o=t(79)(!1);r(r.S,"Object",{values:function(t){return o(t)}})},{32:32,79:79}],272:[function(t,e,n){"use strict";var r=t(32),o=t(38),i=t(23),a=t(64)(),u=t(117)("observable"),s=t(3),c=t(7),f=t(6),l=t(86),p=t(40),h=t(37),d=h.RETURN,m=function(t){return null==t?void 0:s(t)},v=function(t){var e=t._c;e&&(t._c=void 0,e())},y=function(t){return void 0===t._o},g=function(t){y(t)||(t._o=void 0,v(t))},b=function(t,e){c(t),this._c=void 0,this._o=t,t=new w(this);try{var n=e(t),r=n;null!=n&&("function"==typeof n.unsubscribe?n=function(){r.unsubscribe()}:s(n),this._c=n)}catch(e){return void t.error(e)}y(this)&&v(this)};b.prototype=l({},{unsubscribe:function(){g(this)}});var w=function(t){this._s=t};w.prototype=l({},{next:function(t){var e=this._s;if(!y(e)){var n=e._o;try{var r=m(n.next);if(r)return r.call(n,t)}catch(t){try{g(e)}finally{throw t}}}},error:function(t){var e=this._s;if(y(e))throw t;var n=e._o;e._o=void 0;try{var r=m(n.error);if(!r)throw t;t=r.call(n,t)}catch(t){try{v(e)}finally{throw t}}return v(e),t},complete:function(t){var e=this._s;if(!y(e)){var n=e._o;e._o=void 0;try{var r=m(n.complete);t=r?r.call(n,t):void 0}catch(t){try{v(e)}finally{throw t}}return v(e),t}}});var _=function(t){f(this,_,"Observable","_f")._f=s(t)};l(_.prototype,{subscribe:function(t){return new b(t,this._f)},forEach:function(t){var e=this;return new(i.Promise||o.Promise)(function(n,r){s(t);var o=e.subscribe({next:function(e){try{return t(e)}catch(t){r(t),o.unsubscribe()}},error:r,complete:n})})}}),l(_,{from:function(t){var e="function"==typeof this?this:_,n=m(c(t)[u]);if(n){var r=c(n.call(t));return r.constructor===e?r:new e(function(t){return r.subscribe(t)})}return new e(function(e){var n=!1;return a(function(){if(!n){try{if(h(t,!1,function(t){if(e.next(t),n)return d})===d)return}catch(t){if(n)throw t;return void e.error(t)}e.complete()}}),function(){n=!0}})},of:function(){for(var t=0,e=arguments.length,n=Array(e);t<e;)n[t]=arguments[t++];return new("function"==typeof this?this:_)(function(t){var e=!1;return a(function(){if(!e){for(var r=0;r<n.length;++r)if(t.next(n[r]),e)return;t.complete()}}),function(){e=!0}})}}),p(_.prototype,u,function(){return this}),r(r.G,{Observable:_}),t(91)("Observable")},{117:117,23:23,3:3,32:32,37:37,38:38,40:40,6:6,64:64,7:7,86:86,91:91}],273:[function(t,e,n){var r=t(63),o=t(7),i=r.key,a=r.set;r.exp({defineMetadata:function(t,e,n,r){a(t,e,o(n),i(r))}})},{63:63,7:7}],274:[function(t,e,n){var r=t(63),o=t(7),i=r.key,a=r.map,u=r.store;r.exp({deleteMetadata:function(t,e){var n=arguments.length<3?void 0:i(arguments[2]),r=a(o(e),n,!1);if(void 0===r||!r.delete(t))return!1;if(r.size)return!0;var s=u.get(e);return s.delete(n),!!s.size||u.delete(e)}})},{63:63,7:7}],275:[function(t,e,n){var r=t(220),o=t(10),i=t(63),a=t(7),u=t(74),s=i.keys,c=i.key,f=function(t,e){var n=s(t,e),i=u(t);if(null===i)return n;var a=f(i,e);return a.length?n.length?o(new r(n.concat(a))):a:n};i.exp({getMetadataKeys:function(t){return f(a(t),arguments.length<2?void 0:c(arguments[1]))}})},{10:10,220:220,63:63,7:7,74:74}],276:[function(t,e,n){var r=t(63),o=t(7),i=t(74),a=r.has,u=r.get,s=r.key,c=function(t,e,n){if(a(t,e,n))return u(t,e,n);var r=i(e);return null!==r?c(t,r,n):void 0};r.exp({getMetadata:function(t,e){return c(t,o(e),arguments.length<3?void 0:s(arguments[2]))}})},{63:63,7:7,74:74}],277:[function(t,e,n){var r=t(63),o=t(7),i=r.keys,a=r.key;r.exp({getOwnMetadataKeys:function(t){return i(o(t),arguments.length<2?void 0:a(arguments[1]))}})},{63:63,7:7}],278:[function(t,e,n){var r=t(63),o=t(7),i=r.get,a=r.key;r.exp({getOwnMetadata:function(t,e){return i(t,o(e),arguments.length<3?void 0:a(arguments[2]))}})},{63:63,7:7}],279:[function(t,e,n){var r=t(63),o=t(7),i=t(74),a=r.has,u=r.key,s=function(t,e,n){if(a(t,e,n))return!0;var r=i(e);return null!==r&&s(t,r,n)};r.exp({hasMetadata:function(t,e){return s(t,o(e),arguments.length<3?void 0:u(arguments[2]))}})},{63:63,7:7,74:74}],280:[function(t,e,n){var r=t(63),o=t(7),i=r.has,a=r.key;r.exp({hasOwnMetadata:function(t,e){return i(t,o(e),arguments.length<3?void 0:a(arguments[2]))}})},{63:63,7:7}],281:[function(t,e,n){var r=t(63),o=t(7),i=t(3),a=r.key,u=r.set;r.exp({metadata:function(t,e){return function(n,r){u(t,e,(void 0!==r?o:i)(n),a(r))}}})},{3:3,63:63,7:7}],282:[function(t,e,n){var r=t(32);r(r.P+r.R,"Set",{toJSON:t(20)("Set")})},{20:20,32:32}],283:[function(t,e,n){"use strict";var r=t(32),o=t(97)(!0);r(r.P,"String",{at:function(t){return o(this,t)}})},{32:32,97:97}],284:[function(t,e,n){"use strict";var r=t(32),o=t(27),i=t(108),a=t(50),u=t(36),s=RegExp.prototype,c=function(t,e){this._r=t,this._s=e};t(52)(c,"RegExp String",function(){var t=this._r.exec(this._s);return{value:t,done:null===t}}),r(r.P,"String",{matchAll:function(t){if(o(this),!a(t))throw TypeError(t+" is not a regexp!");var e=String(this),n="flags"in s?String(t.flags):u.call(t),r=new RegExp(t.source,~n.indexOf("g")?n:"g"+n);return r.lastIndex=i(t.lastIndex),new c(r,e)}})},{108:108,27:27,32:32,36:36,50:50,52:52}],285:[function(t,e,n){"use strict";var r=t(32),o=t(100);r(r.P,"String",{padEnd:function(t){return o(this,t,arguments.length>1?arguments[1]:void 0,!1)}})},{100:100,32:32}],286:[function(t,e,n){"use strict";var r=t(32),o=t(100);r(r.P,"String",{padStart:function(t){return o(this,t,arguments.length>1?arguments[1]:void 0,!0)}})},{100:100,32:32}],287:[function(t,e,n){"use strict";t(102)("trimLeft",function(t){return function(){return t(this,1)}},"trimStart")},{102:102}],288:[function(t,e,n){"use strict";t(102)("trimRight",function(t){return function(){return t(this,2)}},"trimEnd")},{102:102}],289:[function(t,e,n){t(115)("asyncIterator")},{115:115}],290:[function(t,e,n){t(115)("observable")},{115:115}],291:[function(t,e,n){var r=t(32);r(r.S,"System",{global:t(38)})},{32:32,38:38}],292:[function(t,e,n){for(var r=t(130),o=t(87),i=t(38),a=t(40),u=t(56),s=t(117),c=s("iterator"),f=s("toStringTag"),l=u.Array,p=["NodeList","DOMTokenList","MediaList","StyleSheetList","CSSRuleList"],h=0;h<5;h++){var d,m=p[h],v=i[m],y=v&&v.prototype;if(y){y[c]||a(y,c,l),y[f]||a(y,f,m),u[m]=l;for(d in r)y[d]||o(y,d,r[d],!0)}}},{117:117,130:130,38:38,40:40,56:56,87:87}],293:[function(t,e,n){var r=t(32),o=t(104);r(r.G+r.B,{setImmediate:o.set,clearImmediate:o.clear})},{104:104,32:32}],294:[function(t,e,n){var r=t(38),o=t(32),i=t(44),a=t(83),u=r.navigator,s=!!u&&/MSIE .\./.test(u.userAgent),c=function(t){return s?function(e,n){return t(i(a,[].slice.call(arguments,2),"function"==typeof e?e:Function(e)),n)}:t};o(o.G+o.B+o.F*s,{setTimeout:c(r.setTimeout),setInterval:c(r.setInterval)})},{32:32,38:38,44:44,83:83}],295:[function(t,e,n){t(243),t(180),t(182),t(181),t(184),t(186),t(191),t(185),t(183),t(193),t(192),t(188),t(189),t(187),t(179),t(190),t(194),t(195),t(146),t(148),t(147),t(197),t(196),t(167),t(177),t(178),t(168),t(169),t(170),t(171),t(172),t(173),t(174),t(175),t(176),t(150),t(151),t(152),t(153),t(154),t(155),t(156),t(157),t(158),t(159),t(160),t(161),t(162),t(163),t(164),t(165),t(166),t(230),t(235),t(242),t(233),t(225),t(226),t(231),t(236),t(238),t(221),t(222),t(223),t(224),t(227),t(228),t(229),t(232),t(234),t(237),t(239),t(240),t(241),t(141),t(143),t(142),t(145),t(144),t(129),t(127),t(134),t(131),t(137),t(139),t(126),t(133),t(123),t(138),t(121),t(136),t(135),t(128),t(132),t(120),t(122),t(125),t(124),t(140),t(130),t(213),t(219),t(214),t(215),t(216),t(217),t(218),t(198),t(149),t(220),t(255),t(256),t(244),t(245),t(250),t(253),t(254),t(248),t(251),t(249),t(252),t(246),t(247),t(199),t(200),t(201),t(202),t(203),t(206),t(204),t(205),t(207),t(208),t(209),t(210),t(212),t(211),t(257),t(283),t(286),t(285),t(287),t(288),t(284),t(289),t(290),t(268),t(271),t(267),t(265),t(266),t(269),t(270),t(260),t(282),t(291),t(259),t(261),t(263),t(262),t(264),t(273),t(274),t(276),t(275),t(278),t(277),t(279),t(280),t(281),t(258),t(272),t(294),t(293),t(292),e.exports=t(23)},{120:120,121:121,122:122,123:123,124:124,125:125,126:126,127:127,128:128,129:129,130:130,131:131,132:132,133:133,134:134,135:135,136:136,137:137,138:138,139:139,140:140,141:141,142:142,143:143,144:144,145:145,146:146,147:147,148:148,149:149,150:150,151:151,152:152,153:153,154:154,155:155,156:156,157:157,158:158,159:159,160:160,161:161,162:162,163:163,164:164,165:165,166:166,167:167,168:168,169:169,170:170,171:171,172:172,173:173,174:174,175:175,176:176,177:177,178:178,179:179,180:180,181:181,182:182,183:183,184:184,185:185,186:186,187:187,188:188,189:189,190:190,191:191,192:192,193:193,194:194,195:195,196:196,197:197,198:198,199:199,200:200,201:201,202:202,203:203,204:204,205:205,206:206,207:207,208:208,209:209,210:210,211:211,212:212,213:213,214:214,215:215,216:216,217:217,218:218,219:219,220:220,221:221,222:222,223:223,224:224,225:225,226:226,227:227,228:228,229:229,23:23,230:230,231:231,232:232,233:233,234:234,235:235,236:236,237:237,238:238,239:239,240:240,241:241,242:242,243:243,244:244,245:245,246:246,247:247,248:248,249:249,250:250,251:251,252:252,253:253,254:254,255:255,256:256,257:257,258:258,259:259,260:260,261:261,262:262,263:263,264:264,265:265,266:266,267:267,268:268,269:269,270:270,271:271,272:272,273:273,274:274,275:275,276:276,277:277,278:278,279:279,280:280,281:281,282:282,283:283,284:284,285:285,286:286,287:287,288:288,289:289,290:290,291:291,292:292,293:293,294:294}],296:[function(t,e,n){(function(t){!function(t){"use strict";function n(t,e,n,r){var i=e&&e.prototype instanceof o?e:o,a=Object.create(i.prototype),u=new h(r||[]);return a._invoke=c(t,n,u),a}function r(t,e,n){try{return{type:"normal",arg:t.call(e,n)}}catch(t){return{type:"throw",arg:t}}}function o(){}function i(){}function a(){}function u(t){["next","throw","return"].forEach(function(e){t[e]=function(t){return this._invoke(e,t)}})}function s(e){function n(t,o,i,a){var u=r(e[t],e,o);if("throw"!==u.type){var s=u.arg,c=s.value;return c&&"object"==typeof c&&g.call(c,"__await")?Promise.resolve(c.__await).then(function(t){n("next",t,i,a)},function(t){n("throw",t,i,a)}):Promise.resolve(c).then(function(t){s.value=t,i(s)},a)}a(u.arg)}"object"==typeof t.process&&t.process.domain&&(n=t.process.domain.bind(n));var o;this._invoke=function(t,e){function r(){return new Promise(function(r,o){n(t,e,r,o)})}return o=o?o.then(r,r):r()}}function c(t,e,n){var o=E;return function(i,a){if(o===j)throw new Error("Generator is already running");if(o===A){if("throw"===i)throw a;return m()}for(n.method=i,n.arg=a;;){var u=n.delegate;if(u){var s=f(u,n);if(s){if(s===P)continue;return s}}if("next"===n.method)n.sent=n._sent=n.arg;else if("throw"===n.method){if(o===E)throw o=A,n.arg;n.dispatchException(n.arg)}else"return"===n.method&&n.abrupt("return",n.arg);o=j;var c=r(t,e,n);if("normal"===c.type){if(o=n.done?A:O,c.arg===P)continue;return{value:c.arg,done:n.done}}"throw"===c.type&&(o=A,n.method="throw",n.arg=c.arg)}}}function f(t,e){var n=t.iterator[e.method];if(n===v){if(e.delegate=null,"throw"===e.method){if(t.iterator.return&&(e.method="return",e.arg=v,f(t,e),"throw"===e.method))return P;e.method="throw",e.arg=new TypeError("The iterator does not provide a 'throw' method")}return P}var o=r(n,t.iterator,e.arg);if("throw"===o.type)return e.method="throw",e.arg=o.arg,e.delegate=null,P;var i=o.arg;return i?i.done?(e[t.resultName]=i.value,e.next=t.nextLoc,"return"!==e.method&&(e.method="next",e.arg=v),e.delegate=null,P):i:(e.method="throw",e.arg=new TypeError("iterator result is not an object"),e.delegate=null,P)}function l(t){var e={tryLoc:t[0]};1 in t&&(e.catchLoc=t[1]),2 in t&&(e.finallyLoc=t[2],e.afterLoc=t[3]),this.tryEntries.push(e)}function p(t){var e=t.completion||{};e.type="normal",delete e.arg,t.completion=e}function h(t){this.tryEntries=[{tryLoc:"root"}],t.forEach(l,this),this.reset(!0)}function d(t){if(t){var e=t[w];if(e)return e.call(t);if("function"==typeof t.next)return t;if(!isNaN(t.length)){var n=-1,r=function e(){for(;++n<t.length;)if(g.call(t,n))return e.value=t[n],e.done=!1,e;return e.value=v,e.done=!0,e};return r.next=r}}return{next:m}}function m(){return{value:v,done:!0}}var v,y=Object.prototype,g=y.hasOwnProperty,b="function"==typeof Symbol?Symbol:{},w=b.iterator||"@@iterator",_=b.asyncIterator||"@@asyncIterator",x=b.toStringTag||"@@toStringTag",S="object"==typeof e,k=t.regeneratorRuntime;if(k)S&&(e.exports=k);else{(k=t.regeneratorRuntime=S?e.exports:{}).wrap=n;var E="suspendedStart",O="suspendedYield",j="executing",A="completed",P={},I={};I[w]=function(){return this};var F=Object.getPrototypeOf,N=F&&F(F(d([])));N&&N!==y&&g.call(N,w)&&(I=N);var M=a.prototype=o.prototype=Object.create(I);i.prototype=M.constructor=a,a.constructor=i,a[x]=i.displayName="GeneratorFunction",k.isGeneratorFunction=function(t){var e="function"==typeof t&&t.constructor;return!!e&&(e===i||"GeneratorFunction"===(e.displayName||e.name))},k.mark=function(t){return Object.setPrototypeOf?Object.setPrototypeOf(t,a):(t.__proto__=a,x in t||(t[x]="GeneratorFunction")),t.prototype=Object.create(M),t},k.awrap=function(t){return{__await:t}},u(s.prototype),s.prototype[_]=function(){return this},k.AsyncIterator=s,k.async=function(t,e,r,o){var i=new s(n(t,e,r,o));return k.isGeneratorFunction(e)?i:i.next().then(function(t){return t.done?t.value:i.next()})},u(M),M[x]="Generator",M[w]=function(){return this},M.toString=function(){return"[object Generator]"},k.keys=function(t){var e=[];for(var n in t)e.push(n);return e.reverse(),function n(){for(;e.length;){var r=e.pop();if(r in t)return n.value=r,n.done=!1,n}return n.done=!0,n}},k.values=d,h.prototype={constructor:h,reset:function(t){if(this.prev=0,this.next=0,this.sent=this._sent=v,this.done=!1,this.delegate=null,this.method="next",this.arg=v,this.tryEntries.forEach(p),!t)for(var e in this)"t"===e.charAt(0)&&g.call(this,e)&&!isNaN(+e.slice(1))&&(this[e]=v)},stop:function(){this.done=!0;var t=this.tryEntries[0].completion;if("throw"===t.type)throw t.arg;return this.rval},dispatchException:function(t){function e(e,r){return i.type="throw",i.arg=t,n.next=e,r&&(n.method="next",n.arg=v),!!r}if(this.done)throw t;for(var n=this,r=this.tryEntries.length-1;r>=0;--r){var o=this.tryEntries[r],i=o.completion;if("root"===o.tryLoc)return e("end");if(o.tryLoc<=this.prev){var a=g.call(o,"catchLoc"),u=g.call(o,"finallyLoc");if(a&&u){if(this.prev<o.catchLoc)return e(o.catchLoc,!0);if(this.prev<o.finallyLoc)return e(o.finallyLoc)}else if(a){if(this.prev<o.catchLoc)return e(o.catchLoc,!0)}else{if(!u)throw new Error("try statement without catch or finally");if(this.prev<o.finallyLoc)return e(o.finallyLoc)}}}},abrupt:function(t,e){for(var n=this.tryEntries.length-1;n>=0;--n){var r=this.tryEntries[n];if(r.tryLoc<=this.prev&&g.call(r,"finallyLoc")&&this.prev<r.finallyLoc){var o=r;break}}o&&("break"===t||"continue"===t)&&o.tryLoc<=e&&e<=o.finallyLoc&&(o=null);var i=o?o.completion:{};return i.type=t,i.arg=e,o?(this.method="next",this.next=o.finallyLoc,P):this.complete(i)},complete:function(t,e){if("throw"===t.type)throw t.arg;return"break"===t.type||"continue"===t.type?this.next=t.arg:"return"===t.type?(this.rval=this.arg=t.arg,this.method="return",this.next="end"):"normal"===t.type&&e&&(this.next=e),P},finish:function(t){for(var e=this.tryEntries.length-1;e>=0;--e){var n=this.tryEntries[e];if(n.finallyLoc===t)return this.complete(n.completion,n.afterLoc),p(n),P}},catch:function(t){for(var e=this.tryEntries.length-1;e>=0;--e){var n=this.tryEntries[e];if(n.tryLoc===t){var r=n.completion;if("throw"===r.type){var o=r.arg;p(n)}return o}}throw new Error("illegal catch attempt")},delegateYield:function(t,e,n){return this.delegate={iterator:d(t),resultName:e,nextLoc:n},"next"===this.method&&(this.arg=v),P}}}}("object"==typeof t?t:"object"==typeof window?window:"object"==typeof self?self:this)}).call(this,"undefined"!=typeof global?global:"undefined"!=typeof self?self:"undefined"!=typeof window?window:{})},{}],297:[function(t,e,n){"use strict";e.exports=function(t,e){return e.filter(function(e){return-1===t.indexOf(e)})}},{}],298:[function(t,e,n){"use strict";e.exports=function(t,e){for(var n=arguments.length>2&&void 0!==arguments[2]?arguments[2]:null,r=0;r<t.length;r++){var o=e.call(n,t[r]);if(void 0!==o)return!0===o?t[r]:o}}},{}],299:[function(t,e,n){"use strict";e.exports=function(t){return Array.prototype.slice.call(t)}},{}],300:[function(t,e,n){"use strict";e.exports=function(t){return Array.isArray(t)?t.filter(function(e,n){return t.indexOf(e)===n}):[]}},{}],301:[function(t,e,n){"use strict";function r(t){return this.native=t,a(this.api.bind(this),this)}var o="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t},i=t(337),a=t(324);e.exports=r,r.prototype.api=function(t,e){if("object"===(void 0===t?"undefined":o(t)))for(var n in t)this.api(n,t[n]);else{if(!t)throw"agent/store must have a valid name";if(void 0===e)return this.getItem(t);null===e?this.removeItem(t):this.setItem(t,e)}},r.prototype.getItem=function(t){return i(this.native.getItem(t))},r.prototype.setItem=function(t,e){this.native.setItem(t,JSON.stringify(e))},r.prototype.removeItem=function(t){this.native.removeItem(t)}},{324:324,337:337}],302:[function(t,e,n){"use strict";var r=t(298),o=t(301);e.exports=new o({getItem:function(t){var e=t+"=",n=document.cookie.split(";");return r(n,function(t){if((t=t.replace(/(^\s+|\s+$)/,""))&&0===t.indexOf(e))return t.substr(e.length)})||null},setItem:function(t,e){document.cookie=t+"="+e},removeItem:function(t){document.cookie=t+"=; expires=Thu, 01 Jan 1970 00:00:01 GMT;"}})},{298:298,301:301}],303:[function(t,e,n){"use strict";var r=t(304),o=t(301);try{var i="__tricks_temp__",a=window.localStorage;a.setItem(i,1),a.removeItem(i),e.exports=new o(a)}catch(t){e.exports=r}},{301:301,304:304}],304:[function(t,e,n){"use strict";var r=t(302),o=t(301);try{var i="__tricks_temp__",a=window.sessionStorage;a.setItem(i,1),a.removeItem(i),e.exports=new o(a)}catch(t){e.exports=r}},{301:301,302:302}],305:[function(t,e,n){"use strict";function r(t){var e=void 0;try{e=s('<iframe name="'+t+'">')}catch(t){e=s("iframe")}return a(e,{name:t,id:t,style:"display:none;"}),document.body.appendChild(e),e}function o(t){var e=null,n=[],r=0,o=null;if(u("input",t)&&(e=t.form,f(e.elements).forEach(function(e){e!==t&&e.setAttribute("disabled",!0)}),t=e),u("form",t))f((e=t).elements).forEach(function(t){t.disabled||"file"!==t.type||(e.encoding=e.enctype="multipart/form-data",t.setAttribute("name","file"))});else{for(o in t)t.hasOwnProperty(o)&&u("input",t[o])&&"file"===t[o].type&&((e=t[o].form).encoding=e.enctype="multipart/form-data");e?p(e,"submit",function(){d(function(){n.forEach(function(t){t&&(t.setAttribute("disabled",!1),t.disabled=!1)}),n.length=0})}):(e=i("form"),p(e,"submit",function(){d(function(){e.parentNode.removeChild(e)})}));var a=void 0;for(o in t)if(t.hasOwnProperty(o)){var s=u("input",t[o])||u("textArea",t[o])||u("select",t[o]);if(s&&t[o].form===e)s&&t[o].name!==o&&(t[o].setAttribute("name",o),t[o].name=o);else{var c=e.elements[o];if(a)for(l(c,window.NodeList)||(c=[c]),r=0;r<c.length;r++)c[r].parentNode.removeChild(c[r]);a=i("input",{type:"hidden",name:o},e),s?a.value=t[o].value:u(null,t[o])?a.value=t[o].innerHTML||t[o].innerText:a.value=t[o]}}f(e.elements).forEach(function(e){e.name in t||!0===e.getAttribute("disabled")||(e.setAttribute("disabled",!0),n.push(e))})}return e}var i=t(310),a=t(311),u=t(313),s=t(312),c=t(319),f=t(299),l=t(326),p=t(320),h=t(318),d=t(343);e.exports=function(t,e,n,i,u){var s=arguments.length>5&&void 0!==arguments[5]?arguments[5]:6e4,f=void 0,l=0,p=function(t){return l++||(f&&(clearTimeout(f),f=null),i(t),h(v,"submit"),d(function(){return m.parentNode.removeChild(m)})),!0},m=r(u=c(p,u));n&&n.callbackonload&&(m.onload=p.bind(null,{response:"posted",message:"Content was posted"})),s&&(f=setTimeout(p.bind(null,new Error("timeout")),s));var v=o(e);t=t.replace(new RegExp("=\\?(&|$)"),"="+u+"$1"),a(v,{method:"POST",target:u,action:t}),v.target=u,setTimeout(function(){v.submit()},100)}},{299:299,310:310,311:311,312:312,313:313,318:318,319:319,320:320,326:326,343:343}],306:[function(t,e,n){"use strict";var r=t(312),o=t(317);e.exports=function(t,e){var n=arguments.length>2&&void 0!==arguments[2]?arguments[2]:0,i=0,a=void 0,u=document.getElementsByTagName("script")[0].parentNode,s=function(t){!i++&&e&&e(t),a&&clearTimeout(a)};n&&(a=window.setTimeout(function(){s(o("timeout"))},n));var c=r("script",{src:t,onerror:s,onload:s,onreadystatechange:function(){/loaded|complete/i.test(c.readyState)&&s(o("load"))}});return c.async=!0,u.insertBefore(c,u.firstChild),c}},{312:312,317:317}],307:[function(t,e,n){"use strict";var r=t(319),o=t(306),i=/=\?(&|$)/;e.exports=function(t,e,n){var a=arguments.length>3&&void 0!==arguments[3]?arguments[3]:6e4,u=void 0;n=r(function(t){return u=t,!0},n),t=t.replace(i,"="+n+"$1");var s=o(t,function(){e(u),s.parentNode.removeChild(s)},a);return s}},{306:306,319:319}],308:[function(t,e,n){"use strict";var r=t(307),o=t(309),i=t(305),a=t(342),u=t(319),s=t(335),c=t(324);e.exports=function(t,e){if("string"==typeof t&&(t={url:t}),t.url=t.url||t.uri,t.query=t.query||t.qs||{},t.method=(t.method||"get").toLowerCase(),t.proxyHandler=t.proxyHandler||function(t,e){e()},a&&("function"==typeof t.xhr?t.xhr(t,t.query):!1!==t.xhr))t.proxyHandler(t,function(){var n=s(t.url,t.query),r=o(t.method,n,t.responseType,t.headers,t.data,e);r.onprogress=t.onprogress||null,r.upload&&t.onuploadprogress&&(r.upload.onprogress=t.onuploadprogress)});else if(t.callbackID=t.query.callback=u(e),!1===t.jsonp||("function"==typeof t.jsonp&&t.jsonp(t,t.query),"get"!==t.method)){if(!1!==t.form){t.query.redirect_uri=t.redirect_uri,t.query.state=JSON.stringify({callback:t.callbackID}),delete t.query.callback;var n=void 0;if("function"==typeof t.form&&(n=t.form(t,t.query)),"post"===t.method&&!1!==n)return void t.proxyHandler(t,function(){var r=s(t.url,t.query);i(r,t.data,n,e,t.callbackID,t.timeout)})}e({error:"invalid_request"})}else t.proxyHandler(t,function(){var n=s(t.url,c(t.query,t.data||{}));r(n,e,t.callbackID,t.timeout)})}},{305:305,307:307,309:309,319:319,324:324,335:335,342:342}],309:[function(t,e,n){"use strict";function r(t){var e=new FormData;for(var n in t)t.hasOwnProperty(n)&&(o(t[n],window.HTMLInputElement)&&"files"in t[n]?t[n].files.length>0&&e.append(n,t[n].files[0]):o(t[n],window.Blob)?e.append(n,t[n],t.name):e.append(n,t[n]));return e}var o=t(326),i=t(336),a=t(337),u=t(333),s=t(331),c=/([a-z0-9-]+):\s*(.*);?/gi;e.exports=s(function(t,e,n,s,f,l){var p=new XMLHttpRequest;if(t=t.toUpperCase(),p.onload=function(){var t=p.response;t||""!==p.responseType&&"text"!==p.responseType||(t=p.responseText),"string"==typeof t&&"json"===n&&(t=p.responseJSON||a(p.responseText||p.response));var e=i(p.getAllResponseHeaders(),c);e.statusCode=p.status,l(t,e)},p.onerror=p.onload,"GET"===t||"DELETE"===t?f=null:!f||"string"==typeof f||o(f,window.FormData)||o(f,window.File)||o(f,window.Blob)||(f=r(f)),p.open(t,e,!0),"responseType"in p?u(function(){p.responseType=n}):"blob"===n&&p.overrideMimeType("text/plain; charset=x-user-defined"),s)for(var h in s)p.setRequestHeader(h,s[h]);return p.send(f),p})},{326:326,331:331,333:333,336:336,337:337}],310:[function(t,e,n){"use strict";var r=t(312);e.exports=function(t,e){var n=arguments.length>2&&void 0!==arguments[2]?arguments[2]:document.body,o=r(t,e);return n.appendChild(o),o}},{312:312}],311:[function(t,e,n){"use strict";var r=t(314);e.exports=function(t,e){return r(t,function(t){for(var n in e){var r=e[n];"function"==typeof r?t[n]=r:t.setAttribute(n,r)}})}},{314:314}],312:[function(t,e,n){"use strict";var r=t(311);e.exports=function(t,e){var n=document.createElement(t);return r(n,e),n}},{311:311}],313:[function(t,e,n){"use strict";var r=t(326);e.exports=function(t,e){var n="HTML"+(t||"").replace(/^[a-z]/,function(t){return t.toUpperCase()})+"Element";return!!e&&(window[n]?r(e,window[n]):window.Element?r(e,window.Element)&&(!t||e.tagName&&e.tagName.toLowerCase()===t):!(r(e,Object)||r(e,Array)||r(e,String)||r(e,Number))&&e.tagName&&e.tagName.toLowerCase()===t)}},{326:326}],314:[function(t,e,n){"use strict";var r=t(316),o=t(326),i=t(299);e.exports=function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:function(){};return r(t)?t=[t]:"string"==typeof t&&(t=document.querySelectorAll(t)),o(t,Array)||(t=i(t)),e&&t.forEach(e),t}},{299:299,316:316,326:326}],315:[function(t,e,n){"use strict";var r=t(310),o=t(338);e.exports=function(t){var e=o({position:"absolute",left:"-1000px",bottom:0,height:"1px",width:"1px"},";",":");return r("iframe",{src:t,style:e})}},{310:310,338:338}],316:[function(t,e,n){"use strict";var r=t(326),o="undefined"!=typeof HTMLElement?HTMLElement:Element,i="undefined"!=typeof HTMLDocument?HTMLDocument:Document,a=window.constructor;e.exports=function(t){return r(t,o)||r(t,i)||r(t,a)}},{326:326}],317:[function(t,e,n){"use strict";var r={bubbles:!0,cancelable:!0},o=function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:r;return new Event(t,e)};try{o("test")}catch(t){o=function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:r,n=document.createEvent("Event");return n.initEvent(t,!!e.bubbles,!!e.cancelable),n}}e.exports=o},{}],318:[function(t,e,n){"use strict";var r=t(314),o=t(317);e.exports=function(t,e){return r(t,function(t){return t.dispatchEvent(o(e))})}},{314:314,317:317}],319:[function(t,e,n){"use strict";function r(t,e){for(var n=arguments.length,r=Array(n>2?n-2:0),o=2;o<n;o++)r[o-2]=arguments[o];e.apply(void 0,r)&&delete window[t]}var o=t(341);e.exports=function(t,e){var n=arguments.length>2&&void 0!==arguments[2]?arguments[2]:"_tricks_";return e=e||n+o(),window[e]=r.bind(null,e,t),e}},{341:341}],320:[function(t,e,n){"use strict";var r="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t},o=t(314),i=/[\s,]+/,a=!1;try{var u=Object.defineProperty({},"passive",{get:function(){a=!0}});window.addEventListener("test",null,u)}catch(t){}e.exports=function(t,e,n){var u=arguments.length>3&&void 0!==arguments[3]&&arguments[3];return"object"===(void 0===u?"undefined":r(u))&&u.passive&&!a&&(u=!1),e=e.split(i),o(t,function(t){return e.forEach(function(e){return t.addEventListener(e,n,u)})})}},{314:314}],321:[function(t,e,n){"use strict";var r="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t};e.exports=function(t,e){var n={},o=0,i=null,a=null;for(a in t)if(t.hasOwnProperty(a))break;if(1===e.length&&"object"===r(e[0])&&"o!"!==t[a])for(a in e[0])if(t.hasOwnProperty(a)&&a in t)return e[0];for(a in t)if(t.hasOwnProperty(a))if(i=r(e[o]),"function"==typeof t[a]&&t[a].test(e[o])||"string"==typeof t[a]&&(t[a].indexOf("s")>-1&&"string"===i||t[a].indexOf("o")>-1&&"object"===i||t[a].indexOf("i")>-1&&"number"===i||t[a].indexOf("a")>-1&&"object"===i||t[a].indexOf("f")>-1&&"function"===i))n[a]=e[o++];else if("string"==typeof t[a]&&t[a].indexOf("!")>-1)return!1;return n}},{}],322:[function(t,e,n){"use strict";var r="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t},o=t(327);e.exports=function t(e){if(null===e||"object"!==(void 0===e?"undefined":r(e))||e instanceof Date||"nodeName"in e||o(e)||"function"==typeof FormData&&e instanceof FormData)return e;if(Array.isArray(e))return e.map(t.bind(this));var n={};for(var i in e)n[i]=t(e[i]);return n}},{327:327}],323:[function(t,e,n){"use strict";e.exports=function(t,e){if(t||!e){var n={};for(var r in t)r in e||(n[r]=t[r]);return n}return t}},{}],324:[function(t,e,n){"use strict";var r=t(326);e.exports=function t(e){for(var n=arguments.length,o=Array(n>1?n-1:0),i=1;i<n;i++)o[i-1]=arguments[i];return o.forEach(function(n){if(Array.isArray(e)&&Array.isArray(n))Array.prototype.push.apply(e,n);else if(r(e,Object)&&r(n,Object)&&e!==n)for(var o in n)e[o]=t(e[o],n[o]);else e=Array.isArray(n)?n.slice(0):n}),e}},{326:326}],325:[function(t,e,n){"use strict";var r=t(327);e.exports=function(t){for(var e in t)if(t.hasOwnProperty(e)&&r(t[e]))return!0;return!1}},{327:327}],326:[function(t,e,n){"use strict";e.exports=function(t,e){return e&&t instanceof e}},{}],327:[function(t,e,n){"use strict";var r=t(326);e.exports=function(t){return r(t,Object)&&(r(t,"undefined"!=typeof HTMLInputElement?HTMLInputElement:void 0)&&"file"===t.type||r(t,"undefined"!=typeof HTMLInput?HTMLInput:void 0)&&"file"===t.type||r(t,"undefined"!=typeof FileList?FileList:void 0)||r(t,"undefined"!=typeof File?File:void 0)||r(t,"undefined"!=typeof Blob?Blob:void 0))}},{326:326}],328:[function(t,e,n){"use strict";var r="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t};e.exports=function(t){if(!t)return!0;if(Array.isArray(t))return!t.length;if("object"===(void 0===t?"undefined":r(t)))for(var e in t)if(t.hasOwnProperty(e))return!1;return!0}},{}],329:[function(t,e,n){"use strict";var r=t(324);e.exports=function(){for(var t=arguments.length,e=Array(t),n=0;n<t;n++)e[n]=arguments[n];return e.unshift({}),r.apply(void 0,e)}},{324:324}],330:[function(t,e,n){"use strict";function r(t,e){var n=this;return e&&"function"==typeof e&&t.split(f).forEach(function(t){n.events[t]=[e].concat(n.events[t]||[])}),this}function o(t,e){return this.findEvents(t,function(t,n){e&&this.events[t][n]!==e||(this.events[t][n]=null)}),this}function i(t){for(var e=arguments.length,n=Array(e>1?e-1:0),r=1;r<e;r++)n[r-1]=arguments[r];n.push(t);for(var o=this;o&&o.findEvents;)o.findEvents(t+",*",function(e,r){n[n.length-1]="*"===e?t:e,this.events[e][r].apply(this,n)}),o=o.parent;return this}function a(){for(var t=this,e=arguments.length,n=Array(e),r=0;r<e;r++)n[r]=arguments[r];return c(function(){t.emit.apply(t,n)}),this}function u(t,e){var n=t.split(f);for(var r in this.events)this.events.hasOwnProperty(r)&&n.indexOf(r)>-1&&this.events[r].forEach(s.bind(this,r,e))}function s(t,e,n,r){n&&e.call(this,t,r)}var c=t(343),f=/[\s,]+/;e.exports=function(){return this.parent={events:this.events,findEvents:this.findEvents,parent:this.parent,utils:this.utils},this.events={},this.off=o,this.on=r,this.emit=i,this.emitAfter=a,this.findEvents=u,this}},{343:343}],331:[function(t,e,n){"use strict";e.exports=function(t){var e=function t(){for(var e=arguments.length,n=Array(e),r=0;r<e;r++)n[r]=arguments[r];return t.fn.apply(null,n)};return e.fn=t,e}},{}],332:[function(t,e,n){"use strict";var r=/^data:([^;,]+(;charset=[^;,]+)?)(;base64)?,/i;e.exports=function(t){var e=t.match(r);if(!e)return t;for(var n=atob(t.replace(r,"")),o=n.length,i=new Uint8Array(o),a=0;a<o;a++)i[a]=n.charCodeAt(a);return new Blob([i],{type:e[1]})}},{}],333:[function(t,e,n){"use strict";e.exports=function(t){try{return t.call(null)}catch(t){}}},{}],334:[function(t,e,n){"use strict";e.exports=function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:0;return new Promise(function(n,r){var o=void 0,i=void 0,a=function(t){return function(e){return o&&clearTimeout(o),i=!0,t(e)}},u=a(n),s=a(r);!function n(){try{t(u,s),i||(o=setTimeout(n,e))}catch(t){r(t)}}()})}},{}],335:[function(t,e,n){"use strict";var r=t(340),o=t(328);e.exports=function(t,e,n){var i=void 0;if(e){n=n||encodeURIComponent;for(var a in e){var u="([\\?\\&])"+a+"=[^\\&]*";i=new RegExp(u),t.match(i)&&(t=t.replace(i,"$1"+a+"="+n(e[a])),delete e[a])}}return o(e)?t:t+(t.indexOf("?")>-1?"&":"?")+r(e,n)}},{328:328,340:340}],336:[function(t,e,n){"use strict";e.exports=function(t,e){for(var n=arguments.length>2&&void 0!==arguments[2]?arguments[2]:function(t){return t},r={},o=void 0;o=e.exec(t);)r[o[1]]=n(o[2]);return r}},{}],337:[function(t,e,n){"use strict";var r=t(333);e.exports=function(t){return r(function(){return JSON.parse(t)})}},{333:333}],338:[function(t,e,n){"use strict";e.exports=function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:"&",n=arguments.length>2&&void 0!==arguments[2]?arguments[2]:"=",r=arguments.length>3&&void 0!==arguments[3]?arguments[3]:function(t){return t};return Object.keys(t).map(function(e){var o=r(t[e]);return e+(null!==o?n+o:"")}).join(e)}},{}],339:[function(t,e,n){"use strict";var r=t(336),o=/^[#?]/,i=/([^=/&]+)=([^&]+)/g;e.exports=function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:decodeURIComponent;return t=t.replace(o,""),r(t,i,e)}},{336:336}],340:[function(t,e,n){"use strict";var r=t(338),o=function(t){return"?"===t?"?":encodeURIComponent(t)};e.exports=function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:o;return r(t,"&","=",e)}},{338:338}],341:[function(t,e,n){"use strict";e.exports=function(){return parseInt(1e12*Math.random(),10).toString(36)}},{}],342:[function(t,e,n){"use strict";e.exports="withCredentials"in new XMLHttpRequest},{}],343:[function(t,e,n){"use strict";e.exports="function"==typeof setImmediate?setImmediate:function(t){return setTimeout(t,0)}},{}],344:[function(t,e,n){"use strict";e.exports=function(t){if(t.frameElement)t.parent.document.body.removeChild(t.frameElement);else{try{t.close()}catch(t){}t.addEventListener&&t.addEventListener("load",function(){return t.close()})}}},{}],345:[function(t,e,n){"use strict";function r(t){var e=o(t,2),n=e[0],r=e[1],i=n.toLowerCase(),u=r.toLowerCase();if(this[u]&&!(i in this)){var s=void 0!==window["screen"+n]?window["screen"+n]:screen[i],c=screen[u]||window["inner"+r]||a["client"+r];this[i]=parseInt((c-this[u])/2,10)+s}}var o=function(){function t(t,e){var n=[],r=!0,o=!1,i=void 0;try{for(var a,u=t[Symbol.iterator]();!(r=(a=u.next()).done)&&(n.push(a.value),!e||n.length!==e);r=!0);}catch(t){o=!0,i=t}finally{try{!r&&u.return&&u.return()}finally{if(o)throw i}}return n}return function(e,n){if(Array.isArray(e))return e;if(Symbol.iterator in Object(e))return t(e,n);throw new TypeError("Invalid attempt to destructure non-iterable instance")}}(),i=t(338),a=document.documentElement,u=[["Top","Height"],["Left","Width"]];e.exports=function(t,e){var n=arguments.length>2&&void 0!==arguments[2]?arguments[2]:{};return u.forEach(r.bind(n)),window.open(t,e,i(n,","))}},{338:338}],346:[function(t,e,n){"use strict";e.exports=function(t){if(t){if(window.URL&&URL instanceof Function&&0!==URL.length)return new URL(t,window.location);var e=document.createElement("a");return e.href=t,e.cloneNode(!1)}return window.location}},{}],347:[function(t,e,n){"use strict";t(1),t(351),t(352),t(353),t(354),t(355),t(356),t(357),t(358),t(359),t(360),t(361),t(362),t(363),t(364),t(350),t(348),e.exports=t(349)},{1:1,348:348,349:349,350:350,351:351,352:352,353:353,354:354,355:355,356:356,357:357,358:358,359:359,360:360,361:361,362:362,363:363,364:364}],348:[function(t,e,n){"use strict";function r(t,e){var n={closed:!1};return chrome.identity.launchWebAuthFlow({url:t,interactive:e},function(t){if(void 0!==t){var e=a(t),o={location:{assign:function(t){r(t,!1)},search:e.search,hash:e.hash,href:e.href},close:function(){}};u.utils.responseHandler(o,window)}else n.closed=!0}),n}var o="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t},i="function"==typeof Symbol&&"symbol"===o(Symbol.iterator)?function(t){return void 0===t?"undefined":o(t)}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":void 0===t?"undefined":o(t)},a=t(346),u=t(349);if("object"===("undefined"==typeof chrome?"undefined":i(chrome))&&"object"===i(chrome.identity)&&chrome.identity.launchWebAuthFlow){u.utils.popup=function(t){return r(t,!0)},u.utils.iframe=function(t){r(t,!1)},u.utils.request_cors=function(t){return t(),!0};var s={};chrome.storage.local.get("hello",function(t){s=t.hello||{}}),u.utils.store=function(t,e){return 0===arguments.length?s:1===arguments.length?s[t]||null:e?(s[t]=e,chrome.storage.local.set({hello:s}),e):null===e?(delete s[t],chrome.storage.local.set({hello:s}),null):void 0}}e.exports=u},{346:346,349:349}],349:[function(t,e,n){"use strict";function o(t){return function(){var e=t.apply(this,arguments);return new Promise(function(t,n){function r(o,i){try{var a=e[o](i),u=a.value}catch(t){return void n(t)}if(!a.done)return Promise.resolve(u).then(function(t){r("next",t)},function(t){r("throw",t)});t(u)}return r("next")})}}function i(t,e){return{error:{code:t,message:e}}}var a="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t},u="function"==typeof Symbol&&"symbol"===a(Symbol.iterator)?function(t){return void 0===t?"undefined":a(t)}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":void 0===t?"undefined":a(t)},s=t(321),c=t(322),f=t(344),l=t(335),p=t(323),h=t(297),d=t(324),m=t(319),v=t(315),y=t(328),g=t(329),b=t(339),w=t(340),_=t(345),x=t(330),S=t(341),k=t(308),E=t(303),O=t(300),j=t(346),A=t(334),P=function t(e){return t.use(e)};e.exports=P,d(P,{settings:{redirect_uri:"undefined"!=typeof location?location.href.split("#")[0]:null,response_type:"token",display:"popup",state:"",oauth_proxy:"https://auth-server.herokuapp.com/proxy",timeout:2e4,popup:{resizable:1,scrollbars:1,width:500,height:550},scope:["basic"],scope_map:{basic:""},default_service:null,force:null,page_uri:"undefined"!=typeof location?location.href:null},services:{},use:function(t){var e=Object.create(this);return e.settings=Object.create(this.settings),t&&(e.settings.default_service=t),x.call(e),e},init:function(t,e){if(!t)return this.services;for(var n in t)t.hasOwnProperty(n)&&"object"!==u(t[n])&&(t[n]={id:t[n]});return d(this.services,t),e&&(d(this.settings,e),"redirect_uri"in e&&(this.settings.redirect_uri=j(e.redirect_uri).href)),this},login:function(){for(var t=this,e=arguments.length,n=Array(e),r=0;r<e;r++)n[r]=arguments[r];return o(regeneratorRuntime.mark(function e(){var r,o,a,u,c,f,d,v,y,b,w,x,k,E,I,F,N,M,T,R;return regeneratorRuntime.wrap(function(e){for(;;)switch(e.prev=e.next){case 0:if(R=function(t,e){P.emit(t,e)},M=function(t){return!!t},N=function(t){return t},r=t.utils,o=s({network:"s",options:"o",callback:"f"},n),a=void 0,u=p(o.options,t.settings),c=o.options=g(t.settings,o.options||{}),c.popup=g(t.settings.popup,o.options.popup||{}),o.network=o.network||t.settings.default_service,"string"==typeof o.network&&o.network in t.services){e.next=12;break}throw i("invalid_network","The provided network was not recognized");case 12:if(f=t.services[o.network],d="_hellojs_"+S(),(v=[]).push(new Promise(function(t,e){m(function(n){var o=void 0;(o=n?JSON.parse(n):i("cancelled","The authentication was not completed")).error?e(o):(r.store(o.network,o),t({network:o.network,authResponse:o}))},d)})),y=j(c.redirect_uri).href,b=f.oauth.response_type||c.response_type,/\bcode\b/.test(b)&&!f.oauth.grant&&(b=b.replace(/\bcode\b/,"token")),o.qs=g(u,{client_id:encodeURIComponent(f.id),response_type:encodeURIComponent(b),redirect_uri:encodeURIComponent(y),state:{client_id:f.id,network:o.network,display:c.display,callback:d,state:c.state,redirect_uri:y}}),w=r.store(o.network),x=/[,\s]+/,k=t.settings.scope?[t.settings.scope.toString()]:[],E=g(t.settings.scope_map,f.scope||{}),c.scope&&k.push(c.scope.toString()),w&&"scope"in w&&w.scope instanceof String&&k.push(w.scope),k=k.join(",").split(x),k=O(k).filter(M),o.qs.state.scope=k.join(","),k=k.map(function(t){return t in E?E[t]:t}),k=k.join(",").split(x),k=O(k).filter(M),o.qs.scope=k.join(f.scope_delim||","),!1!==c.force){e.next=38;break}if(!(w&&"access_token"in w&&w.access_token&&"expires"in w&&w.expires>(new Date).getTime()/1e3)){e.next=38;break}if(0!==(I=h((w.scope||"").split(x),(o.qs.state.scope||"").split(x))).length){e.next=38;break}return e.abrupt("return",{unchanged:!0,network:o.network,authResponse:w});case 38:return"page"===c.display&&c.page_uri&&(o.qs.state.page_uri=j(c.page_uri).href),"login"in f&&"function"==typeof f.login&&f.login(o),(!/\btoken\b/.test(b)||parseInt(f.oauth.version,10)<2||"none"===c.display&&f.oauth.grant&&w&&w.refresh_token)&&(o.qs.state.oauth=f.oauth,o.qs.state.oauth_proxy=c.oauth_proxy),o.qs.state=encodeURIComponent(JSON.stringify(o.qs.state)),1===parseInt(f.oauth.version,10)?a=l(c.oauth_proxy,o.qs,N):"none"===c.display&&f.oauth.grant&&w&&w.refresh_token?(o.qs.refresh_token=w.refresh_token,a=l(c.oauth_proxy,o.qs,N)):a=l(f.oauth.auth,o.qs,N),R("auth.init",o),"none"===c.display?r.iframe(a,y):"popup"===c.display?(F=r.popup(a,y,c.popup),v.push(A(function(t,e){if(!F||F.closed){var n=i("cancelled","Login has been cancelled");_||(n=i("blocked","Popup was blocked")),n.network=o.network,e(n)}},100))):window.location=a,(T=Promise.race(v)).then(o.callback,o.callback),T.then(R.bind(t,"auth.login auth"),R.bind(t,"auth.failed auth")),e.abrupt("return",T);case 49:case"end":return e.stop()}},e,t)}))()},logout:function(){for(var t=this,e=arguments.length,n=Array(e),r=0;r<e;r++)n[r]=arguments[r];return o(regeneratorRuntime.mark(function e(){var r,o,a,u,c;return regeneratorRuntime.wrap(function(e){for(;;)switch(e.prev=e.next){case 0:if(r=t.utils,o=s({name:"s",options:"o",callback:"f"},n),a=[],o.options=o.options||{},o.name=o.name||t.settings.default_service,o.authResponse=r.store(o.name),!o.name||o.name in t.services){e.next=10;break}throw i("invalid_network","The network was unrecognized");case 10:if(!o.name||!o.authResponse){e.next=15;break}u=new Promise(function(e){var n={};if(o.options.force){var i=t.services[o.name].logout;if(i)if("function"==typeof i&&(i=i(e,o)),"string"==typeof i)r.iframe(i),n.force=null,n.message="Logout success on providers site was indeterminate";else if(void 0===i)return}e(n)}).then(function(t){return r.store(o.name,null),g({network:o.name},t||{})}),a.push(u),e.next=16;break;case 15:throw i("invalid_session","There was no session to remove");case 16:return(c=Promise.race(a)).then(o.callback,o.callback),c.then(function(t){return P.emit("auth.logout auth",t)},function(t){return P.emit("error",t)}),e.abrupt("return",c);case 20:case"end":return e.stop()}},e,t)}))()},getAuthResponse:function(t){return(t=t||this.settings.default_service)&&t in this.services?this.utils.store(t)||null:null},events:{}}),d(P.utils={iframe:v,popup:_,request:k,store:E},{responseHandler:function(t,e){function n(t,e,n){var i=t.callback,a=t.network;if(o.store(a,t),!("display"in t&&"page"===t.display)){if(n&&i&&i in n){try{delete t.callback}catch(t){}o.store(a,t);var u=JSON.stringify(t);try{r(n,i)(u)}catch(t){}}f(e)}}function r(t,e){return 0!==e.indexOf("_hellojs_")?function(){throw"Could not execute callback "+e}:t[e]}var o=this,i=void 0,a=t.location,u=a.assign&&a.assign.bind(a)||function(e){t.location=e};if((i=b(a.search))&&i.state&&(i.code||i.oauth_token)){var s=JSON.parse(i.state);return i.redirect_uri=s.redirect_uri||a.href.replace(/[?#].*$/,""),void u(s.oauth_proxy+"?"+w(i))}if((i=g(b(a.search||""),b(a.hash||"")))&&"state"in i){try{var c=JSON.parse(i.state);d(i,c)}catch(t){P.emit("error","Could not decode state parameter")}if("access_token"in i&&i.access_token&&i.network)i.expires_in&&0!==parseInt(i.expires_in,10)||(i.expires_in=0),i.expires_in=parseInt(i.expires_in,10),i.expires=(new Date).getTime()/1e3+(i.expires_in||31536e3),n(i,t,e);else if("error"in i&&i.error&&i.network)i.error={code:i.error,message:i.error_message||i.error_description},n(i,t,e);else if(i.callback&&i.callback in e){var l=!!("result"in i&&i.result)&&JSON.parse(i.result);r(e,i.callback)(l),f(t)}i.page_uri&&u(i.page_uri)}else if("oauth_redirect"in i)return void u(decodeURIComponent(i.oauth_redirect))}}),x.call(P),function(t){var e={},n={};t.on("auth.login, auth.logout",function(n){n&&"object"===(void 0===n?"undefined":u(n))&&n.network&&(e[n.network]=t.utils.store(n.network)||{})}),function r(){var o=(new Date).getTime()/1e3;for(var i in t.services){(function(r){if(t.services.hasOwnProperty(r)){if(!t.services[r].id)return"continue";var i=t.utils.store(r)||{},a=t.services[r],u=e[r]||{},s=function(e){t.emit("auth."+e,{network:r,authResponse:i})};if(i&&"callback"in i){var c=i.callback;try{delete i.callback}catch(t){}t.utils.store(r,i);try{window[c](i)}catch(t){}}if(i&&"expires"in i&&i.expires<o){var f=a.refresh||i.refresh_token;return!f||r in n&&!(n[r]<o)?f||r in n||(s("expired"),n[r]=!0):(t.emit("notice",r+" has expired trying to resignin"),t.login(r,{display:"none",force:!1}),n[r]=o+600),"continue"}if(u.access_token===i.access_token&&u.expires===i.expires)return"continue";!i.access_token&&u.access_token?s("logout"):i.access_token&&!u.access_token?s("login"):i.expires!==u.expires&&s("update"),e[r]=i,r in n&&delete n[r]}})(i)}setTimeout(r,1e3)}()}(P),P.api=function(){var t=o(regeneratorRuntime.mark(function t(){for(var e=this,n=arguments.length,o=Array(n),a=0;a<n;a++)o[a]=arguments[a];var f,l,p,h,m,v,g,w,_,x;return regeneratorRuntime.wrap(function(t){for(;;)switch(t.prev=t.next){case 0:if((f=s({path:"s!",query:"o",method:"s",data:"o",timeout:"i",callback:"f"},o))&&f.path){t.next=3;break}throw i("invalid_path","Missing the path parameter from the request");case 3:if(f.method=(f.method||"get").toLowerCase(),f.headers=f.headers||{},f.responseType=f.responseType||"json",f.query=f.query||{},"get"!==f.method&&"delete"!==f.method||(d(f.query,f.data),f.data={}),l=f.data=f.data||{},f.path=f.path.replace(/^\/+/,""),(p=(f.path.split(/[/:]/,2)||[])[0].toLowerCase())in this.services&&(f.network=p,h=new RegExp("^"+p+":?/?"),f.path=f.path.replace(h,"")),f.network=this.settings.default_service=f.network||this.settings.default_service,m=this.services[f.network]){t.next=16;break}throw i("invalid_network","Could not match the service requested: "+f.network);case 16:if(!(f.method in m&&f.path in m[f.method]&&!1===m[f.method][f.path])){t.next=18;break}throw i("invalid_path","The provided path is not available on the selected network");case 18:return f.oauth_proxy||(f.oauth_proxy=this.settings.oauth_proxy),"proxy"in f||(f.proxy=f.oauth_proxy&&m.oauth&&1===parseInt(m.oauth.version,10)),"timeout"in f||(f.timeout=this.settings.timeout),"formatResponse"in f||(f.formatResponse=!0),f.authResponse=this.getAuthResponse(f.network),f.authResponse&&f.authResponse.access_token&&(f.query.access_token=f.authResponse.access_token),v=f.path,g=void 0,f.options=c(f.query),f.data=c(l),w=m[{delete:"del"}[f.method]||f.method]||{},"get"===f.method&&(_=v.split(/[?#]/)[1])&&(d(f.query,b(_)),v=v.replace(/\?.*?(#|$)/,"$1")),(g=v.match(/#(.+)/,""))?(v=v.split("#")[0],f.path=g[1]):v in w?(f.path=v,v=w[v]):"default"in w&&(v=w.default),f.redirect_uri=this.settings.redirect_uri,f.xhr=m.xhr,f.jsonp=m.jsonp,f.form=m.form,f.proxyHandler=P.utils.proxyHandler,x=void 0,x="function"==typeof v?new Promise(function(t){return v(f,t)}):Promise.resolve(v),(x=x.then(function(t){return(t=t.replace(/@\{([a-z_-]+)(\|.*?)?\}/gi,function(t,e,n){var r=n?n.replace(/^\|/,""):"";if(e in f.query)r=f.query[e],delete f.query[e];else if(f.data&&e in f.data)r=f.data[e],delete f.data[e];else if(!n)throw i("missing_attribute","The attribute "+e+" is missing from the request");return r})).match(/^https?:\/\//)||(t=m.base+t),f.url=t,new Promise(function(t){return e.utils.request(f,function(e,n){return t({data:e,headers:n})})})}).then(function(t){var e=t.data,n=t.headers;if(!f.formatResponse){if("object"===(void 0===n?"undefined":u(n))?n.statusCode>=400:"object"===("undefined"==typeof r?"undefined":u(r))&&"error"in e)throw e;return e}if(!0===e&&(e={success:!0}),"delete"===f.method&&(e=!e||y(e)?{success:!0}:e),m.wrap&&(f.path in m.wrap||"default"in m.wrap)){var o=f.path in m.wrap?f.path:"default",i=m.wrap[o](e,n,f);i&&(e=i)}if(e&&"paging"in e&&e.paging.next&&("?"===e.paging.next[0]?e.paging.next=f.path+e.paging.next:e.paging.next+="#"+f.path),!e||"error"in e)throw e;return e})).then(f.callback,f.callback),t.abrupt("return",x);case 41:case"end":return t.stop()}},t,this)}));return function(){return t.apply(this,arguments)}}(),P.utils.proxyHandler=function(t,e){var n=t.authResponse,r="";if(n&&n.oauth&&1===parseInt(n.oauth.version,10)&&(r=t.query.access_token||"",delete t.query.access_token,t.proxy=!0),!t.data||"get"!==t.method&&"delete"!==t.method||(d(t.query,t.data),t.data=null),t.proxy){var o=l(t.url,t.query);t.url=t.oauth_proxy,t.query={path:o,access_token:r,then:t.proxy_response_type||("get"===t.method.toLowerCase()?"redirect":"proxy"),method:t.method.toLowerCase(),suppress_response_codes:!0}}e()},P.utils.responseHandler(window,window.opener||window.parent),e.exports=P},{297:297,300:300,303:303,308:308,315:315,319:319,321:321,322:322,323:323,324:324,328:328,329:329,330:330,334:334,335:335,339:339,340:340,341:341,344:344,345:345,346:346}],350:[function(t,e,n){"use strict";var r=t(346),o=t(349);if(/^file:\/{3}[^/]/.test(window.location.href)&&window.cordova){o.utils.iframe=function(t,e){o.utils.popup(t,e,{hidden:"yes"})};var i=o.utils.popup;o.utils.popup=function(t,e,n){var a=i.call(this,t,e,n);try{if(a&&a.addEventListener){var u=r(e),s=u.origin||u.protocol+"//{a.hostname}";a.addEventListener("loadstart",function(t){var e=t.url;if(0===e.indexOf(s)){var n=r(e),i={location:{assign:function(t){a.executeScript({code:window.location.href+' = "'+t+';"'})},search:n.search,hash:n.hash,href:n.href},close:function(){if(a.close){a.close();try{a.closed=!0}catch(t){}}}};o.utils.responseHandler(i,window)}})}}catch(t){}return a}}e.exports=o},{346:346,349:349}],351:[function(t,e,n){"use strict";var r="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t},o="function"==typeof Symbol&&"symbol"===r(Symbol.iterator)?function(t){return void 0===t?"undefined":r(t)}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":void 0===t?"undefined":r(t)},i=t(349),a=t(332),u=t(340),s=function(t){t&&"error"in t&&(t.error={code:"server_error",message:t.error.message||t.error})},c=function(t,e,n){if(!("object"!==(void 0===t?"undefined":o(t))||"undefined"!=typeof Blob&&t instanceof Blob||"undefined"!=typeof ArrayBuffer&&t instanceof ArrayBuffer||"error"in t)){var r=("app_folder"!==t.root?t.root:"")+t.path.replace(/&/g,"%26");if(r=r.replace(/^\//,""),t.thumb_exists){var a=encodeURIComponent("https://api-content.dropbox.com/1/thumbnails/auto/"+r+"?format=jpeg&size=m");t.thumbnail=n.oauth_proxy+"?path="+a+"&access_token="+n.options.access_token}if(t.type=t.is_dir?"folder":t.mime_type,t.name=t.path.replace(/.*\//g,""),t.is_dir)t.files=r.replace(/^\//,"");else{var u="https://api-content.dropbox.com/1/files/auto/"+r;t.downloadLink=i.settings.oauth_proxy+"?path="+encodeURIComponent(u)+"&access_token="+n.options.access_token,t.file=u}t.id||(t.id=t.path.replace(/^\//,""))}},f=function(t){return function(e,n){delete e.query.limit,n(t)}},l={version:"1.0",auth:"https://www.dropbox.com/1/oauth/authorize",request:"https://api.dropbox.com/1/oauth/request_token",token:"https://api.dropbox.com/1/oauth/access_token"},p={version:2,auth:"https://www.dropbox.com/1/oauth2/authorize",grant:"https://api.dropbox.com/1/oauth2/token"};i.init({dropbox:{name:"Dropbox",oauth:p,login:function(t){t.qs.scope="";var e=decodeURIComponent(t.qs.redirect_uri);0===e.indexOf("http:")&&0!==e.indexOf("http://localhost/")?i.services.dropbox.oauth=l:i.services.dropbox.oauth=p,t.options.popup.width=1e3,t.options.popup.height=1e3},base:"https://api.dropbox.com/1/",root:"sandbox",get:{me:"account/info","me/files":f("metadata/auto/@{parent|}"),"me/folder":f("metadata/auto/@{id}"),"me/folders":f("metadata/auto/"),default:function(t,e){t.path.match("https://api-content.dropbox.com/1/files/")&&(t.method="blob"),e(t.path)}},post:{"me/files":function(t,e){var n=t.data.parent,r=t.data.name;t.data={file:t.data.file},"string"==typeof t.data.file&&(t.data.file=a(t.data.file)),e("https://api-content.dropbox.com/1/files_put/auto/"+n+"/"+r)},"me/folders":function(t,e){var n=t.data.name;t.data={},e("fileops/create_folder?root=@{root|sandbox}&"+u({path:n}))}},del:{"me/files":"fileops/delete?root=@{root|sandbox}&path=@{id}","me/folder":"fileops/delete?root=@{root|sandbox}&path=@{id}"},wrap:{me:function(t){if(s(t),!t.uid)return t;t.name=t.display_name;var e=t.name.split(" ");return t.first_name=e.shift(),t.last_name=e.join(" "),t.id=t.uid,delete t.uid,delete t.display_name,t},default:function(t,e,n){return s(t),t.is_dir&&t.contents&&(t.data=t.contents,delete t.contents,t.data.forEach(function(e){e.root=t.root,c(e,0,n)})),c(t,0,n),t.is_deleted&&(t.success=!0),t}},xhr:function(t){if(t.data&&t.data.file){var e=t.data.file;e&&(e.files?t.data=e.files[0]:t.data=e)}return"delete"===t.method&&(t.method="post"),!0},form:function(t,e){delete e.state,delete e.redirect_uri}}})},{332:332,340:340,349:349}],352:[function(t,e,n){"use strict";var r=t(349),o=t(319),i=t(325),a=t(340),u=t(332),s=function(t){return t.id&&(t.thumbnail=t.picture=""+l+t.id+"/picture"),t},c=function(t){return"data"in t&&t.data.forEach(s),t},f=function(t,e,n){if("boolean"==typeof t&&(t={success:t}),t&&"data"in t){var r=n.authResponse.access_token;if(!(t.data instanceof Array)){var o=t.data;delete t.data,t.data=[o]}t.data.forEach(function(t){t.picture&&(t.thumbnail=t.picture),t.pictures=(t.images||[]).sort(function(t,e){return t.width-e.width}),t.cover_photo&&t.cover_photo.id&&(t.thumbnail=""+l+t.cover_photo.id+"/picture?access_token="+r),"album"===t.type&&(t.files=t.photos=""+l+t.id+"/photos"),t.can_upload&&(t.upload_location=""+l+t.id+"/photos")})}return t};r.init({facebook:{name:"Facebook",oauth:{version:2,auth:"https://www.facebook.com/v2.9/dialog/oauth/",grant:"https://graph.facebook.com/oauth/access_token"},scope:{basic:"public_profile",email:"email",share:"user_posts",birthday:"user_birthday",events:"user_events",photos:"user_photos",videos:"user_videos",friends:"user_friends",files:"user_photos,user_videos",publish_files:"user_photos,user_videos,publish_actions",publish:"publish_actions",offline_access:""},refresh:!1,login:function(t){t.options.force&&(t.qs.auth_type="reauthenticate"),t.qs.display=t.options.display||"popup"},logout:function(t,e){var n=o(t),i=a({callback:n,result:JSON.stringify({force:!0}),state:"{}"}),u=encodeURIComponent(r.settings.redirect_uri+"?"+i),s=(e.authResponse||{}).access_token;if(r.utils.iframe("https://www.facebook.com/logout.php?next="+u+"&access_token="+s),!s)return!1},base:"https://graph.facebook.com/v2.9/",get:{me:"me?fields=email,first_name,last_name,name,timezone,verified","me/friends":"me/friends","me/following":"me/friends","me/followers":"me/friends","me/share":"me/feed","me/like":"me/likes","me/files":"me/albums","me/albums":"me/albums?fields=cover_photo,name","me/album":"@{id}/photos?fields=picture","me/photos":"me/photos","me/photo":"@{id}","friend/albums":"@{id}/albums","friend/photos":"@{id}/photos"},post:{"me/share":"me/feed","me/photo":"@{id}"},wrap:{me:s,"me/friends":c,"me/following":c,"me/followers":c,"me/albums":f,"me/photos":f,"me/files":f,default:f},xhr:function(t,e){return"get"!==t.method&&"post"!==t.method||(e.suppress_response_codes=!0),"post"===t.method&&t.data&&"string"==typeof t.data.file&&(t.data.file=u(t.data.file)),!0},jsonp:function(t,e){var n=t.method;"get"===n||i(t.data)?"delete"===t.method&&(e.method="delete",t.method="post"):(t.data.method=n,t.method="get")},form:function(){return{callbackonload:!0}}}});var l="https://graph.facebook.com/"},{319:319,325:325,332:332,340:340,349:349}],353:[function(t,e,n){"use strict";var r=t(349),o=function(t,e,n){var o=(n?"":"flickr:")+"?method="+t+"&api_key="+r.services.flickr.id+"&format=json";for(var i in e)e.hasOwnProperty(i)&&(o+="&"+i+"="+e[i]);return o},i=function(t){var e=r.getAuthResponse("flickr");t(e&&e.user_nsid?e.user_nsid:null)},a=function(t,e){return e||(e={}),function(n,r){i(function(n){e.user_id=n,r(o(t,e,!0))})}},u=function(t,e){var n="https://www.flickr.com/images/buddyicon.gif";return t.nsid&&t.iconserver&&t.iconfarm&&(n="https://farm"+t.iconfarm+".staticflickr.com/"+t.iconserver+"/buddyicons/"+t.nsid+(e?"_"+e:"")+".jpg"),n},s=function(t,e,n,r,o){return o=o?"_"+o:"","https://farm"+e+".staticflickr.com/"+n+"/"+t+"_"+r+o+".jpg"},c=function(t){t&&t.stat&&"ok"!==t.stat.toLowerCase()&&(t.error={code:"invalid_request",message:t.message})},f=function(t){if(t.photoset||t.photos){t=p(t,"photoset"in t?"photoset":"photos"),d(t),t.data=t.photo,delete t.photo;for(var e=0;e<t.data.length;e++){var n=t.data[e];n.name=n.title,n.picture=s(n.id,n.farm,n.server,n.secret,""),n.pictures=l(n.id,n.farm,n.server,n.secret),n.source=s(n.id,n.farm,n.server,n.secret,"b"),n.thumbnail=s(n.id,n.farm,n.server,n.secret,"m")}}return t},l=function(t,e,n,r){return[{id:"t",max:100},{id:"m",max:240},{id:"n",max:320},{id:"",max:500},{id:"z",max:640},{id:"c",max:800},{id:"b",max:1024},{id:"h",max:1600},{id:"k",max:2048},{id:"o",max:2048}].map(function(o){return{source:s(t,e,n,r,o.id),width:o.max,height:o.max}})},p=function(t,e){return e in t?t=t[e]:"error"in t||(t.error={code:"invalid_request",message:t.message||"Failed to get data from Flickr"}),t},h=function(t){if(c(t),t.contacts){t=p(t,"contacts"),d(t),t.data=t.contact,delete t.contact;for(var e=0;e<t.data.length;e++){var n=t.data[e];n.id=n.nsid,n.name=n.realname||n.username,n.thumbnail=u(n,"m")}}return t},d=function(t){t.page&&t.pages&&t.page!==t.pages&&(t.paging={next:"?page="+ ++t.page})};r.init({flickr:{name:"Flickr",oauth:{version:"1.0a",auth:"https://www.flickr.com/services/oauth/authorize?perms=read",request:"https://www.flickr.com/services/oauth/request_token",token:"https://www.flickr.com/services/oauth/access_token"},base:"https://api.flickr.com/services/rest",get:{me:a("flickr.people.getInfo"),"me/friends":a("flickr.contacts.getList",{per_page:"@{limit|50}"}),"me/following":a("flickr.contacts.getList",{per_page:"@{limit|50}"}),"me/followers":a("flickr.contacts.getList",{per_page:"@{limit|50}"}),"me/albums":a("flickr.photosets.getList",{per_page:"@{limit|50}"}),"me/album":a("flickr.photosets.getPhotos",{photoset_id:"@{id}"}),"me/photos":a("flickr.people.getPhotos",{per_page:"@{limit|50}"})},wrap:{me:function(t){if(c(t),(t=p(t,"person")).id){if(t.realname){t.name=t.realname._content;var e=t.name.split(" ");t.first_name=e.shift(),t.last_name=e.join(" ")}t.thumbnail=u(t,"l"),t.picture=u(t,"l")}return t},"me/friends":h,"me/followers":h,"me/following":h,"me/albums":function(t){return c(t),t=p(t,"photosets"),d(t),t.photoset&&(t.data=t.photoset,t.data.forEach(function(t){t.name=t.title._content,t.photos="https://api.flickr.com/services/rest"+o("flickr.photosets.getPhotos",{photoset_id:t.id},!0)}),delete t.photoset),t},"me/photos":function(t){return c(t),f(t)},default:function(t){return c(t),f(t)}},xhr:!1,jsonp:function(t,e){"get"===t.method&&(delete e.callback,e.jsoncallback=t.callbackID)}}})},{349:349}],354:[function(t,e,n){"use strict";var r=function(t){!t.meta||400!==t.meta.code&&401!==t.meta.code||(t.error={code:"access_denied",message:t.meta.errorDetail})},o=function(t){t&&t.id&&(t.thumbnail=t.photo.prefix+"100x100"+t.photo.suffix,t.name=t.firstName+" "+t.lastName,t.first_name=t.firstName,t.last_name=t.lastName,t.contact&&t.contact.email&&(t.email=t.contact.email))},i=function(t,e){var n=e.access_token;return delete e.access_token,e.oauth_token=n,e.v=20121125,!0};t(349).init({foursquare:{name:"Foursquare",oauth:{version:2,auth:"https://foursquare.com/oauth2/authenticate",grant:"https://foursquare.com/oauth2/access_token"},refresh:!0,base:"https://api.foursquare.com/v2/",get:{me:"users/self","me/friends":"users/self/friends","me/followers":"users/self/friends","me/following":"users/self/friends"},wrap:{me:function(t){return r(t),t&&t.response&&(t=t.response.user,o(t)),t},default:function(t){return r(t),t&&"response"in t&&"friends"in t.response&&"items"in t.response.friends&&(t.data=t.response.friends.items,t.data.forEach(o),delete t.response),t}},xhr:i,jsonp:i}})},{349:349}],355:[function(t,e,n){"use strict";var r="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t},o="function"==typeof Symbol&&"symbol"===r(Symbol.iterator)?function(t){return void 0===t?"undefined":r(t)}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":void 0===t?"undefined":r(t)},i=function(t,e){var n=e?e.statusCode:t&&"meta"in t&&"status"in t.meta&&t.meta.status;401!==n&&403!==n||(t.error={code:"access_denied",message:t.message||(t.data?t.data.message:"Could not get response")},delete t.message)},a=function(t){t.id&&(t.thumbnail=t.picture=t.avatar_url,t.name=t.login)},u=function(t,e){if(t.data&&t.data.length&&e&&e.Link){var n=e.Link.match(/<(.*?)>;\s*rel="next"/);n&&(t.paging={next:n[1]})}};t(349).init({github:{name:"GitHub",oauth:{version:2,auth:"https://github.com/login/oauth/authorize",grant:"https://github.com/login/oauth/access_token",response_type:"code"},scope:{email:"user:email"},base:"https://api.github.com/",get:{me:"user","me/friends":"user/following?per_page=@{limit|100}","me/following":"user/following?per_page=@{limit|100}","me/followers":"user/followers?per_page=@{limit|100}","me/like":"user/starred?per_page=@{limit|100}"},wrap:{me:function(t,e){return i(t,e),a(t),t},default:function(t,e,n){return i(t,e),Array.isArray(t)&&(t={data:t}),t.data&&(u(t,e),t.data.forEach(a)),t}},xhr:function(t){return"get"!==t.method&&t.data&&(t.headers=t.headers||{},t.headers["Content-Type"]="application/json","object"===o(t.data)&&(t.data=JSON.stringify(t.data))),!0}}})},{349:349}],356:[function(t,e,n){"use strict";var r=Object.assign||function(t){for(var e=1;e<arguments.length;e++){var n=arguments[e];for(var r in n)Object.prototype.hasOwnProperty.call(n,r)&&(t[r]=n[r])}return t},o="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t},i="function"==typeof Symbol&&"symbol"===o(Symbol.iterator)?function(t){return void 0===t?"undefined":o(t)}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":void 0===t?"undefined":o(t)},a=function(t){return parseInt(t,10)},u=function(t){return d(t),t.data=t.items,delete t.items,t},s=function(t){if(!t.error)return t.name||(t.name=t.title||t.message),t.picture||(t.picture=t.thumbnailLink),t.thumbnail||(t.thumbnail=t.thumbnailLink),"application/vnd.google-apps.folder"===t.mimeType&&(t.type="folder",t.files="https://www.googleapis.com/drive/v2/files?q=%22"+t.id+"%22+in+parents"),t},c=function(t){return{source:t.url,width:t.width,height:t.height}},f=function(t){if(d(t),"feed"in t&&"entry"in t.feed)t.data=t.feed.entry.map(h),delete t.feed;else{if("entry"in t)return h(t.entry);"items"in t?(t.data=t.items.map(s),delete t.items):s(t)}return t},l=function(t){t.name=t.displayName||t.name,t.picture=t.picture||(t.image?t.image.url:null),t.thumbnail=t.picture},p=function(t,e,n){if(d(t),"feed"in t&&"entry"in t.feed){for(var r=n.query.access_token,o=0;o<t.feed.entry.length;o++){var i=t.feed.entry[o];if(i.id=i.id.$t,i.name=i.title.$t,delete i.title,i.gd$email&&(i.email=i.gd$email&&i.gd$email.length>0?i.gd$email[0].address:null,i.emails=i.gd$email,delete i.gd$email),i.updated&&(i.updated=i.updated.$t),i.link){var a=i.link.length>0?i.link[0].href:null;a&&i.link[0].gd$etag&&(a+=(a.indexOf("?")>-1?"&":"?")+"access_token="+r,i.picture=a,i.thumbnail=a),delete i.link}i.category&&delete i.category}t.data=t.feed.entry,delete t.feed}return t},h=function(t){var e=t.media$group,n=e.media$content.length?e.media$content[0]:{},r=e.media$content||[],o=e.media$thumbnail||[],i=r.concat(o).map(c).sort(function(t,e){return t.width-e.width}),a=0,u=void 0,s={id:t.id.$t,name:t.title.$t,description:t.summary.$t,updated_time:t.updated.$t,created_time:t.published.$t,picture:n?n.url:null,pictures:i,images:[],thumbnail:n?n.url:null,width:n.width,height:n.height};if("link"in t)for(a=0;a<t.link.length;a++){var f=t.link[a];if(f.rel.match(/#feed$/)){s.upload_location=s.files=s.photos=f.href;break}}if("category"in t&&t.category.length)for(u=t.category,a=0;a<u.length;a++)u[a].scheme&&u[a].scheme.match(/#kind$/)&&(s.type=u[a].term.replace(/^.*?#/,""));return"media$thumbnail"in e&&e.media$thumbnail.length&&(u=e.media$thumbnail,s.thumbnail=u[0].url,s.images=u.map(c)),(u=e.media$content)&&u.length&&s.images.push(c(u[0])),s},d=function(t){if("feed"in t&&t.feed.openSearch$itemsPerPage){var e=a(t.feed.openSearch$itemsPerPage.$t),n=a(t.feed.openSearch$startIndex.$t);n+e<a(t.feed.openSearch$totalResults.$t)&&(t.paging={next:"?start="+(n+e)})}else"nextPageToken"in t&&(t.paging={next:"?pageToken="+t.nextPageToken})},m=function(){function t(t){var n=new FileReader;n.onload=function(n){e(btoa(n.target.result),t.type+i+"Content-Transfer-Encoding: base64")},n.readAsBinaryString(t)}function e(t,e){n.push(i+"Content-Type: "+e+i+i+t),o--,u()}var n=[],r=(1e10*Math.random()).toString(32),o=0,i="\r\n",a=i+"--"+r,u=function(){},s=/^data:([^;,]+(;charset=[^;,]+)?)(;base64)?,/i;this.append=function(n,r){"string"!=typeof n&&"length"in Object(n)||(n=[n]);for(var a=0;a<n.length;a++){o++;var u=n[a];if("undefined"!=typeof File&&u instanceof File||"undefined"!=typeof Blob&&u instanceof Blob)t(u);else if("string"==typeof u&&u.match(s)){var c=u.match(s);e(u.replace(s,""),c[1]+i+"Content-Transfer-Encoding: base64")}else e(u,r)}},this.onready=function(t){(u=function(){0===o&&(n.unshift(""),n.push("--"),t(n.join(a),r),n=[])})()}},v=function(t,e){var n={};t.data&&"undefined"!=typeof HTMLInputElement&&t.data instanceof HTMLInputElement&&(t.data={file:t.data}),!t.data.name&&Object(Object(t.data.file).files).length&&"post"===t.method&&(t.data.name=t.data.file.files[0].name),"post"===t.method?t.data={title:t.data.name,parents:[{id:t.data.parent||"root"}],file:t.data.file}:(n=t.data,t.data={},n.parent&&(t.data.parents=[{id:t.data.parent||"root"}]),n.file&&(t.data.file=n.file),n.name&&(t.data.title=n.name));var r=void 0;if("file"in t.data&&(r=t.data.file,delete t.data.file,"object"===(void 0===r?"undefined":i(r))&&"files"in r&&(r=r.files),!r||!r.length))e({error:{code:"request_invalid",message:"There were no files attached with this request to upload"}});else{var o=new m;o.append(JSON.stringify(t.data),"application/json"),r&&o.append(r),o.onready(function(r,o){t.headers["content-type"]='multipart/related; boundary="'+o+'"',t.data=r,e("upload/drive/v2/files"+(n.id?"/"+n.id:"")+"?uploadType=multipart")})}},y=function(t){if("object"===i(t.data))try{t.data=JSON.stringify(t.data),t.headers["content-type"]="application/json"}catch(t){}},g="https://www.google.com/m8/feeds/contacts/default/full?v=3.0&alt=json&max-results=@{limit|1000}&start-index=@{start|1}";t(349).init({google:{name:"Google Plus",oauth:{version:2,auth:"https://accounts.google.com/o/oauth2/auth",grant:"https://accounts.google.com/o/oauth2/token"},scope:{basic:"https://www.googleapis.com/auth/plus.me profile",email:"email",birthday:"",events:"",photos:"https://picasaweb.google.com/data/",videos:"http://gdata.youtube.com",friends:"https://www.google.com/m8/feeds, https://www.googleapis.com/auth/plus.login",files:"https://www.googleapis.com/auth/drive.readonly",publish:"",publish_files:"https://www.googleapis.com/auth/drive",share:"",create_event:"",offline_access:""},scope_delim:" ",login:function(t){"code"===t.qs.response_type&&(t.qs.access_type="offline"),t.options.force&&(t.qs.approval_prompt="force")},base:"https://www.googleapis.com/",get:{me:"plus/v1/people/me","me/friends":"plus/v1/people/me/people/visible?maxResults=@{limit|100}","me/following":g,"me/followers":g,"me/contacts":g,"me/share":"plus/v1/people/me/activities/public?maxResults=@{limit|100}","me/feed":"plus/v1/people/me/activities/public?maxResults=@{limit|100}","me/albums":"https://picasaweb.google.com/data/feed/api/user/default?alt=json&max-results=@{limit|100}&start-index=@{start|1}","me/album":function(t,e){var n=t.query.id;delete t.query.id,e(n.replace("/entry/","/feed/"))},"me/photos":"https://picasaweb.google.com/data/feed/api/user/default?alt=json&kind=photo&max-results=@{limit|100}&start-index=@{start|1}","me/file":"drive/v2/files/@{id}","me/files":"drive/v2/files?q=%22@{parent|root}%22+in+parents+and+trashed=false&maxResults=@{limit|100}","me/folders":"drive/v2/files?q=%22@{id|root}%22+in+parents+and+mimeType+=+%22application/vnd.google-apps.folder%22+and+trashed=false&maxResults=@{limit|100}","me/folder":"drive/v2/files?q=%22@{id|root}%22+in+parents+and+trashed=false&maxResults=@{limit|100}"},post:{"me/files":v,"me/folders":function(t,e){t.data={title:t.data.name,parents:[{id:t.data.parent||"root"}],mimeType:"application/vnd.google-apps.folder"},e("drive/v2/files")}},put:{"me/files":v},del:{"me/files":"drive/v2/files/@{id}","me/folder":"drive/v2/files/@{id}"},patch:{"me/file":"drive/v2/files/@{id}"},wrap:{me:function(t){return t.id&&(t.last_name=t.family_name||(t.name?t.name.familyName:null),t.first_name=t.given_name||(t.name?t.name.givenName:null),t.emails&&t.emails.length&&(t.email=t.emails[0].value),l(t)),t},"me/friends":function(t){return t.items&&(d(t),t.data=t.items,t.data.forEach(l),delete t.items),t},"me/contacts":p,"me/followers":p,"me/following":p,"me/share":u,"me/feed":u,"me/albums":f,"me/photos":function(t){t.data=t.feed.entry.map(h),delete t.feed},default:f},xhr:function(t){return"post"===t.method||"put"===t.method?y(t):"patch"===t.method&&(r(t.query,t.data),t.data=null),!0},form:!1}})},{349:349}],357:[function(t,e,n){"use strict";var r=function(t){return{source:t.url,width:t.width,height:t.height}},o=function(t){return"string"==typeof t?{error:{code:"invalid_request",message:t}}:(t&&"meta"in t&&"error_type"in t.meta&&(t.error={code:t.meta.error_type,message:t.meta.error_message}),t)},i=function(t){return u(t),t&&"data"in t&&t.data.forEach(a),t},a=function(t){t.id&&(t.thumbnail=t.profile_picture,t.name=t.full_name||t.username)},u=function(t){t&&"pagination"in t&&(t.paging={next:t.pagination.next_url},delete t.pagination)};t(349).init({instagram:{name:"Instagram",oauth:{version:2,auth:"https://instagram.com/oauth/authorize/",grant:"https://api.instagram.com/oauth/access_token"},refresh:!0,scope:{basic:"basic",photos:"",friends:"relationships",publish:"likes comments",email:"",share:"",publish_files:"",files:"",videos:"",offline_access:""},scope_delim:" ",base:"https://api.instagram.com/v1/",get:{me:"users/self","me/feed":"users/self/feed?count=@{limit|100}","me/photos":"users/self/media/recent?min_id=0&count=@{limit|100}","me/friends":"users/self/follows?count=@{limit|100}","me/following":"users/self/follows?count=@{limit|100}","me/followers":"users/self/followed-by?count=@{limit|100}","friend/photos":"users/@{id}/media/recent?min_id=0&count=@{limit|100}"},post:{"me/like":function(t,e){var n=t.data.id;t.data={},e("media/"+n+"/likes")}},del:{"me/like":"media/@{id}/likes"},wrap:{me:function(t){return o(t),"data"in t&&(t.id=t.data.id,t.thumbnail=t.data.profile_picture,t.name=t.data.full_name||t.data.username),t},"me/friends":i,"me/following":i,"me/followers":i,"me/photos":function(t){return o(t),u(t),"data"in t&&(t.data=t.data.filter(function(t){return"image"===t.type}),t.data.forEach(function(t){t.name=t.caption?t.caption.text:null,t.thumbnail=t.images.thumbnail.url,t.picture=t.images.standard_resolution.url,t.pictures=Object.keys(t.images).map(function(e){var n=t.images[e];return r(n)}).sort(function(t,e){return t.width-e.width})})),t},default:function(t){return t=o(t),u(t),t}},xhr:function(t){var e=t.method,n="get"!==e;return n&&("post"!==e&&"put"!==e||!t.query.access_token||(t.data.access_token=t.query.access_token,delete t.query.access_token),t.proxy=n),n},form:!1}})},{349:349}],358:[function(t,e,n){"use strict";var r="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t},o="function"==typeof Symbol&&"symbol"===r(Symbol.iterator)?function(t){return void 0===t?"undefined":r(t)}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":void 0===t?"undefined":r(t)},i=function(t,e){var n=void 0,r=void 0;return t&&"Message"in t&&(r=t.Message,delete t.Message,"ErrorCode"in t?(n=t.ErrorCode,delete t.ErrorCode):n=a(e),t.error={code:n,message:r,details:t}),t},a=function(t){switch(t.statusCode){case 400:return"invalid_request";case 403:return"stale_token";case 401:return"invalid_token";case 500:default:return"server_error"}};t(349).init({joinme:{name:"join.me",oauth:{version:2,auth:"https://secure.join.me/api/public/v1/auth/oauth2",grant:"https://secure.join.me/api/public/v1/auth/oauth2"},refresh:!1,scope:{basic:"user_info",user:"user_info",scheduler:"scheduler",start:"start_meeting",email:"",friends:"",share:"",publish:"",photos:"",publish_files:"",files:"",videos:"",offline_access:""},scope_delim:" ",login:function(t){t.options.popup.width=400,t.options.popup.height=700},base:"https://api.join.me/v1/",get:{me:"user",meetings:"meetings","meetings/info":"meetings/@{id}"},post:{"meetings/start/adhoc":function(t,e){e("meetings/start")},"meetings/start/scheduled":function(t,e){var n=t.data.meetingId;t.data={},e("meetings/"+n+"/start")},"meetings/schedule":function(t,e){e("meetings")}},patch:{"meetings/update":function(t,e){e("meetings/"+t.data.meetingId)}},del:{"meetings/delete":"meetings/@{id}"},wrap:{me:function(t,e){return i(t,e),t.email?(t.name=t.fullName,t.first_name=t.name.split(" ")[0],t.last_name=t.name.split(" ")[1],t.id=t.email,t):t},default:function(t,e){return i(t,e),t}},xhr:function(t,e){var n=e.access_token;return delete e.access_token,t.headers.Authorization="Bearer "+n,"get"!==t.method&&t.data&&(t.headers["Content-Type"]="application/json","object"===o(t.data)&&(t.data=JSON.stringify(t.data))),"put"===t.method&&(t.method="patch"),!0}}})},{349:349}],359:[function(t,e,n){"use strict";var r=function(t){t&&"errorCode"in t&&(t.error={code:t.status,message:t.message})},o=function(t){if(!t.error)return t.first_name=t.firstName,t.last_name=t.lastName,t.name=t.formattedName||t.first_name+" "+t.last_name,t.thumbnail=t.pictureUrl,t.email=t.emailAddress,t},i=function(t){return r(t),a(t),t.values&&(t.data=t.values.map(o),delete t.values),t},a=function(t){"_count"in t&&"_start"in t&&t._count+t._start<t._total&&(t.paging={next:"?start="+(t._start+t._count)+"&count="+t._count})},u=function(t,e){"{}"===JSON.stringify(t)&&200===e.statusCode&&(t.success=!0)},s=function(t){t.access_token&&(t.oauth2_access_token=t.access_token,delete t.access_token)},c=function(t,e){t.headers["x-li-format"]="json";var n=t.data.id;t.data=("delete"!==t.method).toString(),t.method="put",e("people/~/network/updates/key="+n+"/is-liked")};t(349).init({linkedin:{oauth:{version:2,response_type:"code",auth:"https://www.linkedin.com/uas/oauth2/authorization",grant:"https://www.linkedin.com/uas/oauth2/accessToken"},refresh:!0,scope:{basic:"r_basicprofile",email:"r_emailaddress",files:"",friends:"",photos:"",publish:"w_share",publish_files:"w_share",share:"",videos:"",offline_access:""},scope_delim:" ",base:"https://api.linkedin.com/v1/",get:{me:"people/~:(picture-url,first-name,last-name,id,formatted-name,email-address)","me/share":"people/~/network/updates?count=@{limit|250}"},post:{"me/share":function(t,e){var n={visibility:{code:"anyone"}};t.data.id?n.attribution={share:{id:t.data.id}}:(n.comment=t.data.message,t.data.picture&&t.data.link&&(n.content={"submitted-url":t.data.link,"submitted-image-url":t.data.picture})),t.data=JSON.stringify(n),e("people/~/shares?format=json")},"me/like":c},del:{"me/like":c},wrap:{me:function(t){return r(t),o(t),t},"me/friends":i,"me/following":i,"me/followers":i,"me/share":function(t){return r(t),a(t),t.values&&(t.data=t.values.map(o),t.data.forEach(function(t){t.message=t.headline}),delete t.values),t},default:function(t,e){r(t),u(t,e),a(t)}},jsonp:function(t,e){s(e),"get"===t.method&&(e.format="jsonp",e["error-callback"]=t.callbackID)},xhr:function(t,e){return"get"!==t.method&&(s(e),t.headers["Content-Type"]="application/json",t.headers["x-li-format"]="json",t.proxy=!0,!0)}}})},{349:349}],360:[function(t,e,n){"use strict";var r=function(t,e){var n=e.access_token;return delete e.access_token,e.oauth_token=n,e["_status_code_map[302]"]=200,!0},o=function(t){return t.id&&(t.picture=t.avatar_url,t.thumbnail=t.avatar_url,t.name=t.username||t.full_name),t},i=function(t){"next_href"in t&&(t.paging={next:t.next_href})};t(349).init({soundcloud:{name:"SoundCloud",oauth:{version:2,auth:"https://soundcloud.com/connect",grant:"https://soundcloud.com/oauth2/token"},base:"https://api.soundcloud.com/",get:{me:"me.json","me/friends":"me/followings.json","me/followers":"me/followers.json","me/following":"me/followings.json",default:function(t,e){e(t.path+".json")}},wrap:{me:function(t){return o(t),t},default:function(t){return Array.isArray(t)&&(t={data:t.map(o)}),i(t),t}},xhr:r,jsonp:r}})},{349:349}],361:[function(t,e,n){"use strict";var r=Object.assign||function(t){for(var e=1;e<arguments.length;e++){var n=arguments[e];for(var r in n)Object.prototype.hasOwnProperty.call(n,r)&&(t[r]=n[r])}return t},o=function(t){if(t.id){if(t.name){var e=t.name.split(" ");t.first_name=e.shift(),t.last_name=e.join(" ")}t.thumbnail=t.profile_image_url_https||t.profile_image_url}return t},i=function(t){return a(t),u(t),t.users&&(t.data=t.users.map(o),delete t.users),t},a=function(t){if(t.errors){var e=t.errors[0];t.error={code:"request_failed",message:e.message}}},u=function(t){"next_cursor_str"in t&&(t.paging={next:"?cursor="+t.next_cursor_str})},s=function(t){return Array.isArray(t)?{data:t}:t},c="https://api.twitter.com/";t(349).init({twitter:{oauth:{version:"1.0a",auth:c+"oauth/authenticate",request:c+"oauth/request_token",token:c+"oauth/access_token"},login:function(t){this.oauth.auth=this.oauth.auth.replace("?force_login=true","")+(t.options.force?"?force_login=true":"")},base:c+"1.1/",get:{me:"account/verify_credentials.json","me/friends":"friends/list.json?count=@{limit|200}","me/following":"friends/list.json?count=@{limit|200}","me/followers":"followers/list.json?count=@{limit|200}","me/share":"statuses/user_timeline.json?count=@{limit|200}","me/like":"favorites/list.json?count=@{limit|200}"},post:{"me/share":function(t,e){var n=t.data;t.data=null;var o=[];n.message&&(o.push(n.message),delete n.message),n.link&&(o.push(n.link),delete n.link),n.picture&&(o.push(n.picture),delete n.picture),o.length&&(n.status=o.join(" ")),n.file?(n["media[]"]=n.file,delete n.file,t.data=n,e("statuses/update_with_media.json")):"id"in n?e("statuses/retweet/"+n.id+".json"):(r(t.query,n),e("statuses/update.json?include_entities=1"))},"me/like":function(t,e){var n=t.data.id;t.data=null,e("favorites/create.json?id="+n)}},del:{"me/like":function(t,e){t.method="post";var n=t.data.id;t.data=null,e("favorites/destroy.json?id="+n)}},wrap:{me:function(t){return a(t),o(t),t},"me/friends":i,"me/followers":i,"me/following":i,"me/share":function(t){return a(t),u(t),!t.error&&"length"in t?{data:t}:t},default:function(t){return t=s(t),u(t),t}},xhr:function(t){return"get"!==t.method}}})},{349:349}],362:[function(t,e,n){"use strict";var r=function(t,e){return null!==t&&"response"in t&&null!==t.response&&t.response.length&&((t=t.response[0]).id=t.uid,t.thumbnail=t.picture=t.photo_max,t.name=t.first_name+" "+t.last_name,e.authResponse&&null!==e.authResponse.email&&(t.email=e.authResponse.email)),t},o=function(t){if(t.error){var e=t.error;t.error={code:e.error_code,message:e.error_msg}}};t(349).init({vk:{name:"Vk",oauth:{version:2,auth:"https://oauth.vk.com/authorize",grant:"https://oauth.vk.com/access_token"},scope:{email:"email",friends:"friends",photos:"photos",videos:"video",share:"share",offline_access:"offline"},refresh:!0,login:function(t){t.qs.display=window.navigator&&window.navigator.userAgent&&/ipad|phone|phone|android/.test(window.navigator.userAgent.toLowerCase())?"mobile":"popup"},base:"https://api.vk.com/method/",get:{me:function(t,e){t.query.fields="id,first_name,last_name,photo_max",e("users.get")}},wrap:{me:function(t,e,n){return o(t),r(t,n)}},xhr:!1,jsonp:!0,form:!1}})},{349:349}],363:[function(t,e,n){"use strict";var r=t(349),o=t(325),i=t(332),a=function(t){return"data"in t&&t.data.forEach(function(t){t.picture&&(t.thumbnail=t.picture),t.images&&(t.pictures=t.images.map(u).sort(function(t,e){return t.width-e.width}))}),t},u=function(t){return{width:t.width,height:t.height,source:t.source}},s=function(t,e,n){if(t.id){var r=n.authResponse.access_token;if(t.emails&&(t.email=t.emails.preferred),!1!==t.is_friend){var o=t.user_id||t.id;t.thumbnail=t.picture="https://apis.live.net/v5.0/"+o+"/picture?access_token="+r}}return t},c=function(t,e,n){return"data"in t&&t.data.forEach(function(t){s(t,0,n)}),t};r.init({windows:{name:"Windows live",oauth:{version:2,auth:"https://login.live.com/oauth20_authorize.srf",grant:"https://login.live.com/oauth20_token.srf"},refresh:!0,logout:function(){return"http://login.live.com/oauth20_logout.srf?ts="+(new Date).getTime()},scope:{basic:"wl.signin,wl.basic",email:"wl.emails",birthday:"wl.birthday",events:"wl.calendars",photos:"wl.photos",videos:"wl.photos",friends:"wl.contacts_emails",files:"wl.skydrive",publish:"wl.share",publish_files:"wl.skydrive_update",share:"wl.share",create_event:"wl.calendars_update,wl.events_create",offline_access:"wl.offline_access"},base:"https://apis.live.net/v5.0/",get:{me:"me","me/friends":"me/friends","me/following":"me/contacts","me/followers":"me/friends","me/contacts":"me/contacts","me/albums":"me/albums","me/album":"@{id}/files","me/photo":"@{id}","me/files":"@{parent|me/skydrive}/files","me/folders":"@{id|me/skydrive}/files","me/folder":"@{id|me/skydrive}/files"},post:{"me/albums":"me/albums","me/album":"@{id}/files/","me/folders":"@{id|me/skydrive/}","me/files":"@{parent|me/skydrive}/files"},del:{"me/album":"@{id}","me/photo":"@{id}","me/folder":"@{id}","me/files":"@{id}"},wrap:{me:s,"me/friends":c,"me/contacts":c,"me/followers":c,"me/following":c,"me/albums":function(t){return"data"in t&&t.data.forEach(function(t){t.photos=t.files="https://apis.live.net/v5.0/"+t.id+"/photos"}),t},"me/photos":a,default:a},xhr:function(t){return"get"===t.method||"delete"===t.method||o(t.data)||("string"==typeof t.data.file?t.data.file=i(t.data.file):(t.data=JSON.stringify(t.data),t.headers={"Content-Type":"application/json"})),!0},jsonp:function(t){"get"===t.method||o(t.data)||(t.data.method=t.method,t.method="get")}}})},{325:325,332:332,349:349}],364:[function(t,e,n){"use strict";var r=function(t){t&&"meta"in t&&"error_type"in t.meta&&(t.error={code:t.meta.error_type,message:t.meta.error_message})},o=function(t,e,n){return r(t),a(t,e,n),t.query&&t.query.results&&t.query.results.contact&&(t.data=t.query.results.contact,delete t.query,Array.isArray(t.data)||(t.data=[t.data]),t.data.forEach(i)),t},i=function(t){t.id=null,!t.fields||t.fields instanceof Array||(t.fields=[t.fields]),(t.fields||[]).forEach(function(e){"email"===e.type&&(t.email=e.value),"name"===e.type&&(t.first_name=e.value.givenName,t.last_name=e.value.familyName,t.name=e.value.givenName+" "+e.value.familyName),"yahooid"===e.type&&(t.id=e.value)})},a=function(t,e,n){return t.query&&t.query.count&&n.options&&(t.paging={next:"?start="+(t.query.count+(+n.options.start||1))}),t},u=function(t){return"https://query.yahooapis.com/v1/yql?q="+(t+" limit @{limit|100} offset @{start|0}").replace(/\s/g,"%20")+"&format=json"};t(349).init({yahoo:{oauth:{version:"1.0a",auth:"https://api.login.yahoo.com/oauth/v2/request_auth",request:"https://api.login.yahoo.com/oauth/v2/get_request_token",token:"https://api.login.yahoo.com/oauth/v2/get_token"},login:function(t){t.options.popup.width=560;try{delete t.qs.state.scope}catch(t){}},base:"https://social.yahooapis.com/v1/",get:{me:u("select * from social.profile(0) where guid=me"),"me/friends":u("select * from social.contacts(0) where guid=me"),"me/following":u("select * from social.contacts(0) where guid=me")},wrap:{me:function(t){if(r(t),t.query&&t.query.results&&t.query.results.profile){(t=t.query.results.profile).id=t.guid,t.last_name=t.familyName,t.first_name=t.givenName||t.nickname;var e=[];t.first_name&&e.push(t.first_name),t.last_name&&e.push(t.last_name),t.name=e.join(" "),t.email=t.emails&&t.emails[0]?t.emails[0].handle:null,t.thumbnail=t.image?t.image.imageUrl:null}return t},"me/friends":o,"me/following":o,default:a}}})},{349:349}]},{},[347])(347)});
//# sourceMappingURL=hello.all.js.map
